/**
 * Bootstrap: read the spawn out of the URL, build the scene, and run the loop.
 */

import {
  BufferAttribute, BufferGeometry, DoubleSide, Matrix4, Mesh, MeshBasicMaterial, PerspectiveCamera, Plane, Quaternion, Raycaster, RingGeometry, Scene,
  Vector2, Vector3, WebGLRenderer,
} from 'three';
import type { Material, Object3D } from 'three';
import { DEFAULT_CONFIG, configFromSearch } from './config.js';
import type { Graphics, PlayerMode } from './config.js';
import { World } from './core/world.js';
import { ElevationTileLoader } from './loaders/elevationLoader.js';
import { VectorTileLoader } from './loaders/vectorLoader.js';
import { BuildingTagService } from './loaders/buildingTagService.js';
import { OsmLineService } from './loaders/osmLineService.js';
import { createPlacementWorkers } from './loaders/placementLoader.js';
import { fullscreenOnFirstTouch, guardBackNavigation, keepScreenAwake, registerServiceWorker } from './app/shell.js';
import { DebugHud } from './three/DebugHud.js';
import type { PlayerSound, PlayerState, Throw } from './three/PlayerController.js';
import { PlayerController } from './three/PlayerController.js';
import { TouchControls, isTouchDevice } from './three/TouchControls.js';
import { RenderScale } from './three/renderScale.js';
import { FrameTimer } from './three/frameTimer.js';
import { readGpu } from './three/gpuInfo.js';
import {
  GRAPHICS_PRESETS, GRAPHICS_SETTINGS, clearGraphicsChoices, isChosen, loadGraphicsChoices, needsReload, saveGraphicsChoices,
} from './three/graphicsSettings.js';
import { ThreeRenderer } from './three/ThreeRenderer.js';
import type { HelicopterModel } from './three/Helicopter.js';
import { HELI, HELI_SEAT, HELI_STEP, Searchlight, animateHelicopterLights, createHelicopter, orientHelicopter } from './three/Helicopter.js';
import { BODY_WEIGHT, CAR_CHASSIS, VEHICLE, animateVehicle, isTruck, carEyeHeight, chassisOf, createRide, gearTops, createVehicle, drawCluster, orientToSlope, updateRide } from './three/Vehicle.js';
import type { Source, Tank } from './core/economy/energy.js';
import { START_BALANCE, drive as burnRoad, fill, fly as burnAir, kronor, priceOf, rangeKm, unitOf } from './core/economy/energy.js';
import { JumpLine, JumpTracks, TRACK_LENGTH, clearHeading } from './three/jumps.js';
import { MOTO_SPEC, Roost, animateMotorbike, createMotoRide, createMotorbike, createRiderState, motoPaint, riderPose, updateMotoRide } from './three/Motorbike.js';
import type { TwoWheelerSpec } from './three/Motorbike.js';
import { SCOOTER_SPEC, animateScooter, createScooterRiderState, scooterPaint, scooterRiderPose } from './three/Scooter.js';
import { createTerrainMaterial } from './three/terrainMaterial.js';
import { Look } from './three/look.js';
import { CharacterCrowd, PLAYER_LOOK, lookFromSeed, officerLook } from './three/character/crowd.js';
import { Villagers } from './three/character/villagers.js';
import { POLICE_SEES, Traffic } from './three/traffic.js';
import { TransitFeed } from './app/transit.js';
import { BusSigns } from './three/busSigns.js';
import { Trams } from './three/trams.js';
import { Horn } from './three/horn.js';
import { SoundSystem } from './three/audio/system.js';
import { EngineVoices, trafficEngine } from './three/audio/voices.js';
import { WorldSound } from './three/audio/world.js';
import { EffectSound, propMaterial } from './three/audio/effects.js';
import type { EngineKind } from './core/audio/engine.js';
import type { Material as SoundMaterial } from './core/audio/sounds.js';
import type { Place } from './core/audio/ambience.js';
import { footfalls, stepSurface } from './core/audio/ambience.js';
import { SnowField } from './three/snowField.js';
import { SnowSpray } from './three/snowSpray.js';
import { CLEARANCE, PLOWED_LANDCOVER, WHEELS, footAt, lyingDepth, sprayRate, vehicleSnow, wheelsAt } from './core/weather/snow.js';
import type { SnowBuild } from './core/weather/snow.js';
import type { Weather } from './core/weather/weather.js';
import type { SirenSource } from './three/police.js';
import { Sirens, flashKit } from './three/police.js';
import { PoliceHelicopter } from './three/policeHelicopter.js';
import type { Country } from './core/geo/country.js';
import { Trains } from './three/trains.js';
import { StationBoards } from './three/stationBoards.js';
import type { RailStation } from './three/ThreeRenderer.js';
import { TrainFeed } from './app/trains.js';
import { TrainSounds } from './three/audio/trainSound.js';
import { CarRadio } from './three/audio/radio.js';
import { GamepadControls } from './three/gamepad.js';
import type { Side } from './core/character/arms.js';
import { armPose, letGo, newArms, punch, raise, stepArms, swing, takeHold } from './core/character/arms.js';
import { countryAt } from './core/geo/country.js';
import { liveryOf } from './core/traffic/police.js';
import type { Offence } from './core/traffic/wanted.js';
import { MOST_STARS, fine, notWanted, offend, pursuers, seesWanted, speeding, stepWanted } from './core/traffic/wanted.js';
import { AT_THE_DOOR, JOG, SPRINT_TIME, TACKLE, chaserStep, officerPace } from './core/traffic/pursuit.js';
import type { Blocked, Way } from './core/traffic/findway.js';
import { aimAlong, clearLine, findWay } from './core/traffic/findway.js';
import type { StationPlace } from './core/sources/osmpolice.js';
import { findPoliceStation } from './loaders/policeStations.js';
import { Wreck } from './three/crumple.js';
import { Debris } from './three/debris.js';
import { Smoke } from './three/smoke.js';
import type { Build, Impact, Zone } from './core/vehicle/damage.js';
import { KNOCK_KMH, addCrush, crushOf, impactOf, isDamage, noDamage } from './core/vehicle/damage.js';
import { BRAKING_DRAG, PARKED_DRAG, STIFFNESS, clearance, crushSpeeds, exchange, fall, massOf, overlap, slide, spinFrom, stopped } from './core/vehicle/collision.js';
import { SHELTER_FRAME, SOFT, bendFor, propBehaviour, thrown } from './core/vehicle/props.js';
import { burst } from './core/vehicle/shards.js';
import { Shards } from './three/shards.js';
import { shelterParts } from './three/flora.js';
import type { BrokenPiece } from './three/crumple.js';
import type { PropRef } from './three/ThreeRenderer.js';
import type { Species } from './core/biome/flora.js';
import { SPECIES, VEHICLES } from './core/biome/flora.js';
import type { Released } from './three/traffic.js';
import type { Footprint } from './core/vehicle/collision.js';
import type { WheelSpot } from './core/vehicle/handling.js';
import { AS_NEW, handlingOf, wheelBends } from './core/vehicle/handling.js';
import type { Body, Struck } from './three/PlayerController.js';
import type { ParkedRef } from './three/ThreeRenderer.js';
import { localTime, trafficLevel } from './core/traffic/profile.js';
import { createParachute } from './three/character/parachute.js';
import type { RiderPose } from './three/character/crowd.js';
import { bikeBoarding, blendPose, carBoarding, drivingPose, heliBoarding, scooterBoarding } from './three/character/boarding.js';
import type { FleetVehicle } from './three/fleet.js';
import { Fleet, tankOf } from './three/fleet.js';
import type { FleetHeli } from './three/heliFleet.js';
import { HeliFleet } from './three/heliFleet.js';
import type { VehicleSpecies } from './core/build/parking.js';
import { SEMI_REACH, VEHICLE_NAME, VEHICLE_SIZE, isTwoWheeler, onDriverSide, paintName } from './core/build/parking.js';
import type { GaitPose } from './core/character/gait.js';
import { advancePhase, airPose, gaitPose, jumpPose, landPose } from './core/character/gait.js';
import type { FallStart, RagdollCollide, RagdollGround } from './core/character/ragdoll.js';
import { J, POINTS, startFall } from './core/character/ragdoll.js';
import type { Hit, Striker } from './core/character/struck.js';
import { KNOCKED_DOWN, strike as strikePerson, touching } from './core/character/struck.js';
import type { BodyWorld } from './three/character/fallen.js';
import { Fallen } from './three/character/fallen.js';
import { biomeWeights } from './core/biome/biome.js';
import { climateAt } from './core/biome/climate.js';
import { setWaterWind, waterMaterial } from './three/vegetation.js';
import { clockFromSearch, localClock, localDate, solarHour, windowsLit } from './core/sky/clock.js';
import { horizonToScene, localSidereal, moonPosition, phaseName, sunPosition } from './core/sky/astronomy.js';
import { dayOfYear, seasonState } from './core/sky/season.js';
import { CLEAR, PRESET_NAMES, parseWindParam, presetWeather, simulatedWeather } from './core/weather/weather.js';
import { WindState, compass } from './core/weather/wind.js';
import { WeatherService } from './loaders/weatherLoader.js';
import { WeatherFx } from './three/weatherFx.js';
import { houseNight } from './three/houseChunk.js';
import { floraNight, setFloraWind } from './three/flora.js';
import { terrainWeather } from './three/terrainMaterial.js';
import { MapTiles } from './map/mapTiles.js';
import { Radar } from './map/radar.js';
import { WeatherSamples } from './map/weatherSamples.js';
import { Minimap, WorldMap } from './map/mapUi.js';
import type { OwnState } from './net/presence.js';
import { Presence, realtimeUrl } from './net/presence.js';
import type { Route } from './core/nav/route.js';
import { makeRoute, onRoute, pointAt, timeLeft } from './core/nav/route.js';
import type { Travel } from './core/nav/autopilot.js';
import { Follower, flyTo } from './core/nav/autopilot.js';
import type { TrafficSense } from './core/nav/autopilot.js';
import { fetchRoute } from './loaders/routeService.js';
import { findDropOff } from './loaders/placeService.js';
import { DELIVER_RADIUS, jobPay, jobTime, payOnDelivery, planJob, pointToward, timeLeft as jobTimeLeft, type Job } from './core/economy/jobs.js';
import type { TruckKind } from './three/Vehicle.js';
import { SpeedSigns, limitBadge, placeSigns } from './three/speedSigns.js';
import { setSignScale } from './three/signs.js';
import type { AutoInput, Collider } from './three/PlayerController.js';
import { geoToEnu, makeOrigin } from './core/geo/enu.js';
import { tileAt } from './core/geo/tilekey.js';
import type { SavedTrip } from './app/tripStore.js';
import { clearTrip, loadTrip, resumeTrip, saveTrip } from './app/tripStore.js';
import type { VehicleSpecies as ParkedSpecies } from './core/build/parking.js';
import { findStart, linkHasStart } from './app/startLocation.js';
import { MAX_SHOWN as REMOTE_MAX_SHOWN, RemotePlayers } from './three/remotePlayers.js';

/** Height over the ground of a parachute drop picked on the world map, metres. */
const MAP_DROP_HEIGHT = 1200;
/** How far past belly-down a full dive tips the body, head toward the ground. */
const DIVE_PITCH = 0.95;

/** Far enough to see the coarse ring from parachute altitude. */
const FAR_PLANE = 90_000;

const container = document.getElementById('app');
if (container === null) throw new Error('#app missing');

// A phone — a touch screen — gets the phone profile: lighter effects and less
// to draw (see `phoneProfile` in config.ts); `?fx=high` or `?fx=phone` overrides.
// A trip left under way carries on by the time since: the game starts where
// it has got to, in its vehicle. See app/tripStore.ts.
const resumed = (() => { const t = loadTrip(); return t === undefined ? undefined : resumeTrip(t); })();
const startSearch = (() => {
  if (resumed === undefined) return window.location.search;
  const p = new URLSearchParams(window.location.search);
  p.set('lat', resumed.lat.toFixed(6));
  p.set('lon', resumed.lon.toFixed(6));
  p.set('heading', resumed.heading.toFixed(0));
  const t = resumed.trip.travel;
  p.set('mode', t === 'car' ? 'drive' : t === 'moto' ? 'moto' : t === 'heli' ? 'heli' : 'walk');
  // In the air still, unless it landed or ran dry on the way.
  if (t === 'heli') p.set('alt', resumed.arrived || resumed.dry ? '0' : '120'); else p.delete('alt');
  return `?${p.toString()}`;
})();
// A screen of two device pixels a CSS pixel or more gets the dense profile:
// at full ratio a 4K panel's frame is four times 1080p's (see DENSE_GRAPHICS).
const baseConfig = configFromSearch(startSearch, DEFAULT_CONFIG, isTouchDevice(window.location.search), window.devicePixelRatio >= 1.75);
// Terrain detail near the player, the profile's; `?terrain=96|160|192|256` overrides.
const terrainRes = Number(new URLSearchParams(window.location.search).get('terrain')) || baseConfig.lod.gridRes;
// The graphics switches chosen on the HUD, over the profile's — unless the
// URL asks for a profile by name. See three/graphicsSettings.ts.
const profileGraphics = baseConfig.graphics;
const fxInUrl = new URLSearchParams(window.location.search).has('fx');
const config = {
  ...baseConfig,
  lod: { ...baseConfig.lod, gridRes: Math.max(32, Math.min(256, terrainRes)) },
  graphics: fxInUrl ? profileGraphics : { ...profileGraphics, ...loadGraphicsChoices() },
};

// The one clock everything time-dependent reads: real time unless the URL
// says otherwise (?time=, ?timescale=). See core/sky/clock.ts.
const skyClock = clockFromSearch(window.location.search);
const today = () => dayOfYear(skyClock.now);

// Real weather from Open-Meteo, unless ?weather= names a preset.
const weatherPreset = new URLSearchParams(window.location.search).get('weather');
const weather = new WeatherService(weatherPreset === null ? undefined : presetWeather(weatherPreset.toLowerCase()));
// ?wind=speed[,from[,gusts]] overrides just the wind, over real or preset weather.
const windParam = new URLSearchParams(window.location.search).get('wind');
const windOverride = windParam === null ? undefined : parseWindParam(windParam);
/** The real weather (or the URL's preset), the URL's wind over it. */
const liveWeather = (): Weather => windOverride === undefined ? weather.current : { ...weather.current, ...windOverride };

/**
 * Weather set by hand from the HUD's world tab, in place of the real: kept
 * per browser, so it holds through the reload a change of season takes.
 */
const WEATHER_SIM_KEY = 'wsg.weather.sim';
const weatherSim: { on: boolean; weather: Weather } = (() => {
  try {
    const saved = JSON.parse(localStorage.getItem(WEATHER_SIM_KEY) ?? 'null') as { on?: unknown; weather?: Partial<Weather> } | null;
    if (saved !== null && typeof saved.on === 'boolean' && typeof saved.weather === 'object') {
      return { on: saved.on, weather: simulatedWeather({ ...CLEAR, ...saved.weather }) };
    }
  } catch {
    // Nothing kept, or nothing readable: the real weather.
  }
  return { on: false, weather: CLEAR };
})();
function saveWeatherSim(): void {
  try {
    localStorage.setItem(WEATHER_SIM_KEY, JSON.stringify(weatherSim));
  } catch {
    // Not kept; it holds for this visit.
  }
}

/** The middle of each season, month (0-based) and day, in the north; the south's are six months on. */
const SEASON_DATES: Record<string, [number, number]> = { spring: [3, 20], summer: [6, 10], autumn: [9, 12], winter: [0, 20] };
const wind = new WindState();

const scene = new Scene();

// The near plane is tight enough for a chase camera four metres behind a
// person; the far plane still reaches the coarse ring seen from a parachute.
const camera = new PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.25, FAR_PLANE);

// No antialiasing on the canvas itself: the scene is drawn into the composer's
// 4× MSAA target and only a full-screen quad ever reaches the canvas, so a
// multisampled canvas would cost memory and a resolve every frame for nothing.
const renderer = new WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, config.graphics.maxPixelRatio));
renderer.setSize(window.innerWidth, window.innerHeight);
container.appendChild(renderer.domElement);
// What a frame costs the CPU and the GPU, for the HUD: the frame rate stops at the screen's refresh.
const frameTimer = new FrameTimer(renderer.getContext() as WebGL2RenderingContext);
/** The graphics card, where the browser names it. */
const gpuName = readGpu(renderer.getContext());
// Counted across every pass of a frame — shadow, scene, occlusion, post — not
// just the last one, so the HUD shows what a frame really costs.
renderer.info.autoReset = false;

// Sky, sun, shadows and occlusion. Fog, background and tone mapping too — see
// `three/look.ts` for why nearly all of the style lives there.
const lookOptions = (g: Graphics) => ({
  shadows: g.shadows,
  ambientOcclusion: g.ambientOcclusion,
  aoScale: g.aoScale,
  shadowMapSize: g.shadowMapSize,
  shadowExtent: g.shadowExtent,
  msaa: g.msaa,
  fxaa: g.fxaa,
});
const look = new Look(renderer, scene, camera, lookOptions(config.graphics));
// Lying snow you wade through and leave tracks in (see three/snowField.ts).
const snowField = new SnowField(renderer, () => worldRenderer.landcoverPieces());
scene.add(snowField.mesh);
// Snow thrown up by the wheels, the plough and the powder behind (see three/snowSpray.ts).
const snowSpray = new SnowSpray();
scene.add(snowSpray.lumps, snowSpray.powder);

// Everyone in the world — the player's own body and every villager — is one
// crowd, drawn as a dozen instanced meshes. The player takes the first slot.
// Render resolution: fixed or dynamic, see three/renderScale.ts. Fill rate
// goes with its square; geometry and draw distance are untouched by it.
const renderScale = new RenderScale(window.location.search, (pixelRatio) => {
  renderer.setPixelRatio(pixelRatio);
  look.resize(window.innerWidth, window.innerHeight);
}, config.graphics.maxPixelRatio);

// Slots for the player, the villagers, and other players (see remotePlayers.ts).
/** Room for the police on foot and on motorbikes, besides the villagers and other players. */
const POLICE_PEOPLE = 16;
const crowd = new CharacterCrowd(Math.max(1, config.villagers + 1) + REMOTE_MAX_SHOWN + POLICE_PEOPLE);
scene.add(crowd.group);
const playerSlot = crowd.add(PLAYER_LOOK)!;
let playerPhase = 0;
/** The jump in the air: which leg leads it (1 the left), and the landing's give, 1 at touchdown easing to 0. */
let jumpLead: 1 | -1 = 1;
let landing = 0;
let lastAvatarPose = '';
let lastAvatarVy = 0;
/** How long the legs take to straighten after a landing, seconds. */
const LAND_TIME = 0.32;
let clock = 0;

const parachute = createParachute();
parachute.visible = false;
scene.add(parachute);
let canopyOpen = 0;

// New tiles are compiled in the background before they are drawn (see
// ThreeRenderer's `prepare`), with the scene's own lights so the shader is
// the one the frame will use.
setSignScale(config.graphics.signScale);
const worldRenderer = new ThreeRenderer(scene, createTerrainMaterial(),
  (object, target) => renderer.compileAsync(object, camera, target), {
    propShadows: config.graphics.propShadows,
    uploadBudget: config.graphics.uploadBudget,
    facetTerrain: config.graphics.facetTerrain,
  });
const player = new PlayerController(camera, config.spawn, renderer.domElement);
// Cars and bikes are found parked, and helicopters on helipads, and got into
// with E; `?vehicles=summon` brings back V, B and H conjuring one anywhere.
player.summonVehicles = new URLSearchParams(window.location.search).get('vehicles') === 'summon';
// The car tells road from grass by whether a built surface is under it, at
// its own height — so a bridge deck is road, and the field below it is not.
player.setSurfaceProbe((lat, lon) => {
  const at = world.toLocal(lat, lon);
  return paved(at.east, -at.north);
});
/**
 * A built surface under scene (x, z) — a road, a path, a bridge — or a car
 * park or paving the map paints on the ground: what drives as tarmac, and is
 * plowed in winter.
 */
function paved(x: number, z: number): boolean {
  return worldRenderer.surfaceHeightAt(x, z, player.groundContact + 0.75) !== undefined
    || PLOWED_LANDCOVER.includes(worldRenderer.landcoverAt(x, z));
}
// In a tunnel the cameras stay under its ceiling, not up in the hill over it.
player.setCeilingProbe((x, z, y) => worldRenderer.tunnelCeiling(x, z, y));
/** How high a built surface can be above the feet and still be stepped onto. */
const STEP_HEIGHT = 0.75;
/** How far over the terrain a surface must be to count as up on a roof, metres. */
const ROOF_CLEARANCE = 1.5;
// Buildings, trunks and posts are solid: the player and the car are pushed
// out of them. Scene x is east and z is south.
function pushOut(lat: number, lon: number, radius: number, feet: number, head: number): ReturnType<Collider> {
  const at = world.toLocal(lat, lon);
  // Up on a roof, the building is underfoot rather than in the way: its
  // eaves, ridge and parapet all reach above the feet. A taller building
  // beside it still stops the body, as there is no roof of this one there.
  // Only a roof: on a bridge deck, as high over the river, the guardrails and
  // parapets still stop the body.
  const terrain = world.heightAt(lat, lon);
  const underfoot = worldRenderer.surfaceHeightAt(at.east, -at.north, feet + STEP_HEIGHT);
  const roof = worldRenderer.rooftopHeightAt(at.east, -at.north, feet + STEP_HEIGHT);
  const aloft = terrain !== undefined && underfoot !== undefined && roof !== undefined && roof >= underfoot - 0.05 &&
    underfoot > terrain + ROOF_CLEARANCE && feet >= underfoot - STEP_HEIGHT;
  // At the wheel, a hedge or a bush is driven through, not into (see `plants`).
  const driving = player.state.mode === 'drive' || player.state.mode === 'moto';
  const out = aloft ? undefined : worldRenderer.collide(at.east, -at.north, radius, feet, head, driving ? throughPlants : undefined);
  // The vehicles left standing are solid too.
  const x0 = out?.x ?? at.east, z0 = out?.z ?? -at.north;
  const car = fleet.collide(x0, z0, radius, feet, head, (la, lo) => world.toLocal(la, lo));
  const heli = heliFleet.collide(car?.x ?? x0, car?.z ?? z0, radius, feet, head, (la, lo) => world.toLocal(la, lo));
  // And so are the cars driving past.
  const moving = traffic.collide(heli?.x ?? car?.x ?? x0, heli?.z ?? car?.z ?? z0, radius, feet, head);
  const parked = moving ?? heli ?? car;
  if (out === undefined && parked === undefined) return undefined;
  const x = parked?.x ?? x0, z = parked?.z ?? z0;
  const dx = x - at.east, dz = z - -at.north, len = Math.hypot(dx, dz) || 1;
  const geo = world.toGeo(x, -z);
  const nx = out?.nx ?? dx / len, nz = out?.nz ?? dz / len;
  // What was hit, if it can be moved: a car in traffic, one left standing,
  // or a parked one (a car-sized box among the props). Its weight shares the hit.
  let body: Body | undefined, size: number | undefined;
  if (moving !== undefined) {
    body = { mass: massOf(moving.car.species), vx: moving.car.vx, vz: moving.car.vz, build: buildOfSpecies(moving.car.species), ref: { kind: 'traffic', id: moving.car.id, police: moving.car.police } };
    size = VEHICLE_SIZE[moving.car.species].halfWidth * 2;
  } else if (car !== undefined && heli === undefined) {
    const v = car.vehicle;
    body = { mass: massOf(v.species), vx: v.motion?.vx ?? v.chaser?.vx ?? 0, vz: v.motion?.vz ?? v.chaser?.vz ?? 0, build: buildOf(v), ref: { kind: 'fleet', vehicle: v } };
    size = VEHICLE_SIZE[v.species].halfWidth * 2;
  } else if (out !== undefined && out.species >= 0 && driving) {
    const name = SPECIES[out.species];
    const prop = propBehaviour(name);
    if (prop !== undefined) {
      // Street furniture on its fixings: it holds, or comes free and is shared by weight.
      body = { mass: prop.mass, vx: 0, vz: 0, build: 'car', stiffness: prop.stiffness, holds: prop.holds, ref: { kind: 'prop', species: name, x: out.cx, z: out.cz } };
    } else if (VEHICLES.has(name)) {
      // A parked vehicle: which one, by its middle.
      const p = worldRenderer.nearestParked(out.cx, out.cz, 1);
      if (p !== undefined && VEHICLE_SIZE[p.species as VehicleSpecies] !== undefined) {
        body = { mass: massOf(p.species), vx: 0, vz: 0, build: buildOfSpecies(p.species), ref: { kind: 'parked', parked: p } };
        size = VEHICLE_SIZE[p.species as VehicleSpecies].halfWidth * 2;
      }
    }
  }
  // How wide what it hit is, for how a crash crushes: a post narrow, a car or a wall wide.
  const width = size ?? Math.min(out?.width ?? Infinity, parked === undefined ? Infinity : 1.8);
  return { lat: geo.lat, lon: geo.lon, nx, nz, width, body };
}
player.setCollider(pushOut);

// The vehicles: parked ones are props in the tiles; the one being driven and
// the ones left behind are full models, kept by the fleet. These two are
// only drawn once, hidden, so their shaders compile before the first drive.
const warmCar = createVehicle();
const ride = createRide();
warmCar.root.visible = false;
scene.add(warmCar.root);
const fleet = new Fleet(scene);
// Crashes: the driven vehicle's wreck, and the pieces broken off it lying in
// the road. See core/vehicle/damage.ts and three/crumple.ts.
const debris = new Debris();
scene.add(debris.group);
/** Broken glass on the ground: see `three/shards.ts`. */
const shards = new Shards();
scene.add(shards.mesh);
const SHELTERS: ReadonlySet<Species> = new Set(['shelter']);

/** The ground under scene (x, z) near height `y`: a built surface within half a metre over it, or the terrain. */
function sceneGround(x: number, z: number, y: number): number | undefined {
  const geo = world.toGeo(x, -z);
  const terrain = world.heightAt(geo.lat, geo.lon);
  if (terrain === undefined) return undefined;
  return worldRenderer.ground(x, z, terrain, y + 0.5);
}

/** Plants a vehicle drives through rather than into: see `core/vehicle/props.ts`. */
const throughPlants = (species: number) => SOFT[SPECIES[species]] !== undefined;
const PLANTS: ReadonlySet<Species> = new Set(Object.keys(SOFT) as Species[]);
/** Plants already flattened, by tile, kind and instance: they drag, but are not flattened again. */
const flattened = new Set<string>();
/** Which fleet vehicle each of the traffic's obstacles is, as last given: see `fleetFootprints`. */
const obstacleOwners: FleetVehicle[] = [];
// Steam, then smoke, from under a crashed vehicle's bonnet.
const smoke = new Smoke();
scene.add(smoke.mesh);
const smokeFrom = new Vector3();

const warmBike = createMotorbike();
/** The ridden two-wheeler's sprung chassis, made again for whichever is got on (see `drive`). */
const motoRide = createMotoRide();
const rider = createRiderState();
const scooterRider = createScooterRiderState();

/** How a two-wheeler rides: the motocrosser's spec, or the e-scooter's. */
function specOf(v: FleetVehicle): TwoWheelerSpec {
  return v.bike?.kind === 'scooter' ? SCOOTER_SPEC : MOTO_SPEC;
}
warmBike.root.visible = false;
scene.add(warmBike.root);
const roost = new Roost();
scene.add(roost.mesh);

// Helicopters wait on helipads, and stay where they are left. See three/heliFleet.ts.
const heliFleet = new HeliFleet(scene);
// Flying at night, a searchlight shines where the camera looks.
const searchlight = new Searchlight(scene);
const viewDirection = new Vector3();
const warmHeli = createHelicopter();
warmHeli.root.visible = false;
scene.add(warmHeli.root);

// Compile the machines' shaders now, in the background, rather than in the
// frame where the player first gets into one. compileAsync collects what to
// compile when it is called, so they need be visible only for the call.
{
  const hidden = [warmCar.root, warmBike.root, warmHeli.root, parachute].filter(o => !o.visible);
  for (const o of hidden) o.visible = true;
  void renderer.compileAsync(scene, camera).catch(() => {});
  for (const o of hidden) o.visible = false;
}

// The loaders must read the origin at request time, not at construction, so a
// rebase applies to everything started after it — hence the indirection through
// a reference the World fills in once it exists.
const worldRef: { current?: World } = {};
const currentOrigin = () => {
  const world = worldRef.current;
  if (world === undefined) throw new Error('World not constructed yet');
  return world.currentOrigin;
};

const loader = new ElevationTileLoader(
  { ...config.elevation, encoding: 'terrarium' },
  currentOrigin,
  config.concurrency.elevation,
  today,
  config.graphics.facetTerrain,
);
const buildingTags = new BuildingTagService();
// Power lines, fences, walls, hedges and cell towers, from the same Overpass servers (off with the building tags).
const osmLines = new OsmLineService();
const vectorLoader = new VectorTileLoader(
  config.vector,
  currentOrigin,
  {
    maxTrees: config.maxTreesPerTile,
    densityScale: config.treeDensityScale,
    houseDetail: config.houseDetail,
    getDay: today,
    buildingTags: config.buildingTags ? buildingTags : undefined,
    osmLines: config.buildingTags ? osmLines : undefined,
    forest: config.forest,
    roadMaxSegment: config.roadMaxSegment,
    minRoadHalfWidth: config.minRoadHalfWidth,
    elevation: { overlay: config.elevation.overlay, encoding: 'terrarium', tileSize: config.elevation.tileSize },
  },
  config.concurrency.vector,
);
// Placing a tile's features on its terrain is the heaviest per-tile work, and
// it runs in its own workers so a tile arriving never freezes a frame.
const world = new World(config, worldRenderer, loader, vectorLoader,
  createPlacementWorkers(Math.min(2, Math.max(1, (navigator.hardwareConcurrency ?? 4) - 2))));
// A fixed point on the earth, to tell when the scene's origin has moved under the debris.
const debrisAnchor = world.toGeo(0, 0);
const debrisAt = { x: 0, z: 0 };
const villagers = new Villagers(crowd, worldRenderer.footpaths, { population: config.villagers });
const traffic = new Traffic(worldRenderer.lanes, { population: config.traffic });
// The buses on their lines, from the real timetable where there is one (see app/transit.ts).
const transitFeed = new TransitFeed();
const busSigns = new BusSigns();
scene.add(busSigns.group);
// Gothenburg's trams on their lines (see three/trams.ts).
const trams = new Trams();
// Trains on the railways: Sweden's live, made up elsewhere. See three/trains.ts.
const trains = new Trains();
scene.add(trains.group);
const trainFeed = new TrainFeed();
// The stations' name boards by the tracks. See three/stationBoards.ts.
const stationBoards = new StationBoards();
scene.add(stationBoards.group);
/** The railway stations within a few kilometres, looked up once a second: the trains stop at them and their boards stand. */
let stationsAbout: { at: number; list: RailStation[] } = { at: -Infinity, list: [] };
function railStations(x: number, z: number): RailStation[] {
  const now = performance.now();
  if (now - stationsAbout.at > 1000) stationsAbout = { at: now, list: worldRenderer.railStationsNear(x, z, 3500) };
  return stationsAbout.list;
}
scene.add(trams.group);
scene.add(traffic.group);
// Everyone in the street sees everyone else: cars stop for the villagers, and
// the villagers wait at the kerb for the cars (core/traffic/pedestrians.ts).
villagers.setTraffic(traffic);
traffic.setPeople(villagers);
// Sound: one mixer for everything, made on the first key press or tap,
// every sound synthesised (see three/audio/ and core/audio/).
const sound = new SoundSystem();
const trainSounds = new TrainSounds(sound);
// An Xbox controller, or any in the standard layout, as the controls. See three/gamepad.ts.
const gamepad = new GamepadControls(player, (text) => {
  note = { text, until: performance.now() + 2500 };
  hintTimer = 0;
}, () => sound.play('ui:pad', { bus: 'effects', gain: 0.5 }));
/** The car radio: the real stations of wherever the car is, R to tune. */
const radio = new CarRadio((text) => {
  note = { text, until: performance.now() + 2500 };
  hintTimer = 0;
});
const engines = new EngineVoices(sound);
const worldSound = new WorldSound(sound);
const effects = new EffectSound(sound);
engines.onAirBrake = (at) => effects.airBrake(at);
const horn = new Horn(sound);

// --- police ----------------------------------------------------------------------------
// Police cars in the traffic, in the livery of the country the player is in
// (core/geo/country.ts, core/traffic/police.ts); K sets those near responding,
// blue lights and sirens, and the traffic gives way. See docs/police.md.
const sirens = new Sirens(sound);
/** Built-up enough, by the traffic's measure, for a police helicopter over it. */
const HELI_CITY = 25;
const policeHeli = new PoliceHelicopter(scene, { flash: liveryOf(undefined).a, bands: liveryOf(undefined).b });
let policeCountry: Country | undefined | null = null;
let policeSince = Infinity;
/** Whether K last set the police near responding: the next press stands them down. */
let policeResponding = false;

/**
 * Every siren the player can hear: the traffic's responding police, and the
 * police cars of the fleet with theirs on, the one being driven among them,
 * whose lamps flash here too.
 */
function policeSirens(px: number, pz: number): SirenSource[] {
  const out = traffic.sirens(px, pz);
  for (const v of fleet.vehicles) {
    if (v.police === undefined) continue;
    flashKit(v.police.kit, v.police.siren, clock);
    if (!v.police.siren) continue;
    const driven = v === fleet.driven;
    const at = world.toLocal(v.lat, v.lon);
    out.push({ id: -1 - fleet.vehicles.indexOf(v), kind: liveryOf(policeCountry ?? undefined).siren, distance: driven ? 0 : Math.hypot(at.east - px, -at.north - pz), closing: 0 });
  }
  return out;
}

// --- police at their stations -----------------------------------------------------
// Near a mapped police station, two officers stand about outside it and a
// police car is parked at the kerb of the road beside it: one the player can
// take. They come when the player is within `STATION_NEAR` and go once they
// are beyond `STATION_FAR`, the car unless it has been driven or damaged.

const STATION_NEAR = 250;
const STATION_FAR = 350;
const stations = new Map<string, { officers: number[]; car: FleetVehicle | undefined }>();
let stationsSince = Infinity;

/** A number of its own from a station's id, for who works there. */
function seedOf(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Staff the stations the player has come near, and stand down the ones left behind, about once a second. */
function staffStations(dt: number, px: number, pz: number): void {
  stationsSince += dt;
  if (stationsSince < 1) return;
  stationsSince = 0;
  const uniform = liveryOf(policeCountry ?? undefined).uniform;
  for (const s of worldRenderer.policeStations(px, pz, STATION_NEAR)) {
    if (stations.has(s.id)) continue;
    const seed = seedOf(s.id);
    const officers: number[] = [];
    for (let k = 0; k < 2; k++) {
      const slot = villagers.addResident(officerLook(seed + k * 7919, uniform), s.x, s.z, 45, 10);
      if (slot !== undefined) officers.push(slot);
    }
    stations.set(s.id, { officers, car: parkPoliceCar(s.x, s.z, seed) });
  }
  const keep = new Set(worldRenderer.policeStations(px, pz, STATION_FAR).map(s => s.id));
  for (const [id, st] of stations) {
    if (keep.has(id)) continue;
    for (const slot of st.officers) villagers.removeResident(slot);
    const car = st.car;
    if (car !== undefined && car !== fleet.driven && fleet.vehicles.includes(car) && car.damage === undefined && car.motion === undefined) fleet.remove(car);
    stations.delete(id);
  }
}

/** A police car parked at the kerb of the road nearest scene (x, z), facing the way its lane runs; undefined with no road near. */
function parkPoliceCar(x: number, z: number, seed: number): FleetVehicle | undefined {
  const lane = worldRenderer.lanes.nearestLane(x, z, 60);
  if (lane === undefined) return undefined;
  // Over at the kerb: right of the lane's middle, (-dz, dx).
  const cx = lane.x - lane.dz * 2.4, cz = lane.z + lane.dx * 2.4;
  const geo = world.toGeo(cx, -cz);
  const y = sampleGroundNow(geo.lat, geo.lon);
  if (y === undefined) return undefined;
  return fleet.add(seed % 2 === 0 ? 'carSedan' : 'carEstate', 0xffffff, geo.lat, geo.lon, y, Math.atan2(-lane.dx, -lane.dz), true);
}

/** The officers riding the traffic's police motorbikes: a crowd slot and a rider's state each, by bike. */
const bikeRiders = new Map<number, { slot: number; rider: ReturnType<typeof createRiderState> }>();

/** Seat an officer on each police motorbike in the traffic, posed as the player rides, and let go of those gone. */
function rideBikes(dt: number): void {
  const bikes = traffic.bikes();
  const seen = new Set<number>();
  const uniform = liveryOf(policeCountry ?? undefined).uniform;
  for (const b of bikes) {
    seen.add(b.id);
    let r = bikeRiders.get(b.id);
    if (r === undefined) {
      const slot = crowd.add(officerLook(b.id * 2654435761, uniform));
      if (slot === undefined) continue;
      r = { slot, rider: createRiderState() };
      bikeRiders.set(b.id, r);
    }
    const seated = riderPose(r.rider, {
      speed: b.speed, steerAngle: 0, lean: -b.lean, accel: 0, airborne: false, offRoad: false, wheelie: 0, landing: 0,
    }, dt);
    crowd.placeRider(r.slot, b.matrix, { ...seated.pose, helmet: true });
  }
  for (const [id, r] of bikeRiders) {
    if (seen.has(id)) continue;
    crowd.remove(r.slot);
    bikeRiders.delete(id);
  }
}

// --- wanted by the police ------------------------------------------------------------
// Speeding past a police car, crashing in its sight, knocking someone down or
// crashing into the police make the player wanted (core/traffic/wanted.ts);
// the police chase them, more the more stars, until they are caught or get away.

const wanted = notWanted();
/** Offences since the frame's wanted step: knocking people down and crashes, each seen or not. */
const offences: Array<{ offence: Offence; seen: boolean }> = [];
/** How long since the player last sped in sight: one star's worth, not one a frame. */
let speedingFor = 0;
// --- police on foot and off the road ----------------------------------------------------
// Officers get out of police cars stopped near the player and run them down
// on foot, tackling them or taking them from their car; police cars leave
// the road to drive at a player who has. See core/traffic/pursuit.ts.

interface Officer {
  slot: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  phase: number;
  /** Seconds of sprint left. */
  sprint: number;
  /** Knocked down: the body, until they are up again. */
  fallen?: Fallen;
  /** Their way round what is built to where they are going, and seconds until it is looked for again. */
  way?: Way;
  replan: number;
}
const officers: Officer[] = [];
/** The police vehicles officers have already got out of: traffic cars by id, the fleet's by themselves. */
const unloaded = new Set<number | FleetVehicle>();
/** Where the police's scene positions were last made true, as a place and in the scene: they move with the scene's origin. */
let policeAnchor: { lat: number; lon: number; x: number; z: number } | undefined;
/** How near a stopped police car the player must be for its officers to get out, metres; how many may be out at once. */
const GET_OUT = 45;
const OFFICERS_MOST = 4;
/** How far the player may get from an officer before they give up, metres. */
const GIVES_UP = 140;
/** How far an officer on foot sees, metres. */
const OFFICER_SEES = 60;
/**
 * Standing still while wanted this long, seconds, the nearest police car
 * within `RUN_OVER` pulls up and its officers run over, however far its
 * road is; the longer they stand, the further it may be, to `RUN_OVER_MOST`.
 */
const STOOD_WANTED = 15;
const RUN_OVER = 160;
const RUN_OVER_MOST = 400;
function runOver(): number {
  return Math.min(RUN_OVER_MOST, RUN_OVER + 10 * Math.max(0, stoodWanted - STOOD_WANTED));
}
/** How long the player has stood still while wanted, seconds. */
let stoodWanted = 0;
/** How many police cars at most leave the road after the player; how near one must be, metres. */
const CHASERS_MOST = 3;
const CHASER_TAKEN = 150;
/** Seconds a police car off the road may stand held up by a wall before it gives up. */
const CHASER_STUCK = 3;
/** How long a police car's way off the road may be, against the straight line: a share of it and metres over. */
const WAY_SHARE = 1.6;
const WAY_OVER = 40;
/** The grid a way is found on, metres a square, and the most squares looked at: for a car, and for an officer. */
const CAR_CELL = 3, CAR_LOOKS = 2000;
const FOOT_CELL = 2, FOOT_LOOKS = 1200;
/** Seconds between looking for a police car's (or an officer's) way again. */
const REPLAN = 1;
/** Seconds since police cars were last asked whether one has a way off the road to the player. */
let takeSince = Infinity;
/** The traffic police cars found to have no way off the road to the player, by id, and when; not asked again for `NO_WAY` seconds. */
const noWay = new Map<number, number>();
const NO_WAY = 8;
/** Seconds after an arrest the officers stay about before they are gone. */
let afterArrest = 0;

/** Whether something built stands within `radius` of scene (x, z) at heights `feet` to `head`. */
function builtAt(radius: number, feet: number, head: number): Blocked {
  return (x, z) => worldRenderer.collide(x, z, radius, feet, head, throughPlants) !== undefined;
}

/** A way off the road for a car standing at `y`, from scene (x0, z0) to (x1, z1), round what is built; undefined if there is none near as short as a chase allows. */
function carWay(x0: number, z0: number, y: number, x1: number, z1: number): Way | undefined {
  const way = findWay(builtAt(1.4, y + 0.3, y + 1.5), x0, z0, x1, z1, CAR_CELL, CAR_LOOKS);
  return way !== undefined && way.length < WAY_SHARE * Math.hypot(x1 - x0, z1 - z0) + WAY_OVER ? way : undefined;
}

/** Get two officers out of a police vehicle standing at scene (cx, cy, cz), facing (dx, dz). */
function unload(key: number | FleetVehicle, cx: number, cy: number, cz: number, dx: number, dz: number): void {
  if (unloaded.has(key)) return;
  unloaded.add(key);
  const uniform = liveryOf(policeCountry ?? undefined).uniform;
  for (const side of [1, -1]) {
    if (officers.length >= OFFICERS_MOST) return;
    const slot = crowd.add(officerLook(Math.floor(cx * 7 + cz * 13 + side * 31), uniform));
    if (slot === undefined) return;
    // Out of the doors: either side of the car, (dz, -dx) its left.
    officers.push({ slot, x: cx + dz * 1.3 * side, y: cy, z: cz - dx * 1.3 * side, yaw: Math.atan2(-dx, -dz), phase: 0, sprint: SPRINT_TIME, replan: officers.length * 0.25 });
  }
}

/**
 * Arrested: fined by the stars `stars` were, no longer wanted, and after a
 * moment with the officers, taken to the nearest police station and let go
 * outside it (`stepRelease`).
 */
function arrested(how: string, stars: number): void {
  const due = fine(stars);
  wallet -= due;
  Object.assign(wanted, notWanted());
  note = { text: `${how}: fined ${kronor(due)}`, until: performance.now() + 4000 };
  afterArrest = 4;
  takeToStation();
  if (trip !== undefined) endTrip('Arrested');
}

/**
 * The officers out on foot, this frame: out of police cars stopped near the
 * player (or one pulled up for them, once the player has stood still long
 * enough), running at them round what is built, and bringing them down:
 * tackled on foot, taken from a vehicle standing. `target` is where the
 * police think the player is: them, or where they last saw them; `known`
 * whether that is them.
 */
function stepOfficers(dt: number, x: number, z: number, target: { x: number; z: number }, known: boolean): void {
  const s = player.state;
  const onFoot = s.mode === 'walk' && !player.isDown;
  const still = Math.abs(s.speed) <= 1;
  stoodWanted = wanted.stars > 0 && known && still ? stoodWanted + dt : 0;
  if (wanted.stars > 0 && known && (onFoot || still) && officers.length < OFFICERS_MOST) {
    const car = traffic.stoppedNear(x, z, GET_OUT);
    if (car !== undefined) unload(car.id, car.x, car.y, car.z, car.dx, car.dz);
    for (const v of fleet.vehicles) {
      if (v.chaser === undefined || v.chaser.speed > 1) continue;
      const at = world.toLocal(v.lat, v.lon);
      if (Math.hypot(at.east - x, -at.north - z) < GET_OUT) unload(v, at.east, v.y, -at.north, -Math.sin(v.yaw), -Math.cos(v.yaw));
    }
    if (stoodWanted > STOOD_WANTED && officers.length === 0) {
      const far = traffic.pullUp(x, z, runOver());
      if (far !== undefined) unload(far.id, far.x, far.y, far.z, far.dx, far.dz);
    }
  }
  afterArrest = Math.max(0, afterArrest - dt);
  // Where the player will be in a moment, if it is them they are after: run to there.
  const v = known ? player.velocity : { x: 0, z: 0 };
  const tx = target.x + v.x * 0.35, tz = target.z + v.z * 0.35;
  for (let i = officers.length - 1; i >= 0; i--) {
    const o = officers[i];
    if (o.fallen !== undefined) {
      o.fallen.step(dt, othersBodies, clock);
      if (o.fallen.stood === undefined) {
        [o.x, o.y, o.z] = o.fallen.at;
        o.fallen.draw();
        continue;
      }
      ({ x: o.x, y: o.y, z: o.z, yaw: o.yaw } = o.fallen.stood);
      o.fallen = undefined;
    }
    const straight = Math.hypot(tx - o.x, tz - o.z);
    if ((wanted.stars === 0 && afterArrest <= 0) || straight > GIVES_UP) {
      crowd.remove(o.slot);
      officers.splice(i, 1);
      continue;
    }
    // Round what is built: a way looked for now and then, straight at them when it is clear.
    o.replan -= dt;
    if (o.replan <= 0) {
      o.replan = REPLAN;
      const open = clearLine(builtAt(0.45, o.y + 0.3, o.y + 1.7), o.x, o.z, tx, tz, 1);
      o.way = open ? undefined : findWay(builtAt(0.45, o.y + 0.3, o.y + 1.7), o.x, o.z, tx, tz, FOOT_CELL, FOOT_LOOKS);
    }
    const aim = o.way !== undefined && o.way.points.length > 1 ? aimAlong(o.way, o.x, o.z, 1.5) : { x: tx, z: tz, remaining: straight };
    const dx = aim.x - o.x, dz = aim.z - o.z, d = Math.hypot(dx, dz);
    // After an arrest, with the player standing still, or come to where they were last seen, they walk.
    let pace = wanted.stars === 0 ? 0 : officerPace(aim.remaining, o.sprint);
    if ((still || !known) && aim.remaining < 12) pace = Math.min(pace, 2);
    if (!known && aim.remaining < 2) pace = 0;
    if (pace > JOG) o.sprint -= dt;
    if (d > 1e-3 && pace > 0) {
      o.x += (dx / d) * Math.min(d, pace * dt);
      o.z += (dz / d) * Math.min(d, pace * dt);
      o.yaw = Math.atan2(-dx, -dz);
    }
    // Not through walls.
    const wall = worldRenderer.collide(o.x, o.z, 0.35, o.y + 0.3, o.y + 1.7, throughPlants);
    if (wall !== undefined) { o.x = wall.x; o.z = wall.z; }
    o.y = bodyGround(o.x, o.z, o.y + 1) ?? o.y;
    o.phase = advancePhase(o.phase, pace, dt);
    crowd.place(o.slot, { x: o.x, y: o.y, z: o.z, yaw: o.yaw, pose: gaitPose(pace, o.phase, clock), visible: true });
    if (wanted.stars === 0) continue;
    // On foot within reach: brought down. In a vehicle standing: taken from it.
    const reach = Math.hypot(x - o.x, z - o.z);
    if (onFoot && reach < TACKLE) {
      // Running from them: brought down. Standing for them: taken by the arm.
      const run = Math.hypot(x - o.x, z - o.z) || 1;
      const stars = wanted.stars;
      if (!still && player.knockDown()) knockDownPlayer({ kind: 'struck', v: [((x - o.x) / run) * 3.5, 0, ((z - o.z) / run) * 3.5], bumper: 0.9 });
      arrested(still ? 'Arrested' : 'Tackled and arrested', stars);
    } else if (!onFoot && still && reach < AT_THE_DOOR + (fleet.driven !== undefined ? VEHICLE_SIZE[fleet.driven.species].halfWidth : 0)) {
      arrested('Arrested', wanted.stars);
    }
  }
  if (wanted.stars === 0 && afterArrest <= 0) {
    unloaded.clear();
    noWay.clear();
  }
}

/**
 * Police cars that have left the road after the player: when the player is
 * off it, or on foot, the nearest chasing police cars with a way to them
 * come out of the traffic and drive it, round what is built, ramming a
 * vehicle and pulling up short of someone on foot for the officers to get
 * out. Standing down, they stop where they are and are cleared away later
 * as wrecks are.
 */
function driveChasers(dt: number, x: number, z: number, target: { x: number; z: number }, known: boolean): void {
  const s = player.state;
  const onFoot = s.mode === 'walk';
  const h = (s.heading * Math.PI) / 180;
  const offRoad = worldRenderer.lanes.nearestLane(x, z, 9) === undefined || (onFoot && worldRenderer.lanes.limitAt(x, z, Math.sin(h), -Math.cos(h)) === undefined);
  const chasing = fleet.vehicles.filter(v => v.chaser !== undefined).length;
  takeSince += dt;
  if (wanted.stars > 0 && known && (offRoad || onFoot) && chasing < CHASERS_MOST && takeSince > REPLAN) {
    takeSince = 0;
    // One way looked for a time, it is not cheap; a car with none is not asked again for a while.
    let way: Way | undefined, looked = false;
    const r = traffic.takeChaser(x, z, Math.max(CHASER_TAKEN, runOver()), (cx, cz, cy, id) => {
      if (looked || (noWay.get(id) ?? -Infinity) > clock - NO_WAY) return false;
      looked = true;
      way = carWay(cx, cz, cy, x, z);
      if (way === undefined) noWay.set(id, clock);
      return way !== undefined;
    });
    if (r !== undefined) {
      const v = wreckFromTraffic(r);
      v.motion = undefined;
      if (v.police !== undefined) v.police.siren = true;
      v.chaser = { speed: Math.hypot(r.vx, r.vz), vx: r.vx, vz: r.vz, stuck: 0, way, replan: REPLAN };
    }
  }
  const pv = known ? player.velocity : { x: 0, z: 0 };
  const tx = target.x + pv.x * 0.5, tz = target.z + pv.z * 0.5;
  // One car looks for its way again a frame: it is not cheap.
  let planned = false;
  for (const v of fleet.vehicles) {
    const c = v.chaser;
    if (c === undefined) continue;
    // Knocked about, it slides with the hit (`slideFleet`) and drives on after; wrecked, it is done.
    if (v.motion !== undefined) {
      c.speed = 0;
      continue;
    }
    if (wanted.stars === 0 || v === fleet.driven || v.handling?.dead !== undefined || c.stuck > CHASER_STUCK) {
      v.chaser = undefined;
      if (v.police !== undefined && v !== fleet.driven) v.police.siren = false;
      continue;
    }
    const at = world.toLocal(v.lat, v.lon);
    const cx = at.east, cz = -at.north;
    c.replan -= dt;
    if (c.replan <= 0 && !planned) {
      planned = true;
      c.replan = REPLAN;
      c.way = carWay(cx, cz, v.y, tx, tz) ?? c.way;
    }
    // Along its way, its last leg straight at them as they are now.
    const straight = Math.hypot(tx - cx, tz - cz);
    const aim = c.way !== undefined && c.way.points.length > 1 ? aimAlong(c.way, cx, cz, 6) : { x: tx, z: tz, remaining: straight };
    const car = { yaw: v.yaw, speed: c.speed };
    chaserStep(car, aim.x - cx, aim.z - cz, Math.hypot(pv.x, pv.z), onFoot || !known, worldRenderer.lanes.nearestLane(cx, cz, 6) === undefined, dt, aim.remaining);
    v.yaw = car.yaw;
    c.speed = car.speed;
    const fx = -Math.sin(v.yaw), fz = -Math.cos(v.yaw);
    let nx = cx + fx * c.speed * dt, nz = cz + fz * c.speed * dt;
    // Kept out of buildings, walls and trees: pushed clear, and slowed.
    const size = VEHICLE_SIZE[v.species];
    const wall = worldRenderer.collide(nx, nz, size.halfWidth, v.y + 0.3, v.y + 1.5, throughPlants);
    if (wall !== undefined) {
      nx = wall.x;
      nz = wall.z;
      c.speed *= 0.6;
    }
    // Held up by a wall short of them: it gives up after a while, and stands.
    c.stuck = c.speed < 1 && straight > 15 ? c.stuck + dt : 0;
    c.vx = fx * c.speed;
    c.vz = fz * c.speed;
    const geo = world.toGeo(nx, -nz);
    v.lat = geo.lat;
    v.lon = geo.lon;
    v.y = sampleGroundAt(geo.lat, geo.lon, v.y) ?? v.y;
    // Pitched and rolled to the ground under it.
    const g = (along: number, side: number) => {
      const p = world.toGeo(nx + fx * along - fz * side, -(nz + fz * along + fx * side));
      return sampleGroundAt(p.lat, p.lon, v.y) ?? v.y;
    };
    v.pitch = Math.atan((g(size.halfLength, 0) - g(-size.halfLength, 0)) / (2 * size.halfLength));
    v.roll = Math.atan((g(0, size.halfWidth) - g(0, -size.halfWidth)) / (2 * size.halfWidth));
  }
}

/** A vehicle going by the officers on foot: anyone it strikes closing fast enough is knocked down. Returns the hits. */
function strikeOfficers(s: Striker, y: number, bumper: number): Hit[] {
  const hits: Hit[] = [];
  for (const o of officers) {
    if (o.fallen !== undefined || Math.abs(o.y - y) > 1.5) continue;
    const hit = strikePerson(s, o.x, o.z);
    if (hit === undefined || hit.closing < KNOCKED_DOWN) continue;
    o.fallen = new Fallen(crowd, o.slot);
    startFall(o.fallen.body, { kind: 'struck', v: [hit.person.x, 0, hit.person.z], bumper });
    hits.push(hit);
  }
  return hits;
}

/** Whether a police car that has left the road has stopped beside scene (x, z). */
function chaserCaught(x: number, z: number): boolean {
  return fleet.vehicles.some((v) => {
    if (v.chaser === undefined || v.chaser.speed > 1) return false;
    const at = world.toLocal(v.lat, v.lon);
    return Math.hypot(at.east - x, -at.north - z) < 9;
  });
}

// --- what the police see ------------------------------------------------------------
// A police car, an officer on foot or the helicopter sees the player within
// its sight with nothing built between (the helicopter, from above, over
// anything). In another vehicle than they last saw them in, or lying low at
// a garage door, they know them only from close by: see `seesWanted`.

/** Where the police last saw the player, or heard of them: scene x, z. Further than `LOST` from it, out of sight, they lose track. */
let lastSeen: { x: number; z: number } | undefined;
const LOST = 150;
/** What the police last saw the player in: a vehicle, or on foot. */
let seenIn: FleetVehicle | 'foot' | undefined;
/** How near overhead, metres, the police helicopter keeps a chased player in sight. */
const HELI_SEES = 220;
/** How far in front of a garage door, past the vehicle's own length, it is parked at it, metres. */
const AT_GARAGE = 2.5;
/** Whether the player was lying low last frame, for telling them once. */
let wasLyingLow = false;
/** Whether the police saw the player when last looked, and seconds since: looked at a few times a second. */
let sawThem = false;
let sightSince = Infinity;
const SIGHT_EVERY = 0.2;

/** Whether the player is lying low: in a vehicle stopped at a garage door, in front of it and within its width. */
function lyingLow(x: number, z: number): boolean {
  const v = fleet.driven;
  if (v === undefined || Math.abs(player.state.speed) > 0.5) return false;
  const reach = VEHICLE_SIZE[v.species].halfLength + AT_GARAGE;
  return worldRenderer.garagesNear(x, z, reach + 10).some((g) => {
    const rx = x - g.x, rz = z - g.z;
    const out = rx * g.faceX + rz * g.faceZ;
    const along = Math.abs(rx * g.faceZ - rz * g.faceX);
    return out > -0.5 && out < reach && along < g.span / 2;
  });
}

/** Whether nothing built stands between eyes at scene (x0, y0, z0) and (x1, y1, z1). */
function inSight(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean {
  return clearLine(builtAt(0.2, Math.min(y0, y1) + 1, Math.max(y0, y1) + 1.6), x0, z0, x1, z1, 2);
}

/** Whether the police see the player now as the one they want, `changed` vehicle since they last saw them or not, `low` or not. */
function policeSeePlayer(x: number, z: number, changed: boolean, low: boolean): boolean {
  const y = player.groundContact;
  const sees = (ox: number, oy: number, oz: number, sight: number) => {
    const d = Math.hypot(ox - x, oz - z);
    return seesWanted(d, sight, changed, low) && inSight(ox, oy, oz, x, y, z);
  };
  for (const car of traffic.policeNear(x, z, y)) if (sees(car.x, car.y, car.z, POLICE_SEES)) return true;
  for (const v of fleet.vehicles) {
    if (v.chaser === undefined) continue;
    const at = world.toLocal(v.lat, v.lon);
    if (sees(at.east, v.y, -at.north, POLICE_SEES)) return true;
  }
  for (const o of officers) if (o.fallen === undefined && sees(o.x, o.y, o.z, OFFICER_SEES)) return true;
  // The helicopter looks down over what is built.
  const heli = policeHeli.position;
  return heli !== undefined && wanted.stars > 0 && seesWanted(Math.hypot(heli.x - x, heli.z - z), HELI_SEES, changed, low);
}

/** Move what the police hold in scene coordinates with the scene's origin, when it moves. */
function shiftPolice(x: number, z: number): void {
  if (policeAnchor === undefined) {
    const geo = world.toGeo(x, -z);
    policeAnchor = { lat: geo.lat, lon: geo.lon, x, z };
    return;
  }
  const here = world.toLocal(policeAnchor.lat, policeAnchor.lon);
  const sx = here.east - policeAnchor.x, sz = -here.north - policeAnchor.z;
  if (sx === 0 && sz === 0) return;
  policeAnchor.x = here.east;
  policeAnchor.z = -here.north;
  const move = (p: { x: number; z: number }) => { p.x += sx; p.z += sz; };
  for (const o of officers) {
    move(o);
    o.fallen?.shift(sx, sz);
    o.way?.points.forEach(move);
  }
  for (const v of fleet.vehicles) v.chaser?.way?.points.forEach(move);
  if (lastSeen !== undefined) move(lastSeen);
}

/** The frame's wanted step: offences, what the police see, the chase, and how it ends, if it does. */
function stepPolice(dt: number, x: number, z: number): 'arrested' | 'escaped' | undefined {
  shiftPolice(x, z);
  const s = player.state;
  const ride = fleet.driven ?? 'foot';
  if (wanted.stars === 0) seenIn = ride;
  const changed = seenIn !== undefined && seenIn !== ride;
  const low = wanted.stars > 0 && lyingLow(x, z);
  if (low && !wasLyingLow) note = { text: 'Lying low: out of sight here, they lose you sooner', until: performance.now() + 3000 };
  wasLyingLow = low;
  sightSince += dt;
  if (sightSince >= SIGHT_EVERY) {
    sightSince = 0;
    sawThem = policeSeePlayer(x, z, changed, low);
  }
  const seen = sawThem;
  // Speeding: thirty over the road's speed, in a machine, with a police car near.
  if ((s.mode === 'drive' || s.mode === 'moto') && seen) {
    const h = (s.heading * Math.PI) / 180;
    const limit = worldRenderer.lanes.limitAt(x, z, Math.sin(h), -Math.cos(h));
    speedingFor = speeding(Math.abs(s.speed), limit) ? speedingFor + dt : 0;
    if (speedingFor > 1.5 && offend(wanted, 'speeding', true)) note = { text: 'Speeding: the police are after you', until: performance.now() + 2500 };
  } else {
    speedingFor = 0;
  }
  let witnessed = false;
  for (const o of offences.splice(0)) {
    if (!offend(wanted, o.offence, o.seen)) continue;
    witnessed = true;
    const why = o.offence === 'person' ? 'You knocked someone down' : o.offence === 'policeCrash' ? 'You hit a police car'
      : o.offence === 'carjack' ? 'You pulled a driver out in sight of the police' : 'You crashed in sight of the police';
    note = { text: `${why}: ${'★'.repeat(wanted.stars)}`, until: performance.now() + 2500 };
  }
  // Seen, or told of by witnesses: they know where, and in what.
  if (seen || witnessed || wanted.stars === 0 || lastSeen === undefined) {
    lastSeen = { x, z };
    seenIn = ride;
  }
  const known = seen || (seenIn === ride && !low && Math.hypot(x - lastSeen.x, z - lastSeen.z) < LOST);
  const caught = Math.abs(s.speed) < 1 && (traffic.caught(x, z) || chaserCaught(x, z));
  const before = wanted.stars;
  const ended = stepWanted(wanted, dt, seen, caught, known, low);
  if (ended === 'arrested') {
    arrested('Arrested', before);
  } else if (ended === 'escaped') {
    note = { text: changed ? 'You got away: they were looking for another vehicle' : 'You got away', until: performance.now() + 3000 };
  }
  // They go for the player while they know where they are; else for where they last saw them.
  const target = known ? { x, z } : lastSeen;
  traffic.pursue(wanted.stars > 0 ? target : undefined, pursuers(wanted.stars));
  driveChasers(dt, x, z, target, known);
  stepOfficers(dt, x, z, target, known);
  stepRelease(dt, x, z);
  return ended;
}

// --- let go at the station ------------------------------------------------------------
// Arrested, the player is taken to the nearest police station, asked of
// OpenStreetMap (`loaders/policeStations.ts`), or else the nearest in the
// tiles loaded, and let go on the pavement outside it, on foot: the screen
// goes dark between. With no station found, they are let go where they are.

/** Seconds from an arrest to being taken to the station; the most to wait on being told where one is, and on its streets loading. */
const TO_STATION = 3;
const ASKING = 10;
const ARRIVING = 10;
/** How far from a lane's middle its pavement is, metres. */
const PAVEMENT = 5;
/**
 * Being taken to the station: seconds on, the station once known (`null`
 * if none was found), and once there, `there`.
 */
let release: { t: number; station?: StationPlace | null; there?: StationPlace } | undefined;
/** The dark the screen goes between the arrest and the station. */
const dark = document.createElement('div');
dark.style.cssText = 'position:fixed;inset:0;background:#000;opacity:0;pointer-events:none;transition:opacity 0.6s;z-index:50';
document.body.appendChild(dark);

/** Start taking the player to the station: asking where the nearest is. */
function takeToStation(): void {
  const s = player.state;
  const r: NonNullable<typeof release> = { t: -TO_STATION };
  release = r;
  void findPoliceStation(s.lat, s.lon).then((station) => { r.station = station ?? null; });
}

/** Take the player to the station, once the arrest is done and they are on their feet. */
function stepRelease(dt: number, x: number, z: number): void {
  const r = release;
  if (r === undefined || player.isDown) return;
  r.t += dt;
  if (r.there === undefined) {
    if (r.t < -0.7) return;
    dark.style.opacity = '1';
    if (r.t < 0 || (r.station === undefined && r.t < ASKING)) return;
    // From OpenStreetMap, or the nearest loaded, or none.
    let station = r.station ?? undefined;
    if (station === undefined) {
      const near = worldRenderer.policeStations(x, z, 1e6).sort((a, b) => Math.hypot(a.x - x, a.z - z) - Math.hypot(b.x - x, b.z - z))[0];
      if (near !== undefined) station = { ...world.toGeo(near.x, -near.z), name: near.name };
    }
    if (station === undefined) {
      release = undefined;
      dark.style.opacity = '0';
      return;
    }
    for (const o of officers) crowd.remove(o.slot);
    officers.length = 0;
    unloaded.clear();
    afterArrest = 0;
    r.there = station;
    r.t = 0;
    player.teleport(station.lat, station.lon);
    return;
  }
  // There: on the pavement of the road nearest the station, on its side, once its streets are in.
  const at = world.toLocal(r.there.lat, r.there.lon);
  const sx = at.east, sz = -at.north;
  const lane = worldRenderer.lanes.nearestLane(sx, sz, 60);
  if (lane === undefined && r.t < ARRIVING) return;
  if (lane !== undefined) {
    const side = Math.sign((sx - lane.x) * -lane.dz + (sz - lane.z) * lane.dx) || 1;
    const geo = world.toGeo(lane.x - lane.dz * PAVEMENT * side, -(lane.z + lane.dx * PAVEMENT * side));
    player.teleport(geo.lat, geo.lon);
  }
  note = { text: `Let go outside ${r.there.name !== '' ? r.there.name : 'the police station'}`, until: performance.now() + 4000 };
  release = undefined;
  setTimeout(() => { dark.style.opacity = '0'; }, 800);
}

/** Dress the police for the country the player is in, looked at every couple of seconds. */
function dressPolice(dt: number, lat: number, lon: number): void {
  policeSince += dt;
  if (policeSince < 2) return;
  policeSince = 0;
  const country = countryAt(lat, lon);
  if (country === policeCountry) return;
  policeCountry = country;
  const livery = liveryOf(country);
  traffic.setLivery(livery);
  policeHeli.repaint({ flash: livery.a, bands: livery.b });
}
let hornHeard = 0;
worldRef.current = world;
// A parked car's fuel is found from where it stands: see ThreeRenderer.buildExhausts.
worldRenderer.toGeo = (east, north) => world.toGeo(east, north);
buildingTags.onLate = (tiles) => { for (const key of tiles) world.refreshVector(key); };
osmLines.onLate = (tiles) => { for (const key of tiles) world.refreshVector(key); };

// The credits; Lantmäteriet's (and Naturvårdsverket's) only while the ground
// under the player is theirs, which also shows where their tiles reach;
// their buildings' likewise, while the tile under the player carries them.
const credits = (overlay: boolean, lmBuildings = false) => [
  config.vector.attribution,
  ...(lmBuildings && config.vector.overlay?.buildingsAttribution !== undefined ? [config.vector.overlay.buildingsAttribution] : []),
  config.elevation.attribution,
  ...(overlay && config.elevation.overlay !== undefined ? [`Elevation here: ${config.elevation.overlay.attribution}`] : []),
  // Both cover Sweden: the forest's tiles reach where the ground model's do.
  ...(overlay && config.forest !== undefined ? [`Forest here: ${config.forest.attribution}`] : []),
  ...(overlay && config.forest?.trees !== undefined ? [`Trees here: ${config.forest.trees.attribution}`] : []),
  'Weather: Open-Meteo',
  'Radar: RainViewer',
];
const hud = new DebugHud(container, credits(false));
hud.setRenderScale(renderScale);

// The graphics switches. A choice is remembered as how it differs from the
// profile, so a profile improved later still reaches what was left alone.
let graphics = config.graphics;
/** Off the URL, or the next visit would take its `?fx=` over the choices. */
const dropFx = () => {
  const p = new URLSearchParams(window.location.search);
  if (!p.has('fx')) return;
  p.delete('fx');
  history.replaceState(history.state, '', `?${p.toString()}${window.location.hash}`);
};
hud.setSound(sound);
// The weather and the season by hand: see DebugHud.setWeather.
hud.setWeather({
  simulating: () => weatherSim.on,
  setSimulating: (on) => {
    // Simulating starts from the weather as it is.
    if (on && !weatherSim.on) weatherSim.weather = simulatedWeather(liveWeather());
    weatherSim.on = on;
    saveWeatherSim();
  },
  weather: () => weatherSim.on ? weatherSim.weather : liveWeather(),
  set: (patch) => {
    weatherSim.weather = simulatedWeather({ ...(weatherSim.on ? weatherSim.weather : liveWeather()), ...patch });
    weatherSim.on = true;
    saveWeatherSim();
  },
  presets: PRESET_NAMES,
  preset: (name) => {
    const p = presetWeather(name);
    if (p === undefined) return;
    weatherSim.weather = p;
    weatherSim.on = true;
    saveWeatherSim();
  },
  // A season is a date: the URL's ?time=, set to the season's middle at the
  // same time of day, then a reload, so every tile is built in it.
  season: () => new URLSearchParams(window.location.search).has('time')
    ? seasonState(climateAt(player.state.lat, player.state.lon), player.state.lat, today()).name : undefined,
  seasons: Object.keys(SEASON_DATES),
  setSeason: (name) => {
    // Back where the player is now, not where the URL last caught them.
    syncUrl(player.state, undefined);
    const params = new URLSearchParams(window.location.search);
    if (name === undefined) {
      params.delete('time');
    } else {
      const now = skyClock.now;
      const [month, day] = SEASON_DATES[name];
      const south = player.state.lat < 0;
      const date = new Date(now.getFullYear(), (month + (south ? 6 : 0)) % 12, day, now.getHours(), now.getMinutes());
      const pad = (n: number) => String(n).padStart(2, '0');
      params.set('time', `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`);
    }
    history.replaceState(history.state, '', `?${params.toString()}${window.location.hash}`);
    window.location.reload();
  },
});
hud.setGraphics(GRAPHICS_SETTINGS, GRAPHICS_PRESETS, () => graphics, (patch) => {
  const next = { ...graphics, ...patch };
  const changed: Partial<Graphics> = {};
  for (const key of Object.keys(next) as (keyof Graphics)[]) {
    // Opened with `?fx=`, its profile is not the one the next visit starts from: keep the lot.
    if (fxInUrl || next[key] !== profileGraphics[key]) Object.assign(changed, { [key]: next[key] });
  }
  saveGraphicsChoices(changed);
  dropFx();
  if (needsReload(graphics, next)) {
    window.location.reload();
    return;
  }
  graphics = next;
  look.setOptions(lookOptions(next));
  renderScale.setMaxRatio(next.maxPixelRatio);
  worldRenderer.setUploadBudget(next.uploadBudget);
}, () => {
  clearGraphicsChoices();
  dropFx();
  window.location.reload();
});

// Maps: drawn from the same data the world streams, in the same style.
const mapServices = {
  tiles: new MapTiles(config.vector.urlTemplate, config.elevation.urlTemplate, today),
  radar: new Radar(),
  samples: new WeatherSamples(),
};
const openWorldMap = () => {
  const s = player.state;
  worldMap.show(s.lat, s.lon);
  player.setInputEnabled(false);
};
const closeWorldMap = () => worldMap.hide();
// "Where am I": opens the world map on the device's own position — GPS on a
// phone, a network estimate on a desktop — for the player to confirm the jump.
const goToMyLocation = () => {
  if (!worldMap.open) openWorldMap();
  void worldMap.locateMe();
};
const minimap = new Minimap(container, mapServices, openWorldMap, goToMyLocation);
const worldMap = new WorldMap(
  container,
  mapServices,
  // Picked on the map: dropped in by parachute, from `MAP_DROP_HEIGHT`.
  (lat, lon) => player.teleport(lat, lon, MAP_DROP_HEIGHT),
  () => player.setInputEnabled(true),
);

// Phones and tablets get a stick, buttons and drag-to-look; see TouchControls.
// Multiplayer: everyone else online, on the maps. See docs/multiplayer.md.
const presence = new Presence(realtimeUrl(window.location));
const refreshPeople = () => {
  worldMap.setPeople({
    connected: presence.connected, online: presence.online, visible: presence.visible,
  }, () => { presence.visible = !presence.visible; });
};
presence.onChange = refreshPeople;
const remotePlayers = new RemotePlayers(scene, crowd);
let sentState: OwnState | undefined;
let groundUnderFeet: number | undefined;
// A hidden tab stops its frames, and with them its updates: its last word is
// sent now, standing on the ground if it was mid-jump.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'hidden' || sentState === undefined) return;
  const s = sentState;
  presence.sendNow(s.mode === 'walk' && groundUnderFeet !== undefined ? { ...s, altitude: groundUnderFeet } : s);
});
presence.connect();

// A game opened without a place in its link starts where the player is: see
// app/startLocation.ts. Meanwhile the default spot streams in behind.
if (resumed === undefined && !linkHasStart(window.location.search)) {
  player.setInputEnabled(false);
  void findStart(container).then((place) => {
    player.setInputEnabled(true);
    if (place === undefined) {
      const s = player.state;
      openWorldMap();
      worldMap.askForStart(s.lat, s.lon);
      return;
    }
    player.teleport(place.lat, place.lon);
  });
}

const touch = isTouchDevice(window.location.search)
  ? new TouchControls(container, player, renderer.domElement)
  : undefined;
if (touch !== undefined) minimap.resize(126);
registerServiceWorker();
fullscreenOnFirstTouch();
keepScreenAwake();
// Back closes the world map; otherwise it stays in the game (see shell.ts).
guardBackNavigation(() => {
  if (!worldMap.open) return false;
  closeWorldMap();
  return true;
});

window.addEventListener('keydown', (event) => {
  if (event.code === 'KeyM' && !worldMap.open) minimap.toggle();
  if (event.code === 'KeyN') {
    if (worldMap.open) closeWorldMap();
    else openWorldMap();
  }
  if (event.code === 'Escape' && worldMap.open) closeWorldMap();
  if (event.code === 'KeyL') goToMyLocation();
  if (event.code === 'Minus') minimap.zoomBy(-0.5);
  if (event.code === 'Equal') minimap.zoomBy(0.5);
});

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  look.resize(window.innerWidth, window.innerHeight);
  renderScale.refresh();
});

const focus = new Vector3();
const drawingBuffer = new Vector2();
let hudTimer = 0;
let hudWorld: string[] = [];
let hudPerf: string[] = [];
let climateTimer = 0;
const weatherFx = new WeatherFx(scene);
const sunDir = new Vector3();
const moonDir = new Vector3();

// Time controls: [ and ] step an hour, \ cycles the time-lapse speed.
const SPEEDS = [1, 60, 600, 3600];
window.addEventListener('keydown', (event) => {
  if (event.code === 'BracketLeft') skyClock.shiftHours(-1);
  if (event.code === 'BracketRight') skyClock.shiftHours(1);
  if (event.code === 'Backslash') {
    const i = SPEEDS.indexOf(skyClock.timescale);
    skyClock.timescale = SPEEDS[(i + 1) % SPEEDS.length];
  }
});

/**
 * Keep the URL pointing at where the player is, so a reload or a shared link
 * drops back into the same spot — the same parameters a spawn reads.
 *
 * Throttled: `replaceState` is cheap, but Safari throws after ~100 calls in 30
 * seconds, so every frame is out. Skipped when nothing moved enough to matter.
 */
/** Milliseconds of wall-clock time — the limit is wall-clock, and frame time is clamped. */
const URL_INTERVAL = 2000;
let urlSynced = -Infinity;
let lastUrl = '';
function syncUrl(state: { lat: number; lon: number; heading: number; mode: string; altitude: number }, ground: number | undefined): void {
  const params = new URLSearchParams(window.location.search);
  params.set('lat', state.lat.toFixed(6));
  params.set('lon', state.lon.toFixed(6));
  params.set('heading', state.heading.toFixed(0));
  params.set('mode', state.mode === 'freefall' ? 'parachute' : state.mode);
  // `alt` is metres above ground; it only means something off the ground.
  if ((state.mode === 'heli' || state.mode === 'freefall') && ground !== undefined) {
    params.set('alt', Math.max(0, state.altitude - ground).toFixed(0));
  } else {
    params.delete('alt');
  }
  const search = `?${params.toString()}`;
  if (search === lastUrl) return;
  lastUrl = search;
  try {
    history.replaceState(history.state, '', search + window.location.hash);
  } catch {
    // Rate-limited or sandboxed: the next sync tries again.
  }
}

// Example motocross lines (see three/jumps.ts): J lays one from wherever the player is.
const jumps = new JumpTracks();
scene.add(jumps.group);
const layJumps = () => {
  const s = player.state;
  const at = world.toLocal(s.lat, s.lon);
  const yaw = -s.heading * (Math.PI / 180);
  // Scene forward is (-sin, -cos); right of it (cos, -sin).
  const spot = (y: number, ahead: number, side: number) => ({
    x: at.east - Math.sin(y) * ahead + Math.cos(y) * side,
    z: -at.north - Math.cos(y) * ahead - Math.sin(y) * side,
  });
  const terrain = (x: number, z: number) => { const g = world.toGeo(x, -z); return world.heightAt(g.lat, g.lon); };
  const heading = clearHeading(yaw, (y, ahead, side) => {
    const p = spot(y, ahead, side);
    const g = terrain(p.x, p.z);
    return g !== undefined && worldRenderer.collide(p.x, p.z, 1.2, g + 0.2, g + 2) !== undefined;
  }, (y) => {
    const heights: number[] = [];
    for (let a = 10; a <= 10 + TRACK_LENGTH; a += 20) { const p = spot(y, a, 0); const g = terrain(p.x, p.z); if (g !== undefined) heights.push(g); }
    return heights.length < 2 ? 1 : (Math.max(...heights) - Math.min(...heights)) / TRACK_LENGTH;
  });
  const start = spot(heading, 10, 0);
  const geo = world.toGeo(start.x, -start.z);
  jumps.add(new JumpLine(geo.lat, geo.lon, heading));
  // Standing still, line up with it.
  player.faceIfStill(heading);
};

// --- getting in and out -----------------------------------------------------------
// E by a parked vehicle gets in; E at a standstill gets out again. See
// three/character/boarding.ts for the moves and three/fleet.ts for the cars.

/** How near the player must be to a vehicle's middle to get in, metres. */
const REACH = 4;
interface Boarding {
  vehicle: FleetVehicle;
  out: boolean;
  t: number;
  /** Getting in: the walk to the door, from where the player stood. */
  walk?: { x: number; y: number; z: number; time: number };
}
let boarding: Boarding | undefined;
const boardPose: RiderPose = drivingPose({ hipY: 0, hipZ: 0, wheelY: 0, wheelZ: 0, pedalY: 0, pedalZ: 0, x: 0, outsideX: 0 });
/** The last pose on the bike, to get off from. */
const lastRide: RiderPose = blendPose(boardPose, boardPose, 0);
const doorAt = new Vector3();

/** Remember where the driven vehicle is, for when it is left there. */
function leaveHere(v: FleetVehicle, lat: number, lon: number, y: number, yaw: number, pitch = 0, roll = 0): void {
  v.lat = lat; v.lon = lon; v.y = y; v.yaw = yaw; v.pitch = pitch; v.roll = roll;
}

function resetRides(): void {
  Object.assign(ride, createRide());
  Object.assign(motoRide, createMotoRide());
}

/**
 * Keep the fleet in step with the player's mode: out of a vehicle, nothing
 * is driven; in one with none (V or B with `?vehicles=summon`), a new one
 * where the player is.
 */
function syncFleet(): void {
  const s = player.state;
  const want = s.mode === 'drive' ? 'car' : s.mode === 'moto' ? 'moto' : undefined;
  if (want === undefined) {
    if (fleet.driven !== undefined) parkBeams();
    fleet.driven = undefined;
    return;
  }
  if (fleet.driven?.type === want) return;
  const yaw = (-s.heading * Math.PI) / 180;
  // The vehicle a resumed trip was in, else a conjured one.
  const back = resumeVehicle !== undefined && isTwoWheeler(resumeVehicle.species) === (want === 'moto') ? resumeVehicle : undefined;
  resumeVehicle = undefined;
  const v = back !== undefined
    ? fleet.add(back.species as ParkedSpecies, back.paint, s.lat, s.lon, s.altitude, yaw)
    : fleet.add(want === 'car' ? 'carHatch' : config.spawn.vehicle === 'scooter' ? 'parkedScooter' : 'parkedMoto',
      want === 'car' ? 0xe0503f : config.spawn.vehicle === 'scooter' ? 0xc8262c : 0xf0772f, s.lat, s.lon, s.altitude, yaw);
  if (back !== undefined) {
    v.tank = back.tank;
    // Its dents and what fell off it, made again in the same order.
    if (isDamage(back.damage)) {
      v.damage = back.damage;
      const wreck = wreckOf(v);
      for (const { crush, zone, scrape } of v.damage.crushes) for (const piece of wreck.hit(crush, zone, scrape)) piece.mesh.geometry.dispose();
    }
  }
  drive(v);
}

/** How a vehicle of a parked species crushes. */
function buildOfSpecies(species: string): Build {
  return isTwoWheeler(species) ? 'moto' : species === 'truckBox' || species === 'truckSemi' ? 'truck' : 'car';
}

/** How a vehicle crushes: a car, a truck's cab, or a motorbike. */
function buildOf(v: FleetVehicle): Build {
  return v.bike !== undefined ? 'moto' : v.car !== undefined && isTruck(v.car.kind) ? 'truck' : 'car';
}

/** A vehicle's wreck: its model, ready to be crushed. Its wheels turn on their own and are left out. */
function wreckOf(v: FleetVehicle): Wreck {
  if (v.wreck !== undefined) return v.wreck;
  const skip = new Set<Object3D>();
  if (v.car !== undefined) {
    for (const wheel of [...v.car.wheels, ...(v.car.trailer?.wheels ?? [])]) skip.add(wheel.pivot);
    v.wreck = new Wreck(v.car.root, buildOf(v), skip);
  } else {
    const bike = v.bike!;
    skip.add(bike.frontWheel).add(bike.rearWheel);
    v.wreck = new Wreck(bike.root, 'moto', skip);
  }
  return v.wreck;
}

/**
 * A hit on the vehicle being driven: crush it (a knock under 10 km/h does
 * nothing), keep the damage with it, and throw what broke off on along the
 * way it was going, as a car stops under its loose parts.
 */
function crash(v: FleetVehicle, impact: Impact, velocity: { x: number; z: number }): void {
  const build = buildOf(v);
  const crush = crushOf(impact, build, Math.floor(Math.random() * 2 ** 31));
  if (crush === undefined) return;
  v.damage ??= noDamage();
  addCrush(v.damage, crush, impact.zone, impact.scrape, build);
  for (const piece of wreckOf(v).hit(crush, impact.zone, impact.scrape)) {
    const keep = 0.35 + Math.random() * 0.45;
    const r = () => Math.random() - 0.5;
    debris.add(piece,
      [velocity.x * keep + r() * 2.5, 0.8 + Math.random() * 2.5 + impact.dv * 0.05, velocity.z * keep + r() * 2.5],
      [r() * 14, r() * 10, r() * 14]);
  }
  bendWheels(v);
  if (v === fleet.driven) player.handling = v.handling ?? AS_NEW;
}

/** Where a vehicle's wheels are, for what a crash does to them. */
function wheelSpots(v: FleetVehicle): WheelSpot[] {
  if (v.car !== undefined) return v.car.wheels.map(w => ({ x: w.x, y: v.car!.wheelRadius, z: w.z, front: w.front }));
  const g = specOf(v).geometry;
  return [{ x: 0, y: g.frontRadius, z: g.frontAxleZ, front: true }, { x: 0, y: g.rearRadius, z: g.rearAxleZ, front: false }];
}

/**
 * Knock a vehicle's wheels out of line as its crushes have, and work out how
 * it drives now: pushed back or in, toed out, dropped (see
 * `core/vehicle/handling.ts`).
 */
function bendWheels(v: FleetVehicle): void {
  const spots = wheelSpots(v);
  v.handling = handlingOf(v.damage, buildOf(v), spots);
  if (v.car === undefined || v.damage === undefined) return;
  wheelBends(v.damage.crushes, spots).forEach((b, i) => {
    const wheel = v.car!.wheels[i];
    wheel.pivot.position.x = wheel.x + b.x;
    wheel.pivot.position.z = wheel.z + b.z;
    wheel.toe = b.toe;
    wheel.drop = b.y;
    // Standing, not driven, nothing else turns it.
    wheel.pivot.rotation.y = b.toe;
  });
}

/**
 * The other side of a hit: a parked car, a car in traffic or one left
 * standing, given its share of the hit by its weight. It becomes a full
 * model if it was not one (a parked car taken from the props, a traffic car
 * taken out of the traffic, its driver on the brakes), is shoved and spun,
 * crushes where it was struck, and slides to a stop.
 */
function strike(other: Struck, playerX: number, playerZ: number, velocity: { x: number; z: number }): void {
  const ref = other.body.ref as { kind: 'traffic'; id: number; police?: boolean } | { kind: 'fleet'; vehicle: FleetVehicle } | { kind: 'parked'; parked: ParkedRef }
    | { kind: 'prop'; species: Species; x: number; z: number };
  if (ref.kind === 'prop') {
    if (ref.species === 'shelter') {
      shatterShelter(ref.x, ref.z, other.push, other.dv, { x: playerX + other.contact.x, z: playerZ + other.contact.z }, velocity);
    } else if (other.dv > 0.3) {
      knockProp(ref.species, ref.x, ref.z, other.push, other.dv);
    }
    return;
  }
  let v: FleetVehicle | undefined;
  let before = { x: other.body.vx, z: other.body.vz };
  if (ref.kind === 'fleet') {
    v = ref.vehicle;
    if (!fleet.vehicles.includes(v)) return;
  } else if (ref.kind === 'traffic') {
    // A nudge it drives on from; a real knock and the driver stops. A
    // police car drives on from a harder one: after whoever gave it.
    if (other.dv < (ref.police === true ? POLICE_KNOCK : 0.8)) return;
    const car = traffic.release(ref.id);
    if (car === undefined) return;
    v = wreckFromTraffic(car);
    before = { x: car.vx, z: car.vz };
  } else {
    if (other.dv < 0.5) return;
    v = takeParkedAsFleet(ref.parked);
  }
  const contact = { x: playerX + other.contact.x, z: playerZ + other.contact.z };
  hitBy(v, other.push, other.dv, other.impulse, other.crush, contact, other.width, other.height, before);
}

/**
 * A prop knocked free of its fixings: taken out of the tile, and thrown as a
 * piece of its own (its own model, as it stood), off along the hit with its
 * share of it, tumbling, to come down and lie where it lands.
 */
function knockProp(species: Species, x: number, z: number, push: { x: number; z: number }, dv: number): void {
  const p = worldRenderer.propsNear(x, z, 0.8, new Set([species]))[0];
  if (p === undefined) return;
  const piece = pieceOf(p.mesh.geometry.clone(), instanceWorld(p), p.mesh.material as Material);
  worldRenderer.takeProp(p);
  const { velocity, spin } = thrown(push, dv, piece.half[1], Math.random());
  debris.add(piece, velocity, spin);
}

/** Where a prop instance stands in the scene, as a matrix. */
function instanceWorld(p: PropRef): Matrix4 {
  p.mesh.updateMatrixWorld();
  p.mesh.getMatrixAt(p.index, propMatrix);
  return propMatrix.premultiply(p.mesh.matrixWorld);
}

/**
 * A piece of a prop on its own: `geometry` (in the prop's frame, the
 * piece's to keep) posed by the prop's `world` matrix, sized as it stood
 * and about its middle, as debris needs it.
 */
function pieceOf(geometry: BufferGeometry, world: Matrix4, material: Material): BrokenPiece {
  world.decompose(propAt, propTurn, propScale);
  geometry.scale(propScale.x, propScale.y, propScale.z);
  geometry.computeBoundingBox();
  const box = geometry.boundingBox!, middle = box.getCenter(new Vector3()), size = box.getSize(new Vector3());
  geometry.translate(-middle.x, -middle.y, -middle.z);
  const mesh = new Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.position.copy(middle.applyQuaternion(propTurn).add(propAt));
  mesh.quaternion.copy(propTurn);
  return { mesh, half: [Math.max(0.03, size.x / 2), Math.max(0.03, size.y / 2), Math.max(0.03, size.z / 2)] };
}

/**
 * A vehicle through a bus shelter: every pane of toughened glass bursts into
 * crumbs, carried along with the vehicle near where it struck and falling
 * where they were further off (`burst`). Struck hard (the shelter's own
 * change in speed `dv` past `SHELTER_FRAME`), its frame comes apart, roof,
 * posts, seat and sign thrown as pieces; softer, it stands, bent over, its
 * glass gone. `contact` is where it was struck, `vehicle` how fast the
 * vehicle came (scene x, z).
 */
function shatterShelter(x: number, z: number, push: { x: number; z: number }, dv: number,
  contact: { x: number; z: number }, vehicle: { x: number; z: number }): void {
  const p = worldRenderer.propsNear(x, z, 2.2, SHELTERS)[0];
  if (p === undefined) return;
  const world = instanceWorld(p).clone();
  const material = p.mesh.material as Material;
  // Its stop's name board, hung from the roof's front edge, goes with it (taken first: the shelter gone folds it away).
  const board = worldRenderer.takeStopBoard(p);
  worldRenderer.takeProp(p, 2.2);
  const breaks = dv >= SHELTER_FRAME;
  // Left standing, bent over about its foot toward the push.
  const len = Math.hypot(push.x, push.z) || 1;
  const foot = new Vector3().setFromMatrixPosition(world);
  const bent = new Matrix4().makeTranslation(foot.x, foot.y, foot.z)
    .multiply(new Matrix4().makeRotationAxis(new Vector3(push.z / len, 0, -push.x / len), breaks ? 0 : Math.min(0.15, dv * 0.04)))
    .multiply(new Matrix4().makeTranslation(-foot.x, -foot.y, -foot.z));
  const point = new Vector3();
  const throwPiece = (piece: BrokenPiece) => {
    // Thrown the harder the nearer the hit it was.
    const near = Math.max(0.2, 1 - Math.hypot(piece.mesh.position.x - contact.x, piece.mesh.position.z - contact.z) / 3);
    const { velocity, spin } = thrown(push, dv * near * (0.6 + Math.random() * 0.5), piece.half[1], Math.random());
    debris.add(piece, velocity, spin);
  };
  if (board !== undefined) {
    const piece = boardPiece(board.corners, board.uvs, board.material);
    if (breaks) throwPiece(piece);
    else {
      // Still on the bent frame.
      piece.mesh.updateMatrix();
      piece.mesh.matrix.premultiply(bent).decompose(piece.mesh.position, piece.mesh.quaternion, propScale);
      debris.add(piece, [0, 0, 0], [0, 0, 0], true);
    }
  }
  for (const part of shelterParts()) {
    if (part.glass) {
      // The pane's crumbs, forty a square metre (each a handful of the real ones), from all over it.
      part.geometry.computeBoundingBox();
      const b = part.geometry.boundingBox!, size = b.getSize(new Vector3());
      const area = Math.max(size.x, size.z) * size.y;
      for (let i = Math.round(area * 40); i > 0; i--) {
        point.set(b.min.x + Math.random() * size.x, b.min.y + Math.random() * size.y, b.min.z + Math.random() * size.z).applyMatrix4(world);
        const v = burst(Math.hypot(point.x - contact.x, point.z - contact.z), vehicle, push, [Math.random(), Math.random(), Math.random()]);
        shards.add(point.x, point.y, point.z, v, 0.05 + Math.random() * 0.06, 3 + Math.random() * 12);
      }
      part.geometry.dispose();
      continue;
    }
    const piece = pieceOf(part.geometry, breaks ? world.clone() : world.clone().premultiply(bent), material);
    if (!breaks) {
      debris.add(piece, [0, 0, 0], [0, 0, 0], true);
      continue;
    }
    throwPiece(piece);
  }
}

/**
 * A name board as a piece of its own: a thin panel through its four
 * `corners` (scene metres: lower left, lower right, upper right, upper left,
 * looking at it), showing its patch of the signs' atlas from both sides.
 */
function boardPiece(corners: Vector3[], uvs: number[], material: Material): BrokenPiece {
  const [a, b, c, d] = corners;
  const right = b.clone().sub(a), up = d.clone().sub(a);
  const width = right.length(), height = up.length();
  right.normalize();
  up.normalize();
  // Right × up points out of its face, toward whoever reads it.
  const out = new Vector3().crossVectors(right, up);
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array([
    -width / 2, -height / 2, 0, width / 2, -height / 2, 0, width / 2, height / 2, 0, -width / 2, height / 2, 0,
  ]), 3));
  geometry.setAttribute('uv', new BufferAttribute(new Float32Array(uvs), 2));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  const look = (material as MeshBasicMaterial).clone();
  look.side = DoubleSide;
  const mesh = new Mesh(geometry, look);
  mesh.position.copy(a).add(b).add(c).add(d).multiplyScalar(0.25);
  mesh.quaternion.setFromRotationMatrix(new Matrix4().makeBasis(right, up, out));
  return { mesh, half: [width / 2, height / 2, 0.02] };
}

/** A prop that held: bent over, or knocked crooked, by how near it came to giving. */
function bendHeldProp(species: Species, x: number, z: number, push: { x: number; z: number }, closing: number): void {
  const behaviour = propBehaviour(species);
  if (behaviour === undefined) return;
  const angle = bendFor(behaviour, closing);
  if (angle < 0.02) return;
  const p = worldRenderer.propsNear(x, z, 0.8, new Set([species]))[0];
  if (p !== undefined) worldRenderer.bendProp(p, push, angle);
}

/**
 * The hedges and bushes under the vehicle being driven, at scene (x, z)
 * facing `yaw`: flattened as it pushes through, and dragging at it while it
 * does, less once they are down. Returns the drag, m/s².
 */
function plants(x: number, z: number, yaw: number, species: VehicleSpecies): number {
  const size = VEHICLE_SIZE[species];
  const dx = -Math.sin(yaw), dz = -Math.cos(yaw);
  const body: Footprint = { x, z, dx, dz, halfLength: size.halfLength, halfWidth: size.halfWidth };
  let drag = 0;
  for (const p of worldRenderer.propsNear(x, z, size.halfLength + 2.6, PLANTS)) {
    const soft = SOFT[p.species];
    p.mesh.getMatrixAt(p.index, propMatrix);
    const e = propMatrix.elements, scale = Math.hypot(e[0], e[2]) || 1;
    // A hedge is a piece 4 m long along its own x; a bush round.
    const touching = p.species === 'hedge'
      ? overlap(body, { x: p.x, z: p.z, dx: e[0] / scale, dz: e[2] / scale, halfLength: 2.05 * scale, halfWidth: soft.radius }) !== undefined
      : clearance(body, p.x, p.z) < soft.radius * scale;
    if (!touching) continue;
    const key = `${p.chunk}/${p.species}/${p.index}`;
    if (!flattened.has(key)) {
      flattened.add(key);
      worldRenderer.flattenProp(p, { x: dx, z: dz });
      drag = Math.max(drag, soft.drag);
    } else {
      drag = Math.max(drag, soft.drag * 0.3);
    }
  }
  return drag;
}

const propMatrix = new Matrix4();
const propAt = new Vector3();
const propTurn = new Quaternion();
const propScale = new Vector3();

/** A parked vehicle taken from the props as a full model, where it stood: to be hit, or got into. */
function takeParkedAsFleet(p: ParkedRef): FleetVehicle {
  worldRenderer.takeParked(p);
  const geo = world.toGeo(p.x, -p.z);
  const v = fleet.add(p.species as Parameters<typeof fleet.add>[0], p.paint, geo.lat, geo.lon, p.y, p.yaw);
  v.pitch = p.pitch;
  v.roll = p.roll;
  return v;
}

/** A vehicle taken out of the traffic as a full model, still moving, its driver on the brakes; a truck's trailer swung as it was. */
function wreckFromTraffic(r: Released): FleetVehicle {
  const geo = world.toGeo(r.x, -r.z);
  const v = fleet.add(r.species, r.paint, geo.lat, geo.lon, r.y, r.yaw, r.police !== undefined);
  if (v.police !== undefined && r.police?.responding === true) v.police.siren = true;
  const trailer = v.car?.trailer;
  if (trailer !== undefined && r.trailerYaw !== undefined) trailer.root.rotation.y = Math.atan2(Math.sin(r.trailerYaw - r.yaw), Math.cos(r.trailerYaw - r.yaw));
  v.motion = { vx: r.vx, vz: r.vz, spin: 0, drag: BRAKING_DRAG };
  v.fromTraffic = 0;
  return v;
}

/**
 * Wrecks made from traffic do not stand for ever. One that can still drive
 * drives off after a minute, once the player is not close by; one that
 * cannot is cleared away once the player is far off. Either way, out of
 * the player's own reach: one they have got into is theirs.
 */
function clearWrecks(dt: number, px: number, pz: number): void {
  for (const v of [...fleet.vehicles]) {
    if (v.fromTraffic === undefined || v === fleet.driven || v.chaser !== undefined) continue;
    if (v.motion === undefined) v.fromTraffic += dt;
    const at = world.toLocal(v.lat, v.lon);
    const away = Math.hypot(at.east - px, -at.north - pz);
    const drivable = v.handling?.dead === undefined;
    if ((drivable && v.fromTraffic > 60 && away > 60) || away > 400) fleet.remove(v);
  }
}

/** How high a vehicle strikes another, metres: a car's bumper, a lorry's, a motorbike's front, a scooter's stem. */
function strikeHeight(v: FleetVehicle): number {
  const build = buildOf(v);
  return build === 'truck' ? 0.85 : build === 'moto' ? specOf(v).strikeHeight : 0.5;
}

/**
 * Two vehicles run into each other (`a` into `b` along unit `n`, at scene
 * `contact`), going `va` and `vb`: the hit shared by weight, the crushing by
 * the energy each structure takes (see `core/vehicle/collision.ts`), and
 * each one shoved, spun and crushed where they met.
 */
function collideVehicles(a: FleetVehicle, b: FleetVehicle, n: { x: number; z: number }, contact: { x: number; z: number },
  va: { x: number; z: number }, vb: { x: number; z: number }): void {
  const ma = massOf(a.species), mb = massOf(b.species);
  const closing = (va.x - vb.x) * n.x + (va.z - vb.z) * n.z;
  const { dv1, dv2, impulse } = exchange(ma, mb, closing);
  if (impulse <= 0) return;
  const crush = crushSpeeds(ma, mb, closing, STIFFNESS[buildOf(a)], STIFFNESS[buildOf(b)]);
  const width = (v: FleetVehicle) => VEHICLE_SIZE[v.species].halfWidth * 2;
  hitBy(a, { x: -n.x, z: -n.z }, dv1, impulse, crush.first, contact, width(b), strikeHeight(b), va);
  hitBy(b, n, dv2, impulse, crush.second, contact, width(a), strikeHeight(a), vb);
}

/**
 * One side of a hit: `v`, going `before`, shoved `dv` m/s along unit `push`
 * by `impulse` N·s at scene `contact`, spun by it if that is off its
 * middle, and crushed there as if into a wall at `crushSpeed`, by something
 * `width` wide striking at `height`. A motorbike standing on its own falls
 * over, away from the blow.
 */
function hitBy(v: FleetVehicle, push: { x: number; z: number }, dv: number, impulse: number, crushSpeed: number,
  contact: { x: number; z: number }, width: number, height: number, before: { x: number; z: number }): void {
  const at = world.toLocal(v.lat, v.lon);
  const size = VEHICLE_SIZE[v.species];
  const motion = v.motion ?? { vx: before.x, vz: before.z, spin: 0, drag: PARKED_DRAG };
  motion.vx += push.x * dv;
  motion.vz += push.z * dv;
  const arm = { x: contact.x - at.east, z: contact.z + at.north };
  // A whole articulated rig turns about its middle, a long way back.
  const halfLength = v.car?.trailer !== undefined ? SEMI_REACH / 2 + size.halfLength / 2 : size.halfLength;
  motion.spin += spinFrom(massOf(v.species), halfLength, size.halfWidth, arm, { x: push.x * impulse, z: push.z * impulse });
  v.motion = motion;
  if (v.type === 'moto' && v !== fleet.driven && v.fall === undefined && dv > 0.6) knockOver(v, push);
  const forward = { x: -Math.sin(v.yaw), z: -Math.cos(v.yaw) };
  const after = { x: before.x + push.x * dv, z: before.z + push.z * dv };
  const impact = impactOf(before, after, push, arm, forward, height, width);
  // Crushed by its share of the energy, by weight and stiffness: see `crushSpeeds`.
  impact.dv = crushSpeed;
  crash(v, impact, before);
}

/**
 * Someone running at (px, pz) going (vx, vz) into a motorbike standing in
 * reach knocks it over: one left standing, or a parked one, taken from the
 * props to fall.
 */
function shoulderBikes(px: number, pz: number, vx: number, vz: number): void {
  const speed = Math.hypot(vx, vz), ux = vx / speed, uz = vz / speed;
  const toward = (cx: number, cz: number) => {
    const d = Math.hypot(cx - px, cz - pz) || 1;
    return ((cx - px) * ux + (cz - pz) * uz) / d > 0.5;
  };
  for (const v of fleet.vehicles) {
    if (v.type !== 'moto' || v === fleet.driven || v.fall !== undefined) continue;
    const at = world.toLocal(v.lat, v.lon);
    if (Math.hypot(at.east - px, -at.north - pz) < 1.2 && toward(at.east, -at.north)) knockOver(v, { x: ux * 0.6, z: uz * 0.6 });
  }
  const p = worldRenderer.nearestParked(px, pz, 1.3, (cx, cz, _yaw, species) => isTwoWheeler(species) && toward(cx, cz));
  if (p !== undefined) knockOver(takeParkedAsFleet(p), { x: ux * 0.6, z: uz * 0.6 });
}

/** A motorbike knocked over: it falls away from the push, to its right or its left. */
function knockOver(v: FleetVehicle, push: { x: number; z: number }): void {
  // Right of its heading (−sin, −cos) is (cos, −sin).
  const right = push.x * Math.cos(v.yaw) - push.z * Math.sin(v.yaw);
  v.fall = { angle: 0.05, rate: 1 + Math.min(3, Math.hypot(push.x, push.z) * 2), side: right >= 0 ? 1 : -1 };
}

// --- thrown off -------------------------------------------------------------------
// A motorbike crash over 20 km/h head-on throws the rider over the bars: the
// body tumbles as a ragdoll (core/character/ragdoll.ts) from the pose it was
// riding in, lies a moment where it came to rest, and gets up, while the
// bike falls on its side where it stopped. E beside it picks it up again.

/** How hard a motorbike on its side scrubs along the road, m/s²: metal and plastic on tarmac. */
const BIKE_SLIDING = 4.5;
interface Downed {
  fallen: Fallen;
  /** The bike thrown from: not something for the body to fall against, starting astride it. */
  bike: FleetVehicle | undefined;
  /** A place, and where it was in the scene: the body moves with the scene's origin when that moves. */
  anchor: { lat: number; lon: number; x: number; z: number };
  /**
   * For measuring (`__wsg.downed`, `tools/throwdrive.mjs`): where it went
   * down, and how far on it first struck the ground, metres.
   */
  from: { x: number; z: number };
  landed?: number;
  /** How it went down, for other players to see it go so too: see `net/protocol.ts`. */
  fall: FallStart;
}
let downed: Downed | undefined;

/** The ground under scene (x, z) near height `y`, for a body: the terrain, a built surface, the dirt of a jump line. */
const bodyGround: RagdollGround = (x, z, y) => {
  const ground = sceneGround(x, z, y);
  const geo = world.toGeo(x, -z);
  const dirt = jumps.heightAt(geo.lat, geo.lon);
  return dirt !== undefined && (ground === undefined || dirt > ground) ? dirt : ground;
};

/**
 * A body against walls, posts, trunks, the vehicles left standing, the
 * traffic and the vehicle being driven, but not `skip`, the bike it came off.
 */
function bodyAgainst(skip: FleetVehicle | undefined): RagdollCollide {
  return (x, y, z, radius) => {
    const feet = y - radius, head = y + radius;
    const wall = worldRenderer.collide(x, z, radius, feet, head, throughPlants);
    const x0 = wall?.x ?? x, z0 = wall?.z ?? z;
    const car = fleet.collide(x0, z0, radius, feet, head, (la, lo) => world.toLocal(la, lo), skip);
    const x1 = car?.x ?? x0, z1 = car?.z ?? z0;
    const moving = traffic.collide(x1, z1, radius, feet, head);
    const x2 = moving?.x ?? x1, z2 = moving?.z ?? z1;
    const driven = drivenFootprint();
    const inCar = driven !== undefined && feet < driven.top && head > driven.y ? touching(driven, x2, z2, radius) : undefined;
    if (inCar !== undefined) return { x: inCar.x, z: inCar.z };
    return moving ?? car ?? wall;
  };
}
const bodyCollide: RagdollCollide = (x, y, z, radius) => bodyAgainst(downed?.bike)(x, y, z, radius);
const bodies: BodyWorld = { ground: bodyGround, collide: bodyCollide };
/** The world as anyone else's body meets it: villagers knocked down, other players fallen. */
const othersBodies: BodyWorld = { ground: bodyGround, collide: bodyAgainst(undefined) };
villagers.setBodies(othersBodies);

/** The change in speed, m/s (about 15 km/h), a police car in the traffic shrugs off and drives on from: after whoever hit it. */
const POLICE_KNOCK = 4;

/** How high each build's front strikes a person, metres: a bumper, a lorry's, a bike's front wheel. */
const BUMPER: Record<Build, number> = { car: 0.5, truck: 0.75, moto: 0.55 };

/**
 * People in the way of the vehicle being driven, and of a wreck sliding:
 * the villagers it strikes are knocked down and it feels each one (a car
 * hardly, a motorbike enough to throw its rider); the player on foot, in a
 * sliding wreck's way, is knocked down too.
 */
function strikePeople(): void {
  const driven = fleet.driven;
  if (driven !== undefined && !player.isDown && boarding === undefined) {
    const box = drivenFootprint(), speed = player.forwardSpeed;
    if (box !== undefined && Math.abs(speed) > KNOCKED_DOWN) {
      const s: Striker = { ...box, vx: box.dx * speed, vz: box.dz * speed, mass: player.vehicleMass };
      for (const hit of [...villagers.strike(s, box.y, BUMPER[buildOf(driven)]), ...strikeOfficers(s, box.y, BUMPER[buildOf(driven)])]) {
        player.bump(hit.vehicle);
        offences.push({ offence: 'person', seen: true });
      }
    }
  }
  const me = player.state, at = world.toLocal(me.lat, me.lon);
  for (const v of fleet.vehicles) {
    const m = v.motion ?? v.chaser;
    if (m === undefined || v === fleet.driven || Math.hypot(m.vx, m.vz) < KNOCKED_DOWN) continue;
    const root = fleet.root(v), size = VEHICLE_SIZE[v.species];
    const s: Striker = {
      x: root.position.x, z: root.position.z, dx: -Math.sin(v.yaw), dz: -Math.cos(v.yaw),
      halfLength: size.halfLength, halfWidth: size.halfWidth, vx: m.vx, vz: m.vz, mass: massOf(v.species),
    };
    const bumper = BUMPER[buildOf(v)];
    for (const hit of villagers.strike(s, v.y, bumper)) { m.vx += hit.vehicle.x; m.vz += hit.vehicle.z; }
    if (me.mode !== 'walk' || player.isDown || Math.abs(player.avatarPose.feet - v.y) > 1.5) continue;
    const hit = strikePerson(s, at.east, -at.north);
    if (hit !== undefined && hit.closing >= KNOCKED_DOWN && player.knockDown()) {
      knockDownPlayer({ kind: 'struck', v: [hit.person.x, 0, hit.person.z], bumper });
    }
  }
}

/** The vehicle being driven, as a box on the ground: where it is, its base and its top. */
function drivenFootprint(): (Footprint & { y: number; top: number }) | undefined {
  const v = fleet.driven;
  if (v === undefined) return undefined;
  const root = fleet.root(v);
  const size = VEHICLE_SIZE[v.species];
  // A model's forward is -Z turned by its yaw.
  const yaw = v.type === 'moto' ? player.motoPose.yaw : player.vehiclePose.yaw;
  return {
    x: root.position.x, z: root.position.z, dx: -Math.sin(yaw), dz: -Math.cos(yaw),
    halfLength: size.halfLength, halfWidth: size.halfWidth, y: root.position.y, top: root.position.y + size.height,
  };
}

/**
 * The player down, as a body from the pose last drawn, set going as `fall`
 * says. Off a bike, `bike` is let go of and not fallen against, and the
 * helmet stays on.
 */
function knockDownPlayer(fall: FallStart, bike?: FleetVehicle): void {
  const fallen = new Fallen(crowd, playerSlot, bike !== undefined);
  startFall(fallen.body, fall);
  const s = player.state;
  const at = world.toLocal(s.lat, s.lon);
  const [x, , z] = fallen.at;
  downed = { fallen, bike, anchor: { lat: s.lat, lon: s.lon, x: at.east, z: -at.north }, from: { x, z }, fall };
  if (trip !== undefined) endTrip(fall.kind === 'fell' ? 'You fell' : fall.kind === 'struck' ? 'You were knocked down' : 'You came off');
  note = { text: fall.kind === 'fell' ? 'Fell' : fall.kind === 'struck' ? 'Knocked down' : 'Thrown off', until: performance.now() + 1500 };
  // Hitting the ground from a height: the body's heavy thud.
  if (fall.kind === 'fell') effects.body(Math.hypot(...fall.v), { x, y: fallen.at[1], z });
}

/**
 * The rider thrown off: the body sent over the bars, or off the back; the
 * bike let go, falling on its side, sliding on with what speed the crash
 * left it. Returns the bike, for this frame's hits to crush.
 */
function throwRider(t: Throw): FleetVehicle | undefined {
  const v = fleet.driven?.type === 'moto' ? fleet.driven : undefined;
  const s = player.state;
  if (v !== undefined) {
    leaveHere(v, s.lat, s.lon, sampleGroundNow(s.lat, s.lon) ?? v.y, t.yaw);
    v.fall = { angle: Math.max(0.05, Math.abs(t.lean)), rate: 1.5, side: t.lean >= 0 ? 1 : -1 };
    v.motion = { vx: t.after.x, vz: t.after.z, spin: 0, drag: BIKE_SLIDING };
  }
  knockDownPlayer({ kind: t.backward ? 'back' : 'bars', v: [t.velocity.x, t.velocity.y, t.velocity.z], forward: [t.forward.x, t.forward.z] }, v);
  return v;
}

/** Thrown off, knocked down or fallen: the body tumbling to rest, a moment lying there, then getting up. */
function stepDowned(dt: number): void {
  const d = downed;
  if (d === undefined) return;
  // Taken somewhere else while down: nothing to get up from.
  if (!player.isDown) {
    downed = undefined;
    return;
  }
  const here = world.toLocal(d.anchor.lat, d.anchor.lon);
  const dx = here.east - d.anchor.x, dz = -here.north - d.anchor.z;
  d.fallen.shift(dx, dz);
  d.from.x += dx; d.from.z += dz;
  d.anchor.x = here.east; d.anchor.z = -here.north;
  const fallen = d.fallen, body = fallen.body;
  fallen.step(dt, bodies, clock);
  if (d.landed === undefined && body.age > 0.2 && body.pressed.some(k => k > 0)) {
    const [hx, , hz] = fallen.at;
    d.landed = Math.hypot(hx - d.from.x, hz - d.from.z);
  }
  const up = fallen.stood;
  if (up !== undefined) {
    const geo = world.toGeo(up.x, -up.z);
    player.standUp(geo.lat, geo.lon, up.y, up.yaw);
    downed = undefined;
    return;
  }
  // The camera follows the body: over it lying, with it flying.
  const [x, y, z] = fallen.at;
  const geo = world.toGeo(x, -z);
  player.followBody(geo.lat, geo.lon, Math.max(bodyGround(x, z, y) ?? y - 0.9, y - 0.9));
}

/**
 * Vehicles shoved by a hit slide on, spinning, until their tyres stop them:
 * a parked one braked and in gear, a traffic car with its driver on the
 * brakes. A wall stops them where it stands.
 */
function slideFleet(dt: number): void {
  for (const v of fleet.vehicles) {
    const m = v.motion;
    if (m === undefined || v === fleet.driven) continue;
    const step = slide(m, dt, m.drag);
    const at = world.toLocal(v.lat, v.lon);
    let x = at.east + step.dx, z = -at.north + step.dz;
    const size = VEHICLE_SIZE[v.species];
    const wall = worldRenderer.collide(x, z, size.halfWidth, v.y + 0.3, v.y + 1.5);
    if (wall !== undefined) {
      x = wall.x; z = wall.z;
      // What was going into the wall stops there.
      const into = m.vx * wall.nx + m.vz * wall.nz;
      if (into < 0) { m.vx -= wall.nx * into; m.vz -= wall.nz * into; }
    }
    const geo = world.toGeo(x, -z);
    v.lat = geo.lat;
    v.lon = geo.lon;
    // Spin positive turns +x toward +z, which is a clockwise yaw from above.
    v.yaw -= step.turn;
    v.y = sampleGroundAt(geo.lat, geo.lon, v.y) ?? v.y;
    if (stopped(m)) v.motion = undefined;
  }
  // Motorbikes knocked over go on falling till they lie on their side.
  for (const v of fleet.vehicles) if (v.fall !== undefined && v !== fleet.driven) fall(v.fall, dt);
}

/**
 * The vehicles left standing, wrecks among them, as boxes on the ground:
 * each one's body, and a left truck's trailer as it was left swung. What
 * traffic must not drive into.
 */
function fleetFootprints(owners: FleetVehicle[]): Array<Footprint & { species: VehicleSpecies }> {
  const out: Array<Footprint & { species: VehicleSpecies }> = [];
  owners.length = 0;
  for (const v of fleet.vehicles) {
    if (v === fleet.driven) continue;
    const at = world.toLocal(v.lat, v.lon), size = VEHICLE_SIZE[v.species];
    const dx = -Math.sin(v.yaw), dz = -Math.cos(v.yaw);
    out.push({ x: at.east, z: -at.north, dx, dz, halfLength: size.halfLength, halfWidth: size.halfWidth, species: v.species });
    owners.push(v);
    const t = v.car?.trailer;
    if (t === undefined) continue;
    owners.push(v);
    // Back from the kingpin along the trailer's own heading.
    const yaw = v.yaw + t.root.rotation.y, tx = Math.sin(yaw), tz = Math.cos(yaw);
    const mid = (t.length - 1.2) / 2;
    out.push({ x: at.east - dx * t.kingpinZ + tx * mid, z: -at.north - dz * t.kingpinZ + tz * mid, dx: -tx, dz: -tz, halfLength: (t.length + 1.2) / 2, halfWidth: t.width / 2, species: v.species });
  }
  return out;
}

/** The ground under a point near `y`: a built surface within a step of it, or the terrain. */
function sampleGroundAt(lat: number, lon: number, y: number): number | undefined {
  const terrain = world.heightAt(lat, lon);
  if (terrain === undefined) return undefined;
  const at = world.toLocal(lat, lon);
  return worldRenderer.ground(at.east, -at.north, terrain, y + STEP_HEIGHT);
}

/** Make `v` the vehicle being driven: the beam in it, the rides reset, its powertrain the one that drives. */
function drive(v: FleetVehicle): void {
  fleet.driven = v;
  // A truck drives as a truck: its own chassis, and the camera back by the whole rig.
  player.chassis = v.car === undefined ? CAR_CHASSIS : chassisOf(v.car.kind);
  const trailer = v.car?.trailer;
  player.rigLength = trailer === undefined ? (v.car === undefined ? VEHICLE.length : player.chassis.length)
    : player.chassis.length / 2 + trailer.kingpinZ + trailer.length;
  player.powertrain = v.car === undefined ? undefined : {
    kind: v.tank.energy === 'electric' ? 'electric' : v.tank.energy === 'diesel' ? 'diesel' : 'petrol',
    weight: BODY_WEIGHT[v.car.kind],
  };
  if (v.car !== undefined) fleet.mountBeams(v, warmCar.headlights);
  else fleet.mountBeams(v, [warmBike.headlight]);
  if (v.bike?.kind === 'scooter') scooterPaint.color.setHex(v.paint);
  else if (v.bike !== undefined) motoPaint.color.setHex(v.paint);
  // A two-wheeler rides as itself: its own power, grip and springs, from rest.
  if (v.bike !== undefined) {
    player.twoWheeler = specOf(v);
    Object.assign(motoRide, createMotoRide(player.twoWheeler));
  }
  // How heavy it is, for sharing a hit, and how it drives, crashes and all.
  player.vehicleMass = massOf(v.species, v.type === 'moto');
  player.vehicleBuild = buildOf(v);
  if (v.handling === undefined) bendWheels(v);
  player.handling = v.handling ?? AS_NEW;
  v.motion = undefined;
  // Getting on a bike that lies on its side picks it up; got into, a wreck is the player's.
  v.fall = undefined;
  v.fromTraffic = undefined;
  resetRides();
}

/**
 * How far an articulated truck's trailer can swing round on its kingpin
 * before it would strike the cab, radians.
 */
const HITCH_LIMIT = 1.35;

/**
 * Pull the driven truck's trailer along behind its kingpin. The trailer's
 * axles stay where they were but for being drawn toward the kingpin, at
 * their own distance from it: so in a turn the trailer cuts inside the
 * tractor's path and straightens out after, and reversing, pushed rather
 * than pulled, it swings ever further round unless the tractor is steered
 * after it, toward a jackknife, as a real one does. Kept in latitude and
 * longitude, so a shift of the world's origin does not move it.
 */
type Trailer = NonNullable<NonNullable<FleetVehicle['car']>['trailer']>;

/** Where the trailer would be with the tractor at (x, z) facing `yaw`: its kingpin, axles, heading and swing. */
function trailerAt(v: FleetVehicle, trailer: Trailer, x: number, z: number, yaw: number) {
  const bx = Math.sin(yaw), bz = Math.cos(yaw);
  const kx = x + bx * trailer.kingpinZ, kz = z + bz * trailer.kingpinZ;
  let ax: number, az: number;
  if (v.trailerAxle === undefined) {
    // Straight behind, as it was found.
    ax = kx + bx * trailer.axleZ;
    az = kz + bz * trailer.axleZ;
  } else {
    const at = world.toLocal(v.trailerAxle.lat, v.trailerAxle.lon);
    ax = at.east;
    az = -at.north;
  }
  let dx = kx - ax, dz = kz - az;
  const d = Math.hypot(dx, dz) || 1;
  dx /= d; dz /= d;
  // Its heading, as the trailer's own nose points (to the kingpin), and its swing against the tractor's.
  let hitch = Math.atan2(Math.sin(Math.atan2(-dx, -dz) - yaw), Math.cos(Math.atan2(-dx, -dz) - yaw));
  if (Math.abs(hitch) > HITCH_LIMIT) {
    hitch = Math.sign(hitch) * HITCH_LIMIT;
    const t = yaw + hitch;
    dx = -Math.sin(t);
    dz = -Math.cos(t);
  }
  ax = kx - dx * trailer.axleZ;
  az = kz - dz * trailer.axleZ;
  return { kx, kz, ax, az, dx, dz, hitch };
}

function pullTrailer(v: FleetVehicle, trailer: Trailer, x: number, z: number, yaw: number): void {
  const { kx, kz, ax, az, dx, dz, hitch } = trailerAt(v, trailer, x, z, yaw);
  const geo = world.toGeo(ax, -az);
  v.trailerAxle = { lat: geo.lat, lon: geo.lon };
  trailer.root.rotation.y = hitch;
  // For the traffic: the trailer is something to stop for.
  const mid = (trailer.length - 1.2) / 2;
  traffic.setPlayerTrailer({ x: kx - dx * mid, z: kz - dz * mid, dx, dz, halfLength: (trailer.length + 1.2) / 2, halfWidth: trailer.width / 2 });
}

/**
 * How far the trailer, with the tractor at `place`, sinks into
 * buildings, posts and other vehicles: summed over circles down its length,
 * metres. The nose, over the tractor's chassis, is left to the tractor's own.
 */
function trailerOverlap(v: FleetVehicle, trailer: Trailer, place: { lat: number; lon: number; yaw: number }, altitude: number): number {
  const at = world.toLocal(place.lat, place.lon);
  const { kx, kz, dx, dz } = trailerAt(v, trailer, at.east, -at.north, place.yaw);
  const r = trailer.width / 2;
  let sum = 0;
  for (let along = 2.5; along <= trailer.length - 1.2 - r * 0.6; along += r * 1.5) {
    const x = kx - dx * along, z = kz - dz * along;
    const geo = world.toGeo(x, -z);
    const out = pushOut(geo.lat, geo.lon, r, altitude + 0.5, altitude + 4);
    if (out === undefined) continue;
    const o = world.toLocal(out.lat, out.lon);
    sum += Math.hypot(o.east - x, -o.north - z);
  }
  return sum;
}

const trailerAxlePoint = new Vector3();
/**
 * Rest the trailer's back on its own wheels. Its nose sits on the tractor's
 * fifth wheel and nods with it; its axles, thirteen metres back, are on the
 * ground there. Hung rigidly off the tractor, every nod of the cab over a
 * bump swung the trailer's tail up or down half a metre. So it is pitched on
 * the kingpin to bring its axles to the ground under them, eased as its
 * springs would, and no further than a trailer tips.
 */
function restTrailer(v: FleetVehicle, trailer: Trailer, ground: (lat: number, lon: number) => number | undefined, dt: number): void {
  const axle = v.trailerAxle;
  const under = axle === undefined ? undefined : ground(axle.lat, axle.lon);
  const root = trailer.root;
  root.rotation.order = 'YXZ';
  const was = root.rotation.x;
  root.rotation.x = 0;
  root.parent?.updateMatrixWorld(true);
  root.updateMatrixWorld(true);
  trailerAxlePoint.set(0, 0, trailer.axleZ).applyMatrix4(root.matrixWorld);
  // Rotating about x by a takes the axles (0, 0, L) to height −L sin a.
  const want = under === undefined ? 0 : Math.asin(Math.max(-0.2, Math.min(0.2, -(under - trailerAxlePoint.y) / trailer.axleZ)));
  root.rotation.x = was + (want - was) * (1 - Math.exp(-dt * 10));
  root.updateMatrixWorld(true);
}

/** Put the shared beams back in the hidden models, out of the scene's light count. */
function parkBeams(): void {
  for (const beam of warmCar.headlights) {
    beam.intensity = 0;
    warmCar.body.add(beam, beam.target);
  }
  warmBike.headlight.intensity = 0;
  warmBike.steer.add(warmBike.headlight, warmBike.headlight.target);
}

/** What E would get into from here: a left-behind vehicle, or a parked one. */
/**
 * What E would get into from here: a left-behind vehicle, or a parked one.
 * Only from the driver's side, so the walk to the door goes round the car
 * rather than through it. With `anySide`, whatever is nearest, for the hint to
 * send the player round.
 */
function vehicleInReach(anySide = false): { fleet?: FleetVehicle; parked?: ReturnType<typeof worldRenderer.nearestParked> } | undefined {
  const s = player.state;
  if (s.mode !== 'walk' || boarding !== undefined) return undefined;
  const at = world.toLocal(s.lat, s.lon);
  const px = at.east, pz = -at.north;
  const toLocal = (la: number, lo: number) => world.toLocal(la, lo);
  const own = fleet.nearest(px, pz, REACH, toLocal,
    anySide ? undefined : (cx, cz, v) => onDriverSide(px, pz, cx, cz, v.yaw, v.species));
  const parked = worldRenderer.nearestParked(px, pz, REACH,
    anySide ? undefined : (cx, cz, yaw, species) => onDriverSide(px, pz, cx, cz, yaw, species as VehicleSpecies));
  if (own === undefined && parked === undefined) return undefined;
  if (own !== undefined && parked !== undefined) {
    const o = world.toLocal(own.lat, own.lon);
    return Math.hypot(o.east - at.east, -o.north + at.north) <= Math.hypot(parked.x - at.east, parked.z + at.north) ? { fleet: own } : { parked };
  }
  return own !== undefined ? { fleet: own } : { parked };
}

function getIn(): void {
  const heli = heliInReach();
  if (heli !== undefined) {
    boardHeli(heli);
    return;
  }
  const found = vehicleInReach();
  if (found === undefined) return;
  let v = found.fleet;
  if (v === undefined && found.parked !== undefined) {
    const p = found.parked;
    worldRenderer.takeParked(p);
    const geo = world.toGeo(p.x, -p.z);
    v = fleet.add(p.species as Parameters<typeof fleet.add>[0], p.paint, geo.lat, geo.lon, p.y, p.yaw);
    v.pitch = p.pitch;
    v.roll = p.roll;
  }
  if (v === undefined) return;
  const avatar = player.avatarPose;
  const at = world.toLocal(player.state.lat, player.state.lon);
  // Where the walk to the door starts, and how long it takes at a stroll.
  fleet.place((la, lo) => world.toLocal(la, lo));
  const root = fleet.root(v);
  root.updateMatrixWorld(true);
  if (v.car !== undefined) doorAt.set(v.car.seat.outsideX, 0, v.car.seat.hipZ - 0.15);
  else doorAt.set(-0.55, 0, 0.1);
  doorAt.applyMatrix4(root.matrixWorld);
  const distance = Math.hypot(doorAt.x - at.east, doorAt.z + at.north);
  boarding = { vehicle: v, out: false, t: 0, walk: { x: at.east, y: avatar.feet, z: -at.north, time: Math.max(0.25, Math.min(1.8, distance / 1.5)) } };
  drive(v);
  const seat = v.car === undefined ? undefined
    : { side: v.car.seat.x, eye: carEyeHeight(v.car.kind), back: v.car.seat.hipZ };
  player.enterVehicle(v.type === 'car' ? 'drive' : 'moto', v.lat, v.lon, v.y, v.yaw, seat);
  player.setInputEnabled(false);
  void offerJob(v);
}

function getOut(): void {
  const v = fleet.driven;
  const s = player.state;
  if (v === undefined || boarding !== undefined || Math.abs(s.speed) > 1.5) return;
  const root = fleet.root(v);
  root.updateMatrixWorld(true);
  if (v.car !== undefined) doorAt.set(v.car.seat.outsideX, 0, v.car.seat.hipZ - 0.15);
  else doorAt.set(-0.55, 0, 0.1);
  doorAt.applyMatrix4(root.matrixWorld);
  const geo = world.toGeo(doorAt.x, -doorAt.z);
  const feet = sampleGroundNow(geo.lat, geo.lon) ?? v.y;
  if (v.car !== undefined) for (const beam of v.car.headlights) beam.intensity = 0;
  player.exitVehicle(geo.lat, geo.lon, feet, v.yaw);
  parkBeams();
  fleet.driven = undefined;
  boarding = { vehicle: v, out: true, t: 0 };
  player.setInputEnabled(false);
}

// --- helicopters ----------------------------------------------------------------
// E beside one on a pad walks to its door and climbs in; H, landed and all but
// stopped, climbs out. See `heliBoarding` for the moves.

/** How near the airframe the player must be to get in, metres. */
const HELI_REACH = 2.5;
let padTimer = 0;
/** Climbing in or out of a helicopter: like `Boarding`, for the helicopter. */
let heliBoard: { heli: FleetHeli; out: boolean; t: number; walk?: { x: number; y: number; z: number; time: number } } | undefined;
/** The pilot at the controls, posed in the seat. */
const pilotPose = drivingPose(HELI_SEAT);
/**
 * The rotor that is turning — the flown machine's, or the one just left as
 * it winds down — and how fast, 0..1 of flying speed.
 */
let rotor: { model: HelicopterModel; spin: number } | undefined;
/** Where to stand outside the pilot's door, in the airframe's frame. */
const heliDoor = new Vector3(HELI_SEAT.outsideX, 0, HELI_STEP.z + 0.05);

/** Terrain or a built surface — a roof's pad — at (lat, lon), within a step of `near`. */
function groundNear(lat: number, lon: number, near: number): number | undefined {
  const terrain = world.heightAt(lat, lon);
  if (terrain === undefined) return undefined;
  const at = world.toLocal(lat, lon);
  return worldRenderer.ground(at.east, -at.north, terrain, near + STEP_HEIGHT);
}

/**
 * Keep the helicopters in step with the player: one on every pad, and one
 * flown in the helicopter. Flying with none — H with `?vehicles=summon`, or
 * a spawn in the air with `?mode=heli` — conjures one; leaving it with H
 * there leaves it on the ground below.
 */
function syncHelis(dt: number): void {
  padTimer -= dt;
  if (padTimer <= 0) {
    padTimer = 0.5;
    heliFleet.sync(worldRenderer.helipads(), (east, north) => world.toGeo(east, north));
  }
  const s = player.state;
  if (s.mode === 'heli') {
    if (heliFleet.flown === undefined) {
      const h = heliFleet.add(s.lat, s.lon, s.altitude - HELI.skidHeight, (-s.heading * Math.PI) / 180);
      if (resumeVehicle?.species === 'heli') { h.tank = resumeVehicle.tank; resumeVehicle = undefined; }
      heliFleet.fly(h);
    }
    return;
  }
  const h = heliFleet.flown;
  if (h === undefined) return;
  h.y = groundNear(h.lat, h.lon, h.y) ?? h.y;
  heliFleet.leave();
}

function heliInReach(): FleetHeli | undefined {
  const s = player.state;
  if (s.mode !== 'walk' || boarding !== undefined || heliBoard !== undefined) return undefined;
  const at = world.toLocal(s.lat, s.lon);
  return heliFleet.nearest(at.east, -at.north, HELI_REACH, (la, lo) => world.toLocal(la, lo));
}

/** The spot outside `h`'s door, in scene coordinates. */
function heliDoorAt(h: FleetHeli): Vector3 {
  h.model.root.updateMatrixWorld(true);
  return doorAt.copy(heliDoor).applyMatrix4(h.model.root.matrixWorld);
}

function boardHeli(h: FleetHeli): void {
  const avatar = player.avatarPose;
  const at = world.toLocal(player.state.lat, player.state.lon);
  heliFleet.place((la, lo) => world.toLocal(la, lo));
  const door = heliDoorAt(h);
  const distance = Math.hypot(door.x - at.east, door.z + at.north);
  heliBoard = { heli: h, out: false, t: 0, walk: { x: at.east, y: avatar.feet, z: -at.north, time: Math.max(0.25, Math.min(2.2, distance / 1.5)) } };
  heliFleet.fly(h);
  player.enterHelicopter(h.lat, h.lon, h.y, h.yaw);
  player.setInputEnabled(false);
}

function leaveHeli(): void {
  const h = heliFleet.flown;
  if (h === undefined || heliBoard !== undefined || !player.helicopterParked) return;
  const door = heliDoorAt(h);
  const geo = world.toGeo(door.x, -door.z);
  const feet = groundNear(geo.lat, geo.lon, h.y) ?? h.y;
  // Facing the machine, as the climb down ends.
  player.exitHelicopter(geo.lat, geo.lon, feet, h.yaw - 1.35);
  heliFleet.leave();
  heliBoard = { heli: h, out: true, t: 0 };
  player.setInputEnabled(false);
}

/**
 * Pose the player climbing in or out of a helicopter, or at its controls.
 * Returns false when not in one, to be posed as usual.
 */
function posePilot(dt: number): boolean {
  const b = heliBoard;
  if (b !== undefined) {
    b.t += dt;
    const root = b.heli.model.root;
    root.updateMatrixWorld(true);
    const walk = b.walk?.time ?? 0;
    if (b.walk !== undefined && b.t < walk) {
      // The walk to the door, turning toward it.
      const door = heliDoorAt(b.heli);
      const k = b.t / walk;
      const dx = door.x - b.walk.x, dz = door.z - b.walk.z;
      const speed = Math.hypot(dx, dz) / walk;
      playerPhase = advancePhase(playerPhase, speed, dt);
      crowd.place(playerSlot, {
        x: b.walk.x + dx * k, y: b.walk.y + (door.y - b.walk.y) * k, z: b.walk.z + dz * k,
        yaw: Math.atan2(-dx, -dz), pose: gaitPose(speed, playerPhase, clock), visible: true,
      });
      return true;
    }
    const r = heliBoarding(HELI_SEAT, HELI_STEP, b.t - walk, b.out, boardPose);
    b.heli.model.door.rotation.y = -1.2 * r.door;
    b.heli.model.door.updateMatrixWorld(true);
    crowd.placeRider(playerSlot, root.matrixWorld, r.pose);
    if (r.done) {
      heliBoard = undefined;
      b.heli.model.door.rotation.y = 0;
      if (!worldMap.open) player.setInputEnabled(true);
    }
    return true;
  }
  const h = heliFleet.flown;
  if (h === undefined || player.state.mode !== 'heli') return false;
  // At the controls: seen through the glass from outside, hidden from the seat.
  if (player.thirdPerson) {
    h.model.root.updateMatrixWorld(true);
    crowd.placeRider(playerSlot, h.model.root.matrixWorld, pilotPose);
  } else {
    crowd.place(playerSlot, { x: 0, y: 0, z: 0, yaw: 0, pose: gaitPose(0, 0, 0), visible: false });
  }
  return true;
}

/**
 * Spin the flown machine's rotor up once its pilot is in, and a left one's
 * down: a few seconds either way, not all at once.
 */
function spinRotor(dt: number): void {
  const flying = player.state.mode === 'heli' && heliBoard === undefined;
  const flown = heliFleet.flown;
  if (flown !== undefined && rotor?.model !== flown.model) {
    rotor = { model: flown.model, spin: player.state.mode === 'heli' && heliBoard === undefined && !player.helicopterParked ? 1 : 0 };
  }
  if (rotor === undefined) return;
  const target = flying && rotor.model === flown?.model ? 1 : 0;
  rotor.spin = target > rotor.spin ? Math.min(1, rotor.spin + dt / 3) : Math.max(0, rotor.spin - dt / 5);
  rotor.model.mainRotor.rotation.y += HELI.rotorSpeed * rotor.spin * dt;
  rotor.model.tailRotor.rotation.x += HELI.rotorSpeed * 1.8 * rotor.spin * dt;
  rotor.model.disc.visible = rotor.spin > 0.6;
  if (rotor.spin === 0 && target === 0) rotor = undefined;
}

/** The ground at a point, as the frame samples it, for stepping out. */
function sampleGroundNow(lat: number, lon: number): number | undefined {
  return world.heightAt(lat, lon);
}

/**
 * Pose the player in a vehicle: getting in, getting out, or at the wheel.
 * Returns false when the player is not in one, to be posed as usual.
 */
function poseDriver(dt: number): boolean {
  const b = boarding;
  if (b !== undefined) {
    b.t += dt;
    const v = b.vehicle;
    const root = fleet.root(v);
    root.updateMatrixWorld(true);
    const walk = b.walk?.time ?? 0;
    if (b.walk !== undefined && b.t < walk) {
      // The walk to the door, turning toward it.
      if (v.car !== undefined) doorAt.set(v.car.seat.outsideX, 0, v.car.seat.hipZ - 0.15);
      else doorAt.set(-0.55, 0, 0.1);
      doorAt.applyMatrix4(root.matrixWorld);
      const k = b.t / walk;
      const dx = doorAt.x - b.walk.x, dz = doorAt.z - b.walk.z;
      const speed = Math.hypot(dx, dz) / walk;
      playerPhase = advancePhase(playerPhase, speed, dt);
      crowd.place(playerSlot, {
        x: b.walk.x + dx * k, y: b.walk.y + (doorAt.y - b.walk.y) * k, z: b.walk.z + dz * k,
        yaw: Math.atan2(-dx, -dz), pose: gaitPose(speed, playerPhase, clock), visible: true,
      });
      return true;
    }
    let done: boolean;
    if (v.car !== undefined) {
      const r = carBoarding(v.car.seat, b.t - walk, b.out, boardPose);
      v.car.door.rotation.y = -1.15 * r.door;
      v.car.door.visible = r.door > 0.01;
      v.car.body.updateWorldMatrix(true, true);
      crowd.placeRider(playerSlot, v.car.body.matrixWorld, r.pose);
      done = r.done;
    } else {
      const r = v.bike?.kind === 'scooter' ? scooterBoarding(lastRide, b.t - walk, b.out, boardPose) : bikeBoarding(lastRide, b.t - walk, b.out, boardPose);
      crowd.placeRider(playerSlot, root.matrixWorld, r.pose);
      done = r.done;
    }
    if (done) {
      boarding = undefined;
      // Stepping onto the scooter wakes its display with two beeps; stepping off, it sleeps with two down.
      if (v.bike?.kind === 'scooter') {
        sound.play(b.out ? 'scooter:off' : 'scooter:on', { bus: 'effects', at: { x: root.position.x, y: root.position.y + 1.1, z: root.position.z }, gain: 0.45, near: 4 });
      }
      if (v.car !== undefined) { v.car.door.rotation.y = 0; v.car.door.visible = false; }
      if (!worldMap.open) player.setInputEnabled(true);
    }
    return true;
  }
  const car = fleet.driven?.car;
  if (car === undefined) return false;
  // At the wheel: seen through the glass from outside, hidden from the seat.
  if (player.thirdPerson) {
    // This frame's: updating the body alone reads its car's matrix from the
    // last frame, and at speed the driver drew a frame behind — back and forth.
    car.body.updateWorldMatrix(true, false);
    crowd.placeRider(playerSlot, car.body.matrixWorld, drivingPose(car.seat));
  } else {
    crowd.place(playerSlot, { x: 0, y: 0, z: 0, yaw: 0, pose: gaitPose(0, 0, 0), visible: false });
  }
  return true;
}

/** Main beam in the car: Shift switches it. */
let highBeam = false;
/** A passing note in the hint, such as "Main beam on", and when it goes. */
let note: { text: string; until: number } | undefined;

/** "E: drive the red estate", while one is in reach. */
const boardHint = document.createElement('div');
boardHint.className = 'wsg-board-hint';
Object.assign(boardHint.style, {
  position: 'absolute', left: '50%', bottom: '18%', transform: 'translateX(-50%)',
  padding: '6px 12px', borderRadius: '8px', background: 'rgba(20, 24, 30, 0.72)', color: '#f4f1ea',
  font: '600 14px/1.3 system-ui, sans-serif', pointerEvents: 'none', display: 'none', whiteSpace: 'nowrap',
});
container.appendChild(boardHint);
let hintTimer = 0;
function updateBoardHint(dt: number): void {
  hintTimer -= dt;
  if (hintTimer > 0) return;
  hintTimer = 0.2;
  const s = player.state;
  let text = '';
  const canopy = player.packedCanopy;
  if (note !== undefined && performance.now() < note.until) text = note.text;
  else if (canopy !== undefined) {
    // Falling with the canopy packed: it opens by itself only if never dived.
    const pull = touch === undefined ? 'Space' : '🪂';
    text = canopy.opensItself ? `${pull}  open the parachute · it opens by itself at 400 m`
      : canopy.agl < 700 ? `⚠  ${pull}  OPEN THE PARACHUTE  ·  ${Math.max(0, Math.round(canopy.agl))} m` : `${pull}  open the parachute: you dived, it is yours to pull`;
  } else if (trip !== undefined) text = tripText();
  else if (filling !== undefined) {
    text = `${filling.tank.energy === 'electric' ? '⚡' : '⛽'}  ${filling.added.toFixed(1)} ${unitOf(filling.tank.energy)} · ${kronor(filling.cost)}`;
  } else if (refuelHere() !== undefined && player.acceptsInput) {
    const { tank, source, name } = refuelHere()!;
    const what = tank.energy === 'electric' ? (source === 'home' ? 'charge at home' : 'charge') : `fill up ${tank.energy === 'jet' ? 'Jet A-1' : tank.energy}`;
    text = `${touch === undefined ? 'Hold F' : '⛽'}  ${what}${name !== '' ? ` at ${name}` : ''} · ${kronor(priceOf(tank.energy, source), 2)}/${unitOf(tank.energy)}`;
  } else if (boarding === undefined && s.mode === 'walk' && heliInReach() !== undefined) {
    text = `${touch === undefined ? 'E' : '🚗'}  fly the helicopter`;
  } else if (s.mode === 'heli' && player.acceptsInput) {
    text = player.helicopterParked ? `${touch === undefined ? 'H' : 'Exit'}  get out` : '';
  } else if (boarding === undefined && s.mode === 'walk' && !player.isDown) {
    const found = vehicleInReach();
    const species = found?.fleet?.species ?? found?.parked?.species;
    const paint = found?.fleet?.paint ?? found?.parked?.paint;
    if (species !== undefined && paint !== undefined) {
      const name = VEHICLE_NAME[species as keyof typeof VEHICLE_NAME] ?? 'vehicle';
      // What it runs on and how full it is, before getting in.
      let tank = found?.fleet?.tank;
      if (tank === undefined && found?.parked !== undefined) {
        const geo = world.toGeo(found.parked.x, -found.parked.z);
        tank = tankOf(species as VehicleSpecies, paint, geo.lat, geo.lon);
      }
      const fuel = tank === undefined ? '' : `   ${tank.energy === 'electric' ? '⚡ electric' : `⛽ ${tank.energy}`} ${Math.round((tank.level / tank.capacity) * 100)} %`;
      const hurt = found?.fleet?.handling;
      const state = hurt === undefined || hurt === AS_NEW || found?.fleet?.damage === undefined ? ''
        : hurt.dead !== undefined ? '   💥 wrecked' : '   💥 damaged';
      text = `${touch === undefined ? 'E' : '🚗'}  ${isTwoWheeler(species) ? 'ride' : 'drive'} the ${paintName(paint)} ${name}${fuel}${state}`;
    } else {
      // Beside one, but on the wrong side: say where the way in is.
      const other = vehicleInReach(true);
      const kind = other?.fleet?.species ?? other?.parked?.species;
      if (kind !== undefined) text = isTwoWheeler(kind) ? 'Go round to the left to get on' : 'Go round to the driver\'s door';
    }
  } else if (s.mode === 'drive' && player.acceptsInput && jobText() !== undefined) {
    text = jobText()!;
  } else if (boarding === undefined && fleet.driven !== undefined && Math.abs(s.speed) < 1.5 && player.acceptsInput) {
    // Wrecked, it says why it will not go.
    const dead = fleet.driven.handling?.dead;
    const why = dead === 'engine' ? 'The engine is wrecked: it will not start  ·  '
      : dead === 'wheel' ? 'A wheel is torn from its suspension: it will not drive  ·  ' : '';
    text = `${why}${touch === undefined ? 'E' : 'Exit'}  get ${fleet.driven.type === 'moto' ? 'off' : 'out'}`;
  }
  // Beside a train: which it is, and where it is going.
  if (text === '' && s.mode === 'walk') {
    const at = world.toLocal(s.lat, s.lon);
    text = trains.describeNear(at.east, -at.north, 40) ?? '';
  }
  boardHint.textContent = text;
  boardHint.style.display = text === '' || worldMap.open ? 'none' : 'block';
}

// --- fuel, charge and money ----------------------------------------------------------
// Every machine runs on something and burns it as it goes; stopped where it
// is sold, F held fills it and the kronor come off the wallet. See
// core/economy/energy.ts for the tanks and the prices.

const WALLET_KEY = 'wsg.wallet';
let wallet = START_BALANCE;
try {
  const saved = Number(localStorage.getItem(WALLET_KEY));
  if (localStorage.getItem(WALLET_KEY) !== null && Number.isFinite(saved)) wallet = saved;
} catch { /* storage blocked: start afresh */ }
let walletSaved = wallet;
/** A fill in progress: how much has gone in, and what it has cost so far. */
let filling: { tank: Tank; added: number; cost: number } | undefined;
let clusterTimer = 0;
/** What the top bar last showed: the browser rewrites innerHTML, so it is not compared against. */
let gaugesHtml = '';

/** The tank of whatever the player is in, if anything. */
function currentTank(): Tank | undefined {
  const mode = player.state.mode;
  if ((mode === 'drive' || mode === 'moto') && boarding === undefined) return fleet.driven?.tank;
  if (mode === 'heli' && heliBoard === undefined) return heliFleet.flown?.tank;
  return undefined;
}

/**
 * Where the machine the player is in could be filled from right now: stopped
 * on a forecourt, by a charging point or a house's wallbox, or landed on a pad.
 */
function refuelHere(): { tank: Tank; source: Source; name: string } | undefined {
  const tank = currentTank();
  if (tank === undefined || tank.capacity - tank.level < 0.05) return undefined;
  const s = player.state;
  const at = world.toLocal(s.lat, s.lon);
  if (s.mode === 'heli') {
    if (!player.helicopterParked) return undefined;
    const pad = worldRenderer.helipads().find(p => Math.hypot(p.x - at.east, p.z + at.north) <= p.radius + 1.5);
    return pad === undefined ? undefined : { tank, source: 'helipad', name: pad.name };
  }
  if (Math.abs(s.speed) > 0.5) return undefined;
  const spot = worldRenderer.energyAt(at.east, -at.north, tank.energy === 'electric' ? ['charger', 'home'] : ['fuel']);
  return spot === undefined ? undefined : { tank, source: spot.source, name: spot.name };
}

/** Burn what the machine uses this frame, fill it while F is held, and show the gauges. */
function updateEnergy(dt: number, speed: number): void {
  const tank = currentTank();
  const mode = player.state.mode;
  if (tank !== undefined) {
    if (mode === 'heli') {
      if (rotor !== undefined && rotor.spin > 0.5) burnAir(tank, dt, !player.helicopterParked);
    } else {
      burnRoad(tank, Math.abs(speed) * dt);
    }
  }
  player.outOfEnergy = tank !== undefined && tank.level <= 0;

  const here = player.held('KeyF') && player.acceptsInput ? refuelHere() : undefined;
  if (here !== undefined) {
    const got = fill(here.tank, here.source, dt, wallet);
    wallet -= got.cost;
    filling ??= { tank: here.tank, added: 0, cost: 0 };
    filling.added += got.added;
    filling.cost += got.cost;
    hintTimer = 0;
    if (got.added === 0) finishFilling(wallet < 1 ? 'Not enough money' : undefined);
  } else if (filling !== undefined) {
    finishFilling();
  }
  if (Math.abs(wallet - walletSaved) >= 1) {
    walletSaved = wallet;
    try { localStorage.setItem(WALLET_KEY, wallet.toFixed(2)); } catch { /* not kept */ }
  }

  // The gauges: the wallet always, and the tank when in a machine.
  const level = tank === undefined ? '' : `   ${tank.energy === 'electric' ? '⚡' : '⛽'} ${Math.round((tank.level / tank.capacity) * 100)} %` +
    // In the air it is time that runs out, not road.
    (mode === 'heli' ? ` · ${Math.round(tank.level)} l · ${Math.round((tank.level / tank.use) * 60)} min` : ` · ${Math.round(rangeKm(tank))} km`);
  // And the station on, in a car with the radio on.
  const station = mode === 'drive' ? radio.playing : undefined;
  const text = `${kronor(wallet)}${level}${station !== undefined ? `   📻 ${station.name}` : ''}`;
  // And the speed limit in force, as the round sign, on a car trip.
  const stars = wanted.stars > 0 ? `   <span style="color:#ffd34d;letter-spacing:1px">${'★'.repeat(wanted.stars)}${'☆'.repeat(MOST_STARS - wanted.stars)}</span>` : '';
  const html = `${text.replace(/[&<>]/g, '')}${stars}${limitBadge(limitHere())}`;
  if (gaugesHtml !== html) { gaugesHtml = html; gauges.innerHTML = html; }
  gauges.style.color = tank !== undefined && tank.level / tank.capacity < 0.12 ? '#ff9a7a' : '#f4f1ea';
  gauges.style.display = worldMap.open ? 'none' : 'block';

  // The car's own instruments, in the one being driven.
  for (const v of fleet.vehicles) if (v.car !== undefined) v.car.cluster.visible = v === fleet.driven;
  clusterTimer -= dt;
  const car = fleet.driven?.car;
  if (car !== undefined && clusterTimer <= 0 && tank !== undefined) {
    clusterTimer = 0.2;
    drawCluster({ speed: speed * 3.6, fraction: tank.level / tank.capacity, electric: tank.energy === 'electric', range: rangeKm(tank), gear: player.gear });
  }
}

/** The fill is over: say what it came to. */
function finishFilling(why?: string): void {
  const f = filling;
  filling = undefined;
  if (f === undefined) return;
  const paid = f.cost > 0.5 ? `Paid ${kronor(f.cost)} for ${f.added.toFixed(1)} ${unitOf(f.tank.energy)}` : '';
  const text = [why, paid].filter(Boolean).join(' · ');
  if (text !== '') note = { text, until: performance.now() + 2500 };
  hintTimer = 0;
}

/** The wallet and the tank, top middle. */
const gauges = document.createElement('div');
gauges.className = 'wsg-gauges';
Object.assign(gauges.style, {
  position: 'absolute', left: '50%', top: '10px', transform: 'translateX(-50%)',
  padding: '5px 12px', borderRadius: '8px', background: 'rgba(20, 24, 30, 0.62)', color: '#f4f1ea',
  font: '600 14px/1.3 system-ui, sans-serif', pointerEvents: 'none', whiteSpace: 'nowrap',
});
container.appendChild(gauges);

// --- haulage ----------------------------------------------------------------------
// Get into a parked truck and it has a load waiting, for a real town some way
// off along the roads; Y takes the job. Its route is on the maps; stopped at
// the drop-off in a truck of its kind, the load is delivered and paid for.
// See core/economy/jobs.ts for what goes where and what it pays.

const JOB_KEY = 'wsg.job';
/** The job taken, if any. */
let job: Job | undefined;
try {
  const saved = localStorage.getItem(JOB_KEY);
  if (saved !== null) job = JSON.parse(saved) as Job;
} catch { /* none */ }
/** A load offered by the truck being driven: being looked for, or found. */
let offer: { vehicle: FleetVehicle; job?: Job; ask: AbortController } | undefined;
let jobTimer = 0;

function saveJob(): void {
  try {
    if (job === undefined) localStorage.removeItem(JOB_KEY);
    else localStorage.setItem(JOB_KEY, JSON.stringify(job));
  } catch { /* not kept */ }
}

/** The kind of truck `v` is, if it is one. */
function truckKind(v: FleetVehicle | undefined): TruckKind | undefined {
  const kind = v?.car?.kind;
  return kind !== undefined && isTruck(kind) ? kind : undefined;
}

/** At most this many points of a route kept with a job, for the maps. */
const JOB_LINE_POINTS = 600;

/** Find a load for the truck just got into: somewhere real, a route there, and its pay. */
async function offerJob(v: FleetVehicle): Promise<void> {
  const kind = truckKind(v);
  if (kind === undefined || offer?.vehicle === v) return;
  offer?.ask.abort();
  const ask = new AbortController();
  const mine = { vehicle: v, ask } as NonNullable<typeof offer>;
  offer = mine;
  const from = { lat: v.lat, lon: v.lon };
  try {
    // A few tries: a way aimed out to sea, or over empty forest, finds nowhere.
    for (let attempt = 0; attempt < 4; attempt++) {
      const plan = planJob(kind, Math.random);
      const aim = pointToward(from.lat, from.lon, plan.km, plan.bearing);
      const drop = await findDropOff(config.vector.urlTemplate, aim, Math.max(2500, plan.km * 300), from, plan.km * 600, ask.signal);
      if (drop === undefined) continue;
      const route = await fetchRoute('car', from, drop, ask.signal);
      // Not across the water: a truck job is by road.
      const straight = Math.hypot((drop.lat - from.lat) * 111_320, (drop.lon - from.lon) * 111_320 * Math.cos((from.lat * Math.PI) / 180));
      if (route.distance < 1500 || route.ferry || route.distance > straight * 2.5) continue;
      const step = Math.max(1, Math.ceil(route.points.length / JOB_LINE_POINTS));
      const line = route.points.filter((_, i) => i % step === 0 || i === route.points.length - 1);
      const end = route.points[route.points.length - 1];
      const km = route.distance / 1000;
      mine.job = {
        kind, cargo: plan.cargo, to: { lat: end.lat, lon: end.lon }, place: drop.name, km,
        pay: jobPay(kind, km), due: Date.now() + jobTime(route.duration) * 1000, line,
      };
      break;
    }
    if (mine.job === undefined && offer === mine) offer = undefined;
  } catch {
    if (offer === mine) offer = undefined;
  }
  hintTimer = 0;
}

/** Y: take the job on offer. */
function takeJob(): void {
  const o = offer;
  if (o?.job === undefined || fleet.driven !== o.vehicle) return;
  // The clock starts now, not when the load was found.
  const allowed = o.job.due - Date.now();
  job = { ...o.job, due: Date.now() + Math.max(allowed, 0) };
  offer = undefined;
  saveJob();
  note = { text: `📦 ${job.cargo} to ${job.place} · ${Math.round(job.km)} km · due in ${jobTimeLeft(job.due - Date.now())}`, until: performance.now() + 5000 };
  hintTimer = 0;
}

/** The job's line, as the hint shows it. */
function jobText(): string | undefined {
  const s = player.state;
  const driven = truckKind(fleet.driven);
  if (offer !== undefined && fleet.driven === offer.vehicle && boarding === undefined) {
    if (offer.job === undefined) return '📦  Looking for a load…';
    const o = offer.job;
    return `${touch === undefined ? 'Y' : '📦'}  take ${job !== undefined ? 'this job instead' : 'the job'}: ${o.cargo} to ${o.place}` +
      ` · ${Math.round(o.km)} km · ${kronor(o.pay)} · ${jobTimeLeft(o.due - Date.now())}`;
  }
  if (job === undefined || s.mode !== 'drive') return undefined;
  const at = world.toLocal(job.to.lat, job.to.lon), me = world.toLocal(s.lat, s.lon);
  const d = Math.hypot(at.east - me.east, at.north - me.north);
  const left = d < 950 ? `${Math.round(d / 10) * 10} m` : `${(d / 1000).toFixed(d < 9500 ? 1 : 0)} km`;
  if (d < DELIVER_RADIUS && driven === job.kind) return '📦  Stop here to unload';
  if (driven !== job.kind) return undefined;
  return `📦  ${job.place} · ${left} · ${jobTimeLeft(job.due - Date.now())} · ${kronor(job.pay)}`;
}

/** Each frame: an offer that went with its truck dropped; a load at its drop-off delivered. */
function updateJob(dt: number): void {
  // Driven off without the load, or left: no job.
  if (offer !== undefined && boarding === undefined && (fleet.driven !== offer.vehicle || Math.abs(player.state.speed) > 3)) {
    offer.ask.abort();
    offer = undefined;
  }
  jobTimer -= dt;
  if (jobTimer > 0 || job === undefined) return;
  jobTimer = 0.25;
  const s = player.state;
  if (s.mode !== 'drive' || truckKind(fleet.driven) !== job.kind || Math.abs(s.speed) > 0.5 || boarding !== undefined) return;
  const at = world.toLocal(job.to.lat, job.to.lon), me = world.toLocal(s.lat, s.lon);
  if (Math.hypot(at.east - me.east, at.north - me.north) > DELIVER_RADIUS) return;
  const late = Date.now() > job.due;
  const paid = payOnDelivery(job.pay, late);
  wallet += paid;
  note = { text: `📦 Delivered ${job.cargo} to ${job.place} · paid ${kronor(paid)}${late ? ' (late: half pay)' : ''}`, until: performance.now() + 6000 };
  job = undefined;
  saveJob();
  hintTimer = 0;
}

// --- going there on its own ---------------------------------------------------------
// Chosen on the world map: "Go there". By car along the roads — to the nearest
// one first — keeping right, within the road's speed, slowing for bends and
// junctions and stopping before turning at one; on foot, running along paths
// and pavements; on the motorbike, riding them; by helicopter, straight
// there. See core/nav/autopilot.ts. Any driving key takes over again.

interface Trip {
  travel: Travel;
  to: { lat: number; lon: number };
  /** Along a route on the ground; none for the helicopter, which flies straight. */
  follower?: Follower;
  /** The route as drawn on the maps. */
  line: Array<{ lat: number; lon: number }>;
  /** The helicopter has lifted off: landing again is arriving. */
  lifted: boolean;
  /** Metres to go, for the display. */
  left: number;
  /** The roads' real limits are known (from OSM): show the one in force. */
  limits: boolean;
  /** The route as it was made — kept to save the trip, to carry on later. */
  plan?: { points: Array<{ lat: number; lon: number }>; speeds: number[]; junctions: Array<{ lat: number; lon: number; stop: boolean }> };
}
/** The vehicle a resumed trip was in, for the first one made. */
let resumeVehicle: SavedTrip['vehicle'] = resumed?.trip.vehicle;
let trip: Trip | undefined;
/** The speed limit signs along a car trip's way. */
const speedSigns = new SpeedSigns(scene);
/** A route being asked for, to be dropped if another is chosen first. */
let tripAsk: AbortController | undefined;
/** Keys that take the controls back from the autopilot. */
const TAKE_OVER = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'Space', 'ShiftLeft', 'ShiftRight', 'KeyQ', 'KeyE']);

/** How the player would go from here, if at all. */
function travelFor(mode: string): Travel | undefined {
  if (boarding !== undefined || heliBoard !== undefined) return undefined;
  return mode === 'drive' ? 'car' : mode === 'moto' ? 'moto' : mode === 'walk' ? 'foot' : mode === 'heli' ? 'heli' : undefined;
}

worldMap.travel = {
  label: () => {
    const t = travelFor(player.state.mode);
    return t === 'car' ? '🚗  Drive there' : t === 'moto' ? '🏍  Ride there' : t === 'foot' ? '🏃  Run there' : t === 'heli' ? '🚁  Fly there' : undefined;
  },
  go: (lat, lon) => { void startTrip(lat, lon); },
};

async function startTrip(lat: number, lon: number): Promise<void> {
  const travel = travelFor(player.state.mode);
  if (travel === undefined) return;
  tripAsk?.abort();
  trip = undefined;
  speedSigns.clear();
  const s = player.state;
  const from = { lat: s.lat, lon: s.lon };
  if (travel === 'heli') {
    trip = { travel, to: { lat, lon }, line: [from, { lat, lon }], lifted: !player.helicopterParked, left: 0, limits: false };
    return;
  }
  note = { text: 'Finding the way there…', until: performance.now() + 20000 };
  hintTimer = 0;
  const ask = new AbortController();
  tripAsk = ask;
  try {
    const found = await fetchRoute(travel === 'car' ? 'car' : travel === 'moto' ? 'bike' : 'foot', from, { lat, lon }, ask.signal);
    if (tripAsk !== ask || travelFor(player.state.mode) !== travel) return;
    // From where the player is — onto the nearest road, for a car — and, off
    // the road at the end, on foot or by bike, the last bit straight there.
    const now = player.state;
    const points = [{ lat: now.lat, lon: now.lon }, ...found.points];
    const speeds = [NaN, ...found.speeds];
    if (travel !== 'car') { points.push({ lat, lon }); speeds.push(NaN); }
    const route: Route = makeRoute(points, speeds, found.junctions);
    trip = {
      travel, to: { lat, lon }, follower: new Follower(route, travel), line: points, lifted: false, left: route.length, limits: found.limits,
      plan: { points, speeds, junctions: found.junctions },
    };
    if (travel === 'car') speedSigns.set(placeSigns(route));
    // Then the roads' own limits and junctions, from OSM, when they come.
    const started = trip;
    void found.roads.then((roads) => {
      if (roads === undefined || trip !== started) return;
      const better = makeRoute(points, [NaN, ...roads.speeds, ...(travel !== 'car' ? [NaN] : [])], roads.junctions);
      route.speeds.set(better.speeds);
      route.junctions = better.junctions;
      started.follower?.junctionsChanged();
      started.limits = true;
      started.plan = { points, speeds: [NaN, ...roads.speeds, ...(travel !== 'car' ? [NaN] : [])], junctions: roads.junctions };
      speedSigns.set(placeSigns(route));
    });
    note = undefined;
  } catch (e) {
    if (ask.signal.aborted) return;
    note = { text: `No way there: ${e instanceof Error ? e.message : String(e)}`, until: performance.now() + 4000 };
  } finally {
    if (tripAsk === ask) tripAsk = undefined;
    hintTimer = 0;
  }
}

function endTrip(why: string): void {
  trip = undefined;
  clearTrip();
  speedSigns.clear();
  player.autopilot = undefined;
  note = { text: why, until: performance.now() + 2500 };
  hintTimer = 0;
}

// ---------------------------------------------------------------------------
// The top-down view (C cycles to it). Straight down, north up: cheaper to
// draw — coarser terrain streamed, a short draw distance — and, on a phone,
// steered by pointing rather than with the stick. A tap on the ground goes
// straight there, whatever is in between; a finger held down steers towards
// wherever it is, for as long as it is held.

/** A press that moves further than this is a drag, not a tap, px. */
const TAP_SLOP = 10;
/** A press held this long steers rather than taps, ms. */
const HOLD_STEER = 220;
/** Drawn no further than this below the camera and around, from above, m. */
const TOP_FAR = 2500;
/** A tap this close to the destination ring, on screen, stops the trip, px. */
const TAP_STOP = 36;

/** Where a tap sent the player: a small, faint ring on the ground. */
const tapMarker = new Mesh(
  new RingGeometry(0.5, 0.65, 32).rotateX(-Math.PI / 2),
  new MeshBasicMaterial({ color: 0xffe08a, transparent: true, opacity: 0.45, depthTest: false, fog: false }),
);
tapMarker.renderOrder = 10;
tapMarker.visible = false;
scene.add(tapMarker);
let wasTop = false;

/** Called each frame: the top view's lighter streaming and draw distance, and the marker. */
function topView(): void {
  const top = player.topDown && !worldMap.open;
  if (top !== wasTop) {
    wasTop = top;
    world.lodOverride = top ? { ...config.lod, minZoom: Math.max(config.lod.minZoom, 13) } : undefined;
    if (!top) { camera.far = FAR_PLANE; camera.updateProjectionMatrix(); }
  }
  if (top) {
    // Far enough to reach the ground from a helicopter high up, and no further.
    const s = player.state;
    const below = Math.max(0, s.altitude - (world.heightAt(s.lat, s.lon) ?? s.altitude));
    const far = Math.max(TOP_FAR, below + 1500);
    if (Math.abs(far - camera.far) > 50) { camera.far = far; camera.updateProjectionMatrix(); }
  }
  const to = trip?.to;
  tapMarker.visible = top && to !== undefined;
  if (to !== undefined && tapMarker.visible) {
    const at = world.toLocal(to.lat, to.lon);
    tapMarker.position.set(at.east, (world.heightAt(to.lat, to.lon) ?? player.state.altitude) + 0.3, -at.north);
    tapMarker.scale.setScalar(Math.max(1, camera.position.y - tapMarker.position.y) / 70);
  }
}

const tapRay = new Raycaster();
const tapPlane = new Plane(new Vector3(0, 1, 0), 0);
const tapPoint = new Vector3();

/** The ground under a point on the screen, from above. */
function groundAtScreen(x: number, y: number): { lat: number; lon: number } | undefined {
  const rect = renderer.domElement.getBoundingClientRect();
  tapRay.setFromCamera(new Vector2(((x - rect.left) / rect.width) * 2 - 1, -((y - rect.top) / rect.height) * 2 + 1), camera);
  // The ground is not flat: meet a level plane at the player's feet, then
  // again at the height found there, until it settles.
  let height = world.heightAt(player.state.lat, player.state.lon) ?? player.state.altitude;
  let geo: { lat: number; lon: number } | undefined;
  for (let i = 0; i < 4; i++) {
    tapPlane.constant = -height;
    if (tapRay.ray.intersectPlane(tapPlane, tapPoint) === null) return geo;
    geo = world.toGeo(tapPoint.x, -tapPoint.z);
    const h = world.heightAt(geo.lat, geo.lon);
    if (h === undefined || Math.abs(h - height) < 0.5) break;
    height = h;
  }
  return geo;
}

/** Go straight to where the ground was tapped: no roads, no route. */
function goTo(lat: number, lon: number): void {
  const travel = travelFor(player.state.mode);
  if (travel === undefined) return;
  tripAsk?.abort();
  speedSigns.clear();
  const s = player.state;
  const points = [{ lat: s.lat, lon: s.lon }, { lat, lon }];
  if (travel === 'heli') {
    trip = { travel, to: { lat, lon }, line: points, lifted: !player.helicopterParked, left: 0, limits: false };
    return;
  }
  const route = makeRoute(points, [NaN, NaN], []);
  trip = {
    travel, to: { lat, lon }, follower: new Follower(route, travel), line: points, lifted: false, left: route.length, limits: false,
    plan: { points, speeds: [NaN, NaN], junctions: [] },
  };
}

/** A finger held on the ground from above: where on the screen, and since when. */
let held: { id: number; x: number; y: number; since: number; moved: boolean; steering: boolean; lifted: boolean } | undefined;

/**
 * The hands on the controls while a finger is held down from above: towards
 * the ground under it, which moves on ahead as the player follows it.
 */
function steerHeld(now: number, ground: number | undefined): AutoInput | undefined {
  const h = held;
  if (h === undefined || !player.topDown || worldMap.open) return undefined;
  if (!h.steering) {
    if (!h.moved && now - h.since < HOLD_STEER) return undefined;
    h.steering = true;
    h.lifted = !player.helicopterParked;
    // Steering by hand drops any trip under way.
    if (trip !== undefined) { trip = undefined; clearTrip(); speedSigns.clear(); tripAsk?.abort(); }
  }
  const travel = travelFor(player.state.mode);
  const to = groundAtScreen(h.x, h.y);
  if (travel === undefined || to === undefined) return undefined;
  const s = player.state;
  const d = geoToEnu(makeOrigin(s.lat, s.lon), to.lat, to.lon);
  const distance = Math.hypot(d.east, d.north);
  // How far the finger is from the middle, as a share of the screen: the
  // same reach means the same speed however high the view is.
  const reach = distance / Math.max(8, (camera.position.y - (ground ?? camera.position.y - 70)) * 0.35);
  if (travel === 'foot') {
    if (distance < 1) return { moveEast: 0, moveNorth: 0 };
    return { moveEast: d.east / distance, moveNorth: d.north / distance, run: reach > 0.35 };
  }
  if (travel === 'heli') {
    const v = player.heliVelocity;
    const agl = ground === undefined ? 100 : s.altitude - HELI.skidHeight - ground;
    if (agl > 8) h.lifted = true;
    const c = flyTo({ east: -d.east, north: -d.north, agl, heading: (s.heading * Math.PI) / 180, ve: v.east, vn: v.north, vu: v.up, landed: player.helicopterParked }, h.lifted);
    return { cyclicPitch: c.cyclicPitch, cyclicRoll: c.cyclicRoll, yaw: c.yaw, collective: c.collective };
  }
  // Car and motorbike: turn towards the finger, faster the further it is;
  // behind and close, back round to it.
  const bearing = Math.atan2(d.east, d.north);
  let off = bearing - (s.heading * Math.PI) / 180;
  off = Math.atan2(Math.sin(off), Math.cos(off));
  const speed = player.forwardSpeed;
  if (distance < 3) return { throttle: speed > 0.5 ? -1 : speed < -0.5 ? 1 : 0, steer: 0 };
  const reverse = Math.abs(off) > 2.1 && distance < 25;
  const want = (travel === 'moto' ? 22 : 28) * Math.min(1, Math.max(0.15, reach)) * (reverse ? -0.3 : Math.max(0.25, Math.cos(off)));
  const throttle = Math.max(-1, Math.min(1, (want - speed) * 0.3));
  const steer = Math.max(-1, Math.min(1, off * 1.6)) * (reverse ? -1 : 1);
  return { throttle, steer };
}

{
  const presses = new Map<number, { x: number; y: number; moved: boolean }>();
  const canvas = renderer.domElement;
  canvas.addEventListener('pointerdown', (e) => {
    presses.set(e.pointerId, { x: e.clientX, y: e.clientY, moved: presses.size > 0 });
    // A second finger makes both a pinch, and stops steering.
    if (presses.size > 1) { for (const p of presses.values()) p.moved = true; held = undefined; return; }
    if (player.topDown && !worldMap.open && e.button === 0) {
      held = { id: e.pointerId, x: e.clientX, y: e.clientY, since: performance.now(), moved: false, steering: false, lifted: false };
    }
  });
  canvas.addEventListener('pointermove', (e) => {
    const p = presses.get(e.pointerId);
    if (p !== undefined && Math.hypot(e.clientX - p.x, e.clientY - p.y) > TAP_SLOP) p.moved = true;
    if (held?.id === e.pointerId) {
      held.x = e.clientX;
      held.y = e.clientY;
      if (p?.moved === true) held.moved = true;
    }
  });
  canvas.addEventListener('pointerup', (e) => {
    const p = presses.get(e.pointerId);
    presses.delete(e.pointerId);
    const steered = held?.id === e.pointerId && held.steering;
    if (held?.id === e.pointerId) held = undefined;
    if (steered || p === undefined || p.moved || e.button > 0 || !player.topDown || worldMap.open) return;
    // On the destination's ring: stop going there.
    if (trip !== undefined && tapMarker.visible) {
      const rect = renderer.domElement.getBoundingClientRect();
      const at = tapPoint.copy(tapMarker.position).project(camera);
      const x = rect.left + ((at.x + 1) / 2) * rect.width, y = rect.top + ((1 - at.y) / 2) * rect.height;
      if (Math.hypot(e.clientX - x, e.clientY - y) < TAP_STOP) { endTrip('Stopped going there'); return; }
    }
    const geo = groundAtScreen(e.clientX, e.clientY);
    if (geo !== undefined) goTo(geo.lat, geo.lon);
  });
  canvas.addEventListener('pointercancel', (e) => {
    presses.delete(e.pointerId);
    if (held?.id === e.pointerId) held = undefined;
  });
  // The wheel zooms the view from above.
  canvas.addEventListener('wheel', (e) => {
    if (!player.topDown || worldMap.open) return;
    e.preventDefault();
    player.zoomTop(Math.exp(e.deltaY * 0.0015));
  }, { passive: false });
}

// --- fists, and things picked up ----------------------------------------------------
// On foot the left and right mouse buttons punch with that fist; both at
// once pick up what lies just ahead (a litter bin, a piece off a wreck), or
// let go of it. Holding it, a button swings it round to that side and the
// wheel raises and lowers it; let go mid-swing, it flies. A fist or a swing
// landing on a car standing in traffic gets its driver out, and the car is
// the player's to take. See core/character/arms.ts.

const arms = newArms();
/** What the player has in their hands, where it was last frame and how fast it goes. */
let carried: { piece: BrokenPiece; last: Vector3; velocity: Vector3 } | undefined;
/** What comes off its fixings to be picked up. */
const LIFTABLE: ReadonlySet<Species> = new Set<Species>(['bin']);
/** Bigger than this, half a side, metres, a piece is too much to lift. */
const MOST_LIFTED = 1.1;
/** What arms landed this frame, for once the body is drawn. */
let landed: Array<Side | 'swing'> = [];
const playerJoints = new Float32Array(POINTS * 3);
const heldAt = new Vector3();
const HELD_UP = new Vector3(0, 1, 0);

/** On foot, hands free to use: not down, not in the view from above. */
function handsFree(): boolean {
  return player.state.mode === 'walk' && player.acceptsInput && downed === undefined && !player.isDown && !player.topDown && !worldMap.open;
}

// The controller's right stick lifts what is held, as the wheel does.
gamepad.carrying = { holding: () => carried !== undefined, raise: (by) => raise(arms, by) };

function useHand(side: Side): void {
  if (!handsFree()) return;
  if (carried !== undefined) swing(arms, side);
  else punch(arms, side);
}

function bothHands(): void {
  if (!handsFree()) return;
  arms.punch.left = arms.punch.right = NaN;
  if (carried !== undefined) letGoOfCarried(1.3);
  else pickUp();
}

{
  const canvas = renderer.domElement;
  // Each button's own press, chords and all, as pointer events do not give.
  canvas.addEventListener('mousedown', (e) => {
    if (e.button !== 0 && e.button !== 2) return;
    if ((e.buttons & 3) === 3) bothHands();
    else useHand(e.button === 0 ? 'left' : 'right');
  });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  canvas.addEventListener('wheel', (e) => {
    if (carried === undefined || player.topDown || worldMap.open) return;
    e.preventDefault();
    raise(arms, -e.deltaY * 0.0012);
  }, { passive: false });
}

/** Where on the ground just ahead of the player is, scene metres. */
function aheadOfPlayer(by: number): { x: number; y: number; z: number } {
  const s = player.state, at = world.toLocal(s.lat, s.lon), yaw = player.avatarPose.yaw;
  return { x: at.east - Math.sin(yaw) * by, y: player.avatarPose.feet, z: -at.north - Math.cos(yaw) * by };
}

function pickUp(): void {
  const a = aheadOfPlayer(0.8);
  let piece = debris.takeNear(a.x, a.y + 0.2, a.z, 1.1);
  let material: SoundMaterial = 'metal';
  if (piece === undefined) {
    const p = worldRenderer.propsNear(a.x, a.z, 1.1, LIFTABLE)[0];
    if (p !== undefined) {
      piece = pieceOf(p.mesh.geometry.clone(), instanceWorld(p), p.mesh.material as Material);
      worldRenderer.takeProp(p);
      debris.group.add(piece.mesh);
      material = 'bin';
    }
  }
  if (piece === undefined) {
    note = { text: 'Nothing here to pick up', until: performance.now() + 1200 };
    hintTimer = 0;
    return;
  }
  if (Math.max(...piece.half) > MOST_LIFTED) {
    // Too much to lift: it stays where it lies.
    debris.add(piece, [0, 0, 0], [0, 0, 0]);
    note = { text: 'Too big to lift', until: performance.now() + 1200 };
    hintTimer = 0;
    return;
  }
  takeHold(arms);
  carried = { piece, last: piece.mesh.position.clone(), velocity: new Vector3() };
  effects.knock(material, 0.6, { x: a.x, y: a.y + 0.5, z: a.z });
}

/** Let go of what is held, flying on at `throwing` times how fast it was moving. */
function letGoOfCarried(throwing: number): void {
  const c = carried;
  if (c === undefined) return;
  carried = undefined;
  letGo(arms);
  const v = c.velocity.multiplyScalar(throwing);
  debris.add(c.piece, [v.x, v.y, v.z], [(Math.random() - 0.5) * 3, (Math.random() - 0.5) * 3, (Math.random() - 0.5) * 3]);
}

/** The arms over `base`, the walk, this frame; or nothing of them when the hands are not free. */
function poseHands(dt: number, base: GaitPose): GaitPose {
  if (!handsFree()) {
    // Got in somewhere, fell, or went up: what was held drops.
    letGoOfCarried(1);
    arms.punch.left = arms.punch.right = arms.swing = NaN;
    landed = [];
    return base;
  }
  landed = stepArms(arms, dt);
  return armPose(base, arms);
}

/**
 * Once the body is drawn: what is held carried in the hands, and what the
 * fists or a swing landed on. `yaw` the body's facing, `twist` the torso's
 * turn on it.
 */
function afterHands(dt: number, yaw: number, twist: number): void {
  if (!handsFree()) return;
  crowd.jointsOf(playerSlot, playerJoints);
  const wrist = (k: number, out: Vector3) => out.set(playerJoints[k * 3], playerJoints[k * 3 + 1], playerJoints[k * 3 + 2]);
  const facing = yaw + twist, fx = -Math.sin(facing), fz = -Math.cos(facing);
  if (carried !== undefined) {
    const { piece } = carried;
    // Between the hands, held out in front of them by its own depth.
    wrist(J.wristL, heldAt).add(wrist(J.wristR, propAt)).multiplyScalar(0.5);
    heldAt.x += fx * (piece.half[2] + 0.06);
    heldAt.z += fz * (piece.half[2] + 0.06);
    piece.mesh.position.copy(heldAt);
    piece.mesh.quaternion.setFromAxisAngle(HELD_UP, facing);
    if (dt > 0) carried.velocity.lerp(propAt.copy(heldAt).sub(carried.last).divideScalar(dt), Math.min(1, dt * 20));
    carried.last.copy(heldAt);
  }
  for (const what of landed) {
    const at = what === 'swing' ? carried?.piece.mesh.position : wrist(what === 'left' ? J.wristL : J.wristR, heldAt);
    if (at === undefined) continue;
    const hit = traffic.standingAt(at.x + fx * 0.1, at.y, at.z + fz * 0.1, 0.35);
    if (hit === undefined) continue;
    effects.knock(what === 'swing' ? 'bin' : 'metal', what === 'swing' ? 3 : 1.5, { x: at.x, y: at.y, z: at.z });
    pullOutDriver(hit.id);
  }
  landed = [];
}

/**
 * A car standing in traffic, struck: its driver gets out of the driver's
 * door and hurries off, and the car stands there, the player's to take.
 */
function pullOutDriver(id: number): void {
  const r = traffic.release(id);
  if (r === undefined) return;
  const v = wreckFromTraffic(r);
  v.motion = undefined;
  // The driver's door, on the left, a step out from it.
  const size = VEHICLE_SIZE[r.species];
  const fx = -Math.sin(r.yaw), fz = -Math.cos(r.yaw), rx = Math.cos(r.yaw), rz = -Math.sin(r.yaw);
  const door = { x: r.x - rx * (size.halfWidth + 0.45) + fx * 0.3, y: r.y, z: r.z - rz * (size.halfWidth + 0.45) + fz * 0.3 };
  villagers.addLeaving(lookFromSeed(Math.floor(Math.random() * 1e9)), door.x, door.y, door.z, r.yaw + Math.PI / 2);
  effects.door(false, door);
  offences.push({ offence: 'carjack', seen: sawThem });
  note = { text: `The driver ran off · ${touch === undefined ? 'E' : '🚗'}  take the car`, until: performance.now() + 3000 };
  hintTimer = 0;
}

/** The autopilot's hands on the controls this frame, or undefined for the player's. */
function steerTrip(dt: number, ground: number | undefined): AutoInput | undefined {
  const t = trip;
  if (t === undefined) return undefined;
  const s = player.state;
  if (travelFor(s.mode) !== t.travel) { endTrip('Stopped going there'); return undefined; }
  const heading = (s.heading * Math.PI) / 180;
  // Not until the ground under it has streamed in: a resumed trip starts before it has.
  if (ground === undefined && t.travel !== 'heli') return t.travel === 'foot' ? { moveEast: 0, moveNorth: 0 } : { throttle: 0, steer: 0 };
  if (t.follower !== undefined) {
    const at = onRoute(t.follower.route, s.lat, s.lon);
    const c = t.follower.ground({ east: at.east, north: at.north, heading, speed: player.forwardSpeed }, dt,
      t.travel === 'car' ? trafficSense(at) : undefined);
    t.left = Math.max(0, t.follower.route.length - t.follower.along);
    if (c.done) { endTrip('You have arrived'); return undefined; }
    if (t.travel === 'foot') return { moveEast: c.moveEast, moveNorth: c.moveNorth, run: true };
    return { throttle: c.throttle, steer: c.steer };
  }
  // The helicopter, straight there.
  const here = geoToEnu(makeOrigin(t.to.lat, t.to.lon), s.lat, s.lon);
  t.left = Math.hypot(here.east, here.north);
  const v = player.heliVelocity;
  const agl = ground === undefined ? 100 : s.altitude - HELI.skidHeight - ground;
  if (agl > 8) t.lifted = true;
  const c = flyTo({ east: here.east, north: here.north, agl, heading, ve: v.east, vn: v.north, vu: v.up, landed: player.helicopterParked }, t.lifted);
  if (c.done) { endTrip('Landed at your destination'); return undefined; }
  return { cyclicPitch: c.cyclicPitch, cyclicRoll: c.cyclicRoll, yaw: c.yaw, collective: c.collective };
}

/**
 * The traffic around the player and the lanes under them, for the autopilot,
 * in its route's frame. That frame and the scene's are both flat, east and
 * north, about different origins: over the hundred metres this looks at, one
 * is the other moved, with the scene's z running south.
 */
function trafficSense(at: { east: number; north: number }): TrafficSense {
  const local = world.toLocal(player.state.lat, player.state.lon);
  const px = local.east, pz = -local.north;
  const toRoute = (x: number, z: number) => ({ east: at.east + (x - px), north: at.north - (z - pz) });
  return {
    others: traffic.nearby(px, pz, player.state.altitude, 120).map(c => ({
      ...toRoute(c.x, c.z), de: c.dx, dn: -c.dz, speed: c.speed, halfLength: c.halfLength,
    })),
    lane: (east, north, de, dn, left) => {
      const r = worldRenderer.lanes.laneFor(px + (east - at.east), pz - (north - at.north), de, -dn, left);
      return r === undefined ? undefined : { ...toRoute(r.x, r.z), hasLeft: r.hasLeft };
    },
  };
}

/** What the autopilot is doing, for the hint. */
function tripText(): string {
  const t = trip!;
  const verb = t.travel === 'car' ? 'Driving' : t.travel === 'moto' ? 'Riding' : t.travel === 'foot' ? 'Running' : 'Flying';
  const left = t.left >= 1000 ? `${(t.left / 1000).toFixed(1)} km` : `${Math.round(t.left)} m`;
  // How long yet: by the limits on the road, or at a run, a ride, a flight.
  const f = t.follower;
  const seconds = f !== undefined && t.travel === 'car' ? timeLeft(f.route, f.along)
    : t.left / (t.travel === 'foot' ? 6 : t.travel === 'moto' ? 9 : 45);
  const time = seconds < 90 ? `${Math.max(1, Math.round(seconds))} s` : `${Math.round(seconds / 60)} min`;
  // And when that is, on the device's own clock.
  const arrives = localClock(new Date(Date.now() + seconds * 1000));
  const takeOver = player.topDown ? 'tap ring to stop' : `${touch === undefined ? 'W A S D' : 'the stick'} to take over`;
  return `🧭  ${verb} there · ${left} · ${time} · arrives ${arrives} · ${takeOver}`;
}

/** The limit in force on a car trip, km/h, for the sign in the corner. */
function limitHere(): number | undefined {
  const f = trip?.travel === 'car' ? trip.follower : undefined;
  if (f === undefined) return undefined;
  const v = f.route.speeds[pointAt(f.route, f.along).stretch];
  return Number.isFinite(v) ? Math.round((v * 3.6) / 10) * 10 : undefined;
}

/** The trip as it stands, to be carried on after the game is closed. */
function tripToKeep(): SavedTrip | undefined {
  const t = trip;
  if (t === undefined) return undefined;
  const s = player.state;
  const driven = fleet.driven, flown = heliFleet.flown;
  const vehicle = t.travel === 'heli' ? (flown === undefined ? undefined : { species: 'heli', paint: 0, tank: flown.tank })
    : driven === undefined ? undefined : { species: driven.species, paint: driven.paint, tank: driven.tank, damage: driven.damage };
  return {
    v: 1, travel: t.travel, to: t.to, at: { lat: s.lat, lon: s.lon },
    points: t.plan?.points ?? [], speeds: t.plan?.speeds ?? [], junctions: t.plan?.junctions ?? [],
    along: t.follower?.along ?? 0, limits: t.limits, savedAt: Date.now(), vehicle,
  };
}
let tripSaveTimer = 0;
/** Keep the trip every couple of seconds, so closing the page loses none of it. */
function keepTrip(dt: number): void {
  tripSaveTimer -= dt;
  if (tripSaveTimer > 0 || trip === undefined) return;
  tripSaveTimer = 2;
  const t = tripToKeep();
  if (t !== undefined) saveTrip(t);
}
// And as the page goes: a phone puts a tab away without a word.
const keepNow = () => { const t = tripToKeep(); if (t !== undefined) saveTrip(t); };
window.addEventListener('pagehide', keepNow);
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') keepNow(); });

// A trip left under way: carried on by the time since — see app/tripStore.ts.
if (resumed !== undefined) {
  const r = resumed, t = r.trip;
  const km = r.covered >= 1000 ? `${(r.covered / 1000).toFixed(1)} km` : `${Math.round(r.covered)} m`;
  const hold = performance.now() + 7000;
  if (r.arrived) {
    clearTrip();
    note = { text: `You arrived while you were away · ${km} on the way`, until: hold };
  } else if (r.dry) {
    clearTrip();
    note = { text: `Ran out of ${t.vehicle?.tank.energy === 'electric' ? 'charge' : 'fuel'} on the way, after ${km}`, until: hold };
  } else if (t.travel === 'heli' || t.points.length < 2) {
    trip = { travel: t.travel, to: t.to, line: [{ lat: r.lat, lon: r.lon }, t.to], lifted: true, left: 0, limits: false };
    note = { text: `Carried on while you were away · ${km}`, until: hold };
  } else {
    const route = makeRoute(t.points, t.speeds, t.junctions);
    const follower = new Follower(route, t.travel);
    follower.along = r.along;
    trip = {
      travel: t.travel, to: t.to, follower, line: t.points, lifted: false, left: route.length - r.along, limits: t.limits,
      plan: { points: t.points, speeds: t.speeds, junctions: t.junctions },
    };
    if (t.travel === 'car') speedSigns.set(placeSigns(route));
    note = { text: `Carried on while you were away · ${km}`, until: hold };
  }
}

player.onPress = (code) => {
  // Any driving key: the player's hands back on the controls.
  if (trip !== undefined && TAKE_OVER.has(code)) endTrip('You took over');
  if (code === 'KeyJ') layJumps();
  if (code === 'KeyK' && fleet.driven?.police !== undefined) {
    // At the wheel of a police car: its own blue lights and siren.
    const p = fleet.driven.police;
    p.siren = !p.siren;
    note = { text: p.siren ? 'Blue lights and siren on' : 'Blue lights and siren off', until: performance.now() + 1500 };
  } else if (code === 'KeyK') {
    // For now a switch to try them by: the police near respond, or stand down.
    const s = player.state, at = world.toLocal(s.lat, s.lon);
    policeResponding = !policeResponding;
    const n = traffic.respond(at.east, -at.north, 500, policeResponding);
    note = { text: n === 0 ? 'No police near' : policeResponding ? `Police responding: ${n}` : 'Police standing down', until: performance.now() + 1500 };
  }
  if (code === 'KeyY') takeJob();
  if (code === 'KeyR' && player.state.mode === 'drive') radio.next(player.state.lat, player.state.lon);
  if (code === 'PunchLeft') useHand('left');
  if (code === 'PunchRight') useHand('right');
  if (code === 'Grab') bothHands();
  if ((code === 'ShiftLeft' || code === 'ShiftRight') && player.state.mode === 'drive') {
    highBeam = !highBeam;
    note = { text: highBeam ? 'Main beam on' : 'Main beam off', until: performance.now() + 1200 };
    hintTimer = 0;
  }
  if (code === 'KeyE') {
    const mode = player.state.mode;
    if (mode === 'walk') getIn();
    else if (mode === 'drive' || mode === 'moto') getOut();
  }
  // Landed, H climbs out through the door; with summoning on, H in the air
  // still takes the player out at once.
  if (code === 'KeyH' && player.state.mode === 'heli' && player.helicopterParked) {
    leaveHeli();
    return true;
  }
};


let last = performance.now();
/** The weather the crowd last dressed for, roughly: see `crowd.setWeather`. */
let dressedFor = '';

// --- snow ------------------------------------------------------------------------

/** The wheels' last places, for the tracks they make between frames. */
let snowWheels: Array<{ x: number; z: number }> = [];
let snowPhase = 0;
let snowFoot: -1 | 1 = 1;

/**
 * The snow under and behind the player this frame: it moves along with them,
 * takes their footprints and their wheels' tracks, and tells the physics how
 * deep it is where they are — none on a plowed road, less in their own ruts.
 */
function stepSnow(dt: number, w: Weather, moved: PlayerState, local: { east: number; north: number }): void {
  const at = { x: local.east, y: moved.altitude, z: -local.north };
  snowField.update(dt, w.snowDepth, w.snowfall, at);
  player.snowing = w.snowfall > 0.1;
  const yaw = (-moved.heading * Math.PI) / 180;
  const s = player.soundState;
  const wheeled = (s.mode === 'drive' || s.mode === 'moto') && !s.airborne;
  if (wheeled) {
    const build: SnowBuild = s.mode === 'moto' ? 'moto' : player.vehicleBuild === 'truck' ? 'truck' : 'car';
    const layout = WHEELS[build];
    const wheels = wheelsAt(at.x, at.z, yaw, layout, build === 'moto' ? undefined : player.chassis.wheelbase);
    if (snowWheels.length === wheels.length) {
      wheels.forEach((p, i) => snowField.track(snowWheels[i].x, snowWheels[i].z, p.x, p.z, layout.tyre));
    }
    snowWheels = wheels;
    // Each wheel in the snow where it stands — none on a plowed road or car
    // park — and where it drives into, half a metre ahead of it or behind it,
    // less where it has already made ruts: the wheels judge the snow, not the
    // car's middle.
    const reach = 0.6;
    const wheelbase = build === 'moto' ? undefined : player.chassis.wheelbase;
    const ahead = wheelsAt(at.x - Math.sin(yaw) * reach, at.z - Math.cos(yaw) * reach, yaw, layout, wheelbase);
    const behind = wheelsAt(at.x + Math.sin(yaw) * reach, at.z + Math.cos(yaw) * reach, yaw, layout, wheelbase);
    const depthAt = (p: { x: number; z: number }) => paved(p.x, p.z) ? 0 : w.snowDepth;
    const atWheels = wheels.map((p, i) => ({
      here: depthAt(p),
      ahead: depthAt(ahead[i]), pressedAhead: snowField.pressedAt(ahead[i].x, ahead[i].z),
      behind: depthAt(behind[i]), pressedBehind: snowField.pressedAt(behind[i].x, behind[i].z),
    }));
    const snow = vehicleSnow(atWheels, build);
    player.snowLying = snow.ahead;
    player.snowLyingBack = snow.behind;
    throwSnow(dt, build, wheels, atWheels, snow.ahead, yaw, s);
  } else {
    snowWheels = [];
    const built = paved(at.x, at.z);
    player.snowLying = s.mode === 'walk' ? lyingDepth(w.snowDepth, built ? 1 : 0, snowField.pressedAt(at.x, at.z)) : 0;
  }
  // A footprint at every footfall, left and right in turn.
  if (s.mode === 'walk' && player.avatarPose.pose === 'ground' && !player.isDown) {
    for (let k = 0; k < footfalls(snowPhase, playerPhase); k++) {
      snowFoot = snowFoot === 1 ? -1 : 1;
      const foot = footAt(at.x, at.z, yaw, snowFoot);
      snowField.footprint(foot.x, foot.z, yaw);
    }
  }
  snowPhase = playerPhase;
}

/** How hard the drive wheels are spinning in place, 0..1: for the snow they throw and the revs (see `hearFrame`). */
let snowSpin = 0;

/**
 * The snow the player's wheels throw up: off each tyre where there is loose
 * snow (or a road's dusting), a rooster tail from the driven wheels spinning
 * in place, a sheet to the side in a slide, and the nose's bow wave where it
 * ploughs deep snow.
 */
function throwSnow(
  dt: number, build: SnowBuild, wheels: Array<{ x: number; z: number }>,
  atWheels: Array<{ here: number; ahead: number; pressedAhead: number }>, ahead: number, yaw: number, s: PlayerSound,
): void {
  const v = fleet.driven;
  const y = v === undefined ? player.state.altitude : fleet.root(v).position.y;
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  // Right of forward is (−fz, fx); the slide is positive to the right.
  const m = { vx: fx * s.speed - fz * s.slide, vz: fz * s.speed + fx * s.slide, fx, fz };
  // Stuck with the pedal down: the driven wheels spin where they stand.
  const pedal = Math.abs(s.throttle);
  const bogged = ahead > CLEARANCE[build] * 0.7 || (s.throttle < 0 && player.snowLyingBack > CLEARANCE[build] * 0.7);
  snowSpin = bogged && pedal > 0.3 && Math.abs(s.speed) < 2.5 ? pedal * (1 - Math.abs(s.speed) / 2.5) : 0;
  // Front-wheel drive for a car, the back axles for a lorry, the back wheel for the bike.
  const driven = (i: number) => build === 'car' ? i < 2 : build === 'truck' ? i >= 2 : i === 1;
  const dust = terrainWeather.roadSnow.value;
  wheels.forEach((p, i) => {
    const wheel = atWheels[i];
    const spin = driven(i) ? snowSpin : 0;
    const loose = Math.max(lyingDepth(wheel.ahead, 0, wheel.pressedAhead), spin > 0 ? wheel.here : 0);
    const rate = sprayRate({ speed: s.speed, slip: s.slip, loose, spin, dust: wheel.here === 0 ? dust : 0 });
    if (rate > 0) snowSpray.tyre(`w${i}`, p.x, y, p.z, m, rate, spin * Math.sign(s.throttle || 1), s.slide, dt);
  });
  // The nose ploughing a drift: a bow wave, more the deeper and faster.
  const plough = Math.min(1, Math.max(0, (ahead / CLEARANCE[build] - 0.35) / 0.8));
  if (plough > 0 && s.speed > 0.8) {
    // The middle of the wheels, and half the vehicle's length on from it.
    const cx = wheels.reduce((sum, p) => sum + p.x, 0) / wheels.length, cz = wheels.reduce((sum, p) => sum + p.z, 0) / wheels.length;
    const half = build === 'moto' ? 1 : player.chassis.length / 2;
    snowSpray.plough('nose', cx + fx * half, y, cz + fz * half, m, plough, build === 'moto' ? 0.25 : player.chassis.width * 0.4, dt);
  }
}

// --- sound ------------------------------------------------------------------------

/** What the player's vehicle hit, for the sound of it: a prop by its kind, a vehicle metal, else a wall or a tree by its width. */
function hitMaterial(other: Struck | undefined, width: number): SoundMaterial {
  if (other === undefined) return width < 0.8 ? 'wood' : 'stone';
  const ref = other.body.ref as { kind?: string; species?: Species };
  return ref.kind === 'prop' && ref.species !== undefined ? propMaterial(ref.species) : 'metal';
}

/** The engine a fleet vehicle has, for its sound. */
function engineOf(v: FleetVehicle): EngineKind {
  const kind = v.car?.kind;
  if (kind === 'semi' || kind === 'truck') return 'truck';
  if (kind !== undefined && kind.startsWith('bus')) return 'bus';
  return v.tank.energy === 'electric' ? 'electric' : v.tank.energy === 'diesel' ? 'diesel' : 'petrol';
}

const soundKeys = new WeakMap<object, string>();
let soundKeyCount = 0;
/** A name for a vehicle that lasts as long as it does, so its engine is its own. */
function soundKey(v: object): string {
  let key = soundKeys.get(v);
  if (key === undefined) soundKeys.set(v, key = `v${++soundKeyCount}`);
  return key;
}

const TREE_SPECIES: ReadonlySet<Species> = new Set(SPECIES.filter(sp => /pine|spruce|birch|broadleaf|jungle|palm|acacia|olive|cypress/i.test(sp)));
const soundForward = new Vector3();
const soundUp = new Vector3();
/** Where the listener is, kept a while: how built-up, wooded and near the sea. */
const soundPlace = { urban: 0, trees: 0, sea: 0, timer: 0 };
let soundMode: PlayerMode | undefined;
let soundCanopy = false;
let soundPhase = 0;
/** The body's pose and climb last frame, for the sound of a landing. */
let soundPose = '';
let soundVy = 0;
let lastFlash = 0;
const policeHeliWas = new Vector3();
/** What the sound costs the main thread a frame, ms, smoothed: for the HUD. */
let soundMs = 0;

/** Everything heard this frame: the listener, the engines, the world, the player's steps and doors. */
function updateSound(dt: number, w: Weather, clock: number, moved: PlayerState, local: { east: number; north: number }): void {
  if (sound.context === undefined) return;
  const started = performance.now();
  hearFrame(dt, w, clock, moved, local);
  soundMs += (performance.now() - started - soundMs) * 0.05;
}

function hearFrame(dt: number, w: Weather, clock: number, moved: PlayerState, local: { east: number; north: number }): void {
  const s = player.soundState;
  // The listener: at the camera, facing where it looks.
  camera.getWorldDirection(soundForward);
  soundUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
  const ear = camera.position;
  sound.listen(ear, soundForward, soundUp);
  const at = { x: local.east, y: moved.altitude + 0.6, z: -local.north };

  // The player's engine.
  const v = fleet.driven;
  const kind: EngineKind | undefined = s.mode === 'heli' ? 'heli' : s.mode === 'moto' ? (v?.bike?.kind === 'scooter' ? 'escooter' : 'moto')
    : s.mode === 'drive' && v !== undefined ? engineOf(v) : undefined;
  engines.updatePlayer(kind, s.mode === 'heli' ? 'heli' : v === undefined ? '' : soundKey(v), at, {
    // Spinning in the snow, the wheels turn as if at speed: the revs climb and the tyres roar, going nowhere.
    throttle: s.throttle, speed: snowSpin > 0 ? Math.sign(s.throttle) * Math.max(Math.abs(s.speed), 9 * snowSpin) : s.speed,
    gear: s.gear, tops: gearTops(player.powertrain, player.chassis), spool: s.spool,
    // Snow under the tyres crunches as gravel does.
    shifting: s.shifting, slip: s.slip, offRoad: s.offRoad || player.snowLying > 0.02, airborne: s.airborne, running: !s.dry,
    misfire: Math.min(0.6, (v?.handling?.smoke ?? 0) * 0.6),
  });
  // The traffic about, and the police helicopter over it.
  const velocity = player.velocity;
  const cars = traffic.heard(ear.x, ear.z, 170).map(c => ({ ...c, kind: trafficEngine(c.species, c.id) }));
  engines.updateTraffic(cars, ear, velocity.x, velocity.z, dt);
  const heliAt = policeHeli.position;
  let closing = 0;
  if (heliAt !== undefined && dt > 0) {
    const d0 = policeHeliWas.distanceTo(ear), d1 = heliAt.distanceTo(ear);
    closing = (d0 - d1) / dt;
    policeHeliWas.copy(heliAt);
  }
  engines.updateOther('policeHeli', 'heli', heliAt, ear, Math.max(-40, Math.min(40, closing)));

  // The place, every second or so: built-up, wooded, by the sea.
  soundPlace.timer -= dt;
  if (soundPlace.timer <= 0) {
    soundPlace.timer = 1;
    soundPlace.urban = Math.min(1, traffic.urban / 60);
    soundPlace.trees = Math.min(1, worldRenderer.propsNear(at.x, at.z, 45, TREE_SPECIES).length / 30);
    let sea = 0, samples = 0;
    for (let k = 0; k < 12; k++) {
      const a = (k / 12) * Math.PI * 2;
      const g = world.toGeo(local.east + Math.cos(a) * 280, local.north + Math.sin(a) * 280);
      const h = world.heightAt(g.lat, g.lon);
      if (h === undefined) continue;
      samples++;
      if (h < 0.6) sea++;
    }
    soundPlace.sea = samples > 0 ? sea / samples : 0;
  }
  const height = moved.agl ?? 0;
  const tunnel = worldRenderer.inTunnel(at.x, at.z, moved.altitude);
  const place: Place = {
    urban: soundPlace.urban, trees: soundPlace.trees, sea: soundPlace.sea, hour: clock, night: look.night,
    temperature: w.temperature, rain: w.rain, snowfall: w.snowfall, wind: w.wind, height, tunnel,
  };
  const flash = weatherFx.flash > 0.99 && lastFlash < 0.99;
  lastFlash = weatherFx.flash;
  const falling = s.mode === 'freefall';
  worldSound.update(dt, place, ear, {
    inside: s.inside && s.mode === 'drive', windMean: w.wind, windPeak: w.gusts, flash, clock,
    airspeed: falling ? Math.hypot(s.speed, player.verticalSpeed) : 0, canopy: falling && s.canopy,
  });

  // On foot: a step at every footfall, on what is underfoot.
  const avatar = player.avatarPose;
  if (s.mode === 'walk' && avatar.pose === 'ground' && !player.isDown) {
    const built = paved(at.x, at.z);
    const surface = stepSurface(built, built ? 0 : w.snowDepth, w.rain, soundPlace.urban, soundPlace.trees, Math.random());
    // Down from a jump: both feet at once, as hard as it landed.
    if (soundPose === 'jump') effects.land(surface, Math.min(1, -soundVy / 8), at);
    effects.onFoot(dt, soundPhase, playerPhase, avatar.speed, surface, at);
  } else {
    effects.tick(dt);
  }
  soundPhase = playerPhase;
  soundPose = avatar.pose;
  soundVy = avatar.vy;
  // Getting into a car or out of it: the door, open and shut.
  if (soundMode !== undefined && soundMode !== s.mode && (s.mode === 'drive' || soundMode === 'drive')) {
    effects.door(false, at);
    effects.door(true, at, 0.75);
  }
  soundMode = s.mode;
  if (s.canopy && !soundCanopy && falling) effects.canopy();
  soundCanopy = s.canopy;
  // In the cabin, the world through the glass; in a tunnel, its echo.
  sound.setCabin(s.inside && s.mode === 'drive');
  sound.setReverb(tunnel ? 0.6 : 0.04 + 0.1 * soundPlace.urban);
}

function frame(now: number): void {
  // Clamp dt so a background tab does not teleport the player on return. The
  // floor matters too: a rAF timestamp is the frame's start time, which can
  // precede the `performance.now()` taken at startup, so the first dt can be
  // negative.
  const dt = Math.max(0, Math.min(0.1, (now - last) / 1000));
  last = now;
  frameTimer.beginFrame();
  renderer.info.reset();
  // A resolution change resizes the canvas, which clears it: done here,
  // before this frame draws, rather than after the last one had.
  renderScale.beforeRender();

  // The ground is the terrain *or* a built surface on it: roads sit a little
  // above the terrain and bridges far above the valleys they cross. Only
  // surfaces within a step of the player's feet count, so walking up a bridge's
  // approach lands on the deck while walking under it stays on the road below.
  const contact = player.groundContact;
  const sampleGround = (lat: number, lon: number): number | undefined => {
    const terrain = world.heightAt(lat, lon);
    if (terrain === undefined) return undefined;
    const at = world.toLocal(lat, lon);
    const ground = worldRenderer.ground(at.east, -at.north, terrain, contact + STEP_HEIGHT);
    const dirt = jumps.heightAt(lat, lon);
    return dirt !== undefined && dirt > ground ? dirt : ground;
  };
  jumps.update((lat, lon) => world.heightAt(lat, lon), (lat, lon) => world.toLocal(lat, lon));

  const before = player.state;
  const ground = sampleGround(before.lat, before.lon);

  // A frame's share of new tiles: their meshes shown a few megabytes at a time.
  worldRenderer.pump();
  player.autopilot = steerHeld(now, ground) ?? steerTrip(dt, ground);
  keepTrip(dt);
  if (trip !== undefined) hintTimer = Math.min(hintTimer, 0.5);
  const rig = fleet.driven?.car?.trailer !== undefined && boarding === undefined && player.state.mode === 'drive' ? player.drivePlace : undefined;
  // The controller's buttons, sticks and triggers, before the player moves.
  gamepad.update(dt, player.state.mode);
  player.update(dt, sampleGround);
  // An articulated truck's trailer is solid too: swung into a wall or a
  // post, it holds the whole rig there, until backed or steered clear.
  // Driving may not push it further in; out again is always allowed.
  if (rig !== undefined) {
    const v = fleet.driven!, trailer = v.car!.trailer!, altitude = player.state.altitude;
    if (trailerOverlap(v, trailer, player.drivePlace, altitude) > trailerOverlap(v, trailer, rig, altitude) + 0.02) player.holdDrive(rig);
  }

  // Thrown off the bike: the body takes over from the rider.
  const thrown = player.takeThrow();
  const thrownFrom = thrown !== undefined ? throwRider(thrown) : undefined;
  // Landed too hard to stand: off a roof, or out of the sky without a canopy.
  const fell = player.takeFall();
  if (fell !== undefined) knockDownPlayer({ kind: 'fell', v: [fell.x, fell.y, fell.z] });
  stepDowned(dt);

  const moved = player.state;
  world.update(player.viewerState(ground));

  const local = world.toLocal(moved.lat, moved.lon);
  player.setLocalPosition(local.east, local.north);
  // The terrain leaves the nearest tunnels' mouths open.
  worldRenderer.updatePortals(camera.position.x, camera.position.z);
  // In a tunnel the lamps come on, as at night: under its roof, not in an open trough.
  const underground = worldRenderer.tunnelCeiling(local.east, -local.north, player.groundContact) !== undefined;
  const lamps = underground ? 1 : look.night;

  // --- vehicles: the one being driven, the ones left behind -------------------
  traffic.setPlayerTrailer(undefined);
  syncFleet();
  fleet.place((la, lo) => world.toLocal(la, lo));
  const pose = player.vehiclePose;
  const vehicle = fleet.driven?.car;
  if (pose.visible && vehicle !== undefined) {
    const groundNow = sampleGround(moved.lat, moved.lon) ?? moved.altitude;
    // Each wheel follows the ground under it, against the plane the chassis
    // sits on: over a kerb or a rut one wheel rises while the body stays level.
    const fx = -Math.sin(pose.yaw), fz = -Math.cos(pose.yaw);
    const wheelGround = vehicle.wheels.map((wheel) => {
      // Car-local x is right of forward, z is backward.
      const sx = local.east + -fz * wheel.x - fx * wheel.z;
      const sz = -local.north + fx * wheel.x - fz * wheel.z;
      const geo = world.toGeo(sx, -sz);
      const g = sampleGround(geo.lat, geo.lon);
      const plane = groundNow + pose.forwardSlope * -wheel.z + pose.lateralSlope * wheel.x;
      return g === undefined ? 0 : g - plane;
    }) as [number, number, number, number];
    // The chassis rides its springs toward the ground rather than sitting on it.
    // In the air the chassis flies where the car is; landing, the springs take it.
    updateRide(ride, { y: moved.altitude, forwardSlope: pose.forwardSlope, lateralSlope: pose.lateralSlope, wheelGround },
      pose.offRoad, dt, pose.airborne, pose.landing);
    vehicle.root.position.set(local.east, ride.y, -local.north);
    orientToSlope(vehicle.root, pose.yaw, Math.tan(ride.pitch), Math.tan(ride.roll));
    vehicle.root.updateMatrix();
    if (vehicle.trailer !== undefined) {
      pullTrailer(fleet.driven!, vehicle.trailer, local.east, -local.north, pose.yaw);
      restTrailer(fleet.driven!, vehicle.trailer, sampleGround, dt);
    }
    animateVehicle(vehicle, {
      speed: pose.speed,
      steerAngle: pose.steerAngle,
      accel: pose.accel,
      lateralAccel: pose.lateralAccel,
      braking: pose.braking,
      night: lamps,
      wheelGround: ride.wheels,
      offRoad: pose.offRoad,
      squash: ride.squash,
      highBeam,
    }, dt);
    // A broken headlamp throws no beam.
    const broken = fleet.driven!.wreck?.brokenBeams;
    if (broken !== undefined) vehicle.headlights.forEach((beam, i) => { if (broken[i]) beam.intensity = 0; });
    leaveHere(fleet.driven!, moved.lat, moved.lon, ride.y, pose.yaw, ride.pitch, ride.roll);
  }

  // --- the motorbike and its rider ------------------------------------------
  const bike = player.motoPose;
  const motorbike = fleet.driven?.bike;
  let riderOnBike: ReturnType<typeof riderPose> | undefined;
  if (bike.visible && motorbike !== undefined) {
    const spec = player.twoWheeler, geo = spec.geometry;
    // The ground under each axle, for the suspension to reach down to.
    const fx = -Math.sin(bike.yaw), fz = -Math.cos(bike.yaw);
    const groundAt = (ahead: number) => {
      const geo = world.toGeo(local.east + fx * ahead, local.north - fz * ahead);
      return sampleGround(geo.lat, geo.lon) ?? moved.altitude;
    };
    const groundFront = groundAt(-geo.frontAxleZ), groundRear = groundAt(-geo.rearAxleZ);
    updateMotoRide(motoRide, { y: moved.altitude, groundFront, groundRear, accel: bike.accel, airPitch: bike.airPitch, wheelie: bike.wheelie },
      dt, bike.airborne, bike.landing, spec);
    const riding = {
      speed: bike.speed, steerAngle: bike.steerAngle, lean: bike.lean, accel: bike.accel,
      airborne: bike.airborne, offRoad: bike.offRoad, wheelie: motoRide.wheelie, landing: bike.landing,
    };
    riderOnBike = motorbike.kind === 'scooter' ? scooterRiderPose(scooterRider, riding, dt) : riderPose(rider, riding, dt);
    const root = motorbike.root;
    // Up on the back wheel it pivots on the rear axle: the middle rises.
    root.position.set(local.east, motoRide.y + geo.rearAxleZ * Math.sin(motoRide.wheelie), -local.north);
    root.rotation.set(0, 0, 0);
    root.rotateY(bike.yaw);
    root.rotateX(motoRide.pitch + motoRide.wheelie);
    // Leaning right is a negative turn about the bike's forward axis.
    root.rotateZ(-(bike.lean + riderOnBike.bikeLean));
    root.updateMatrix();
    if (motorbike.kind === 'scooter') {
      animateScooter(motorbike, {
        speed: bike.speed, steerAngle: bike.steerAngle, front: motoRide.front, rear: motoRide.rear, braking: bike.braking, night: lamps, ridden: true,
      }, dt);
    } else {
      animateMotorbike(motorbike, {
        speed: bike.speed, steerAngle: bike.steerAngle, front: motoRide.front, rear: motoRide.rear,
        braking: bike.braking, night: lamps, throttle: bike.throttle, airborne: bike.airborne,
      }, dt);
    }
    const brokenLamp = fleet.driven!.wreck?.brokenBeams;
    if (brokenLamp !== undefined && (brokenLamp[0] || brokenLamp[1])) motorbike.headlight.intensity = 0;
    // Dirt off the back tyre where it digs in: under power or sliding, off the
    // road. A scooter's small wheel flicks up a little gravel, no more.
    const digging = !bike.airborne && bike.offRoad && Math.abs(bike.speed) > 1.5
      ? ((bike.throttle > 0 ? 25 + Math.max(0, bike.accel) * 12 : 0) + (bike.sliding ? 70 : 0) + Math.abs(bike.lean) * 30 * (bike.throttle > 0 ? 1 : 0))
        * (motorbike.kind === 'scooter' ? 0.2 : 1)
      : 0;
    roost.update(dt, local.east - fx * geo.rearAxleZ, groundRear, -local.north - fz * geo.rearAxleZ, fx, fz, digging, Math.abs(bike.speed));
    leaveHere(fleet.driven!, moved.lat, moved.lon, (groundFront + groundRear) / 2, bike.yaw);
    blendPose(riderOnBike.pose, riderOnBike.pose, 0, lastRide);
  } else {
    roost.update(dt, 0, 0, 0, 0, 1, 0, 0);
  }
  // A scooter left standing: its stand down, its display and LEDs dark.
  for (const v of fleet.vehicles) {
    if (v === fleet.driven || v.bike?.kind !== 'scooter') continue;
    animateScooter(v.bike, { speed: 0, steerAngle: 0, front: SCOOTER_SPEC.geometry.frontSag, rear: SCOOTER_SPEC.geometry.rearSag, braking: false, night: lamps, ridden: false }, dt);
  }

  // --- crashes: the hits this frame, and the pieces they broke off ------------
  const hits = player.takeImpacts();
  for (const { body, closing, push } of player.takeHeld()) {
    const ref = body.ref as { kind: string; species?: Species; x?: number; z?: number };
    if (ref.kind === 'prop') bendHeldProp(ref.species!, ref.x!, ref.z!, push, closing);
  }
  // Hedges and bushes flattened under the wheels, and dragging at them.
  player.plantDrag = fleet.driven !== undefined && (pose.visible || bike.visible) && Math.abs(moved.speed) > 0.3
    ? plants(local.east, -local.north, pose.visible ? pose.yaw : bike.yaw, fleet.driven.species) : 0;
  // The bike just thrown from is let go, but takes the hit that threw its rider.
  const crashing = fleet.driven !== undefined && (pose.visible || bike.visible) ? fleet.driven : thrownFrom;
  if (crashing !== undefined) {
    for (const { impact, velocity, other } of hits) {
      // Hitting another vehicle hard enough to dent it in the police's sight,
      // or the police themselves at all: they do not take a knock lightly.
      if (other !== undefined) {
        const ref = other.body.ref as { kind?: string; police?: boolean; vehicle?: FleetVehicle };
        const police = ref.police === true || ref.vehicle?.police !== undefined;
        // Rammed by a police car off the road after them is no fault of theirs.
        const rammed = ref.vehicle?.chaser !== undefined;
        if (!rammed && impact.dv > (police ? 1 : KNOCK_KMH / 3.6)) {
          offences.push({ offence: police ? 'policeCrash' : 'crash', seen: police || sawThem });
        }
      }
      crash(crashing, impact, velocity);
      effects.crash(player.vehicleBuild === 'moto' ? 'plastic' : 'metal', hitMaterial(other, impact.width), impact.dv, impact.scrape,
        { x: local.east, y: moved.altitude + 0.6, z: -local.north });
      gamepad.rumble(impact.dv / 8, 120 + Math.min(400, impact.dv * 40));
      if (other !== undefined) strike(other, local.east, -local.north, velocity);
    }
  }
  strikePeople();
  // Running into a motorbike on foot knocks it over.
  if (moved.mode === 'walk' && dt > 0) {
    const was = world.toLocal(before.lat, before.lon);
    const vx = (local.east - was.east) / dt, vz = (-local.north + was.north) / dt;
    if (Math.hypot(vx, vz) > 2.5) shoulderBikes(local.east, -local.north, vx, vz);
  }
  // The traffic running into itself, or into a wreck: both made wrecks of.
  for (const c of traffic.takeCrashes()) {
    effects.knock('metal', c.closing, { x: c.x, y: c.a.y + 0.6, z: c.z });
    const a = wreckFromTraffic(c.a);
    const b = c.b !== undefined ? wreckFromTraffic(c.b) : obstacleOwners[c.obstacle ?? -1];
    if (b === undefined || !fleet.vehicles.includes(b)) continue;
    const vb = c.b !== undefined ? { x: c.b.vx, z: c.b.vz } : { x: b.motion?.vx ?? 0, z: b.motion?.vz ?? 0 };
    collideVehicles(a, b, { x: c.nx, z: c.nz }, { x: c.x, z: c.z }, { x: c.a.vx, z: c.a.vz }, vb);
  }
  slideFleet(dt);
  clearWrecks(dt, local.east, -local.north);
  // Smoke from every hurt engine near enough to see: under the bonnet, the cab, or a bike's tank.
  for (const v of fleet.vehicles) {
    const amount = v.handling?.smoke ?? 0;
    if (amount <= 0) continue;
    const root = fleet.root(v);
    if (Math.hypot(root.position.x - local.east, root.position.z + local.north) > 200) continue;
    const size = VEHICLE_SIZE[v.species];
    if (v.type === 'moto') smokeFrom.set(0, 0.7, -0.1);
    else if (v.car !== undefined && isTruck(v.car.kind)) smokeFrom.set(0, 1.0, -size.halfLength + 0.6);
    else smokeFrom.set(0, 0.85, -size.halfLength + 0.7);
    smokeFrom.applyMatrix4(root.matrixWorld);
    smoke.emit(v, smokeFrom.x, smokeFrom.y, smokeFrom.z, amount, dt, { x: wind.x, z: wind.z });
  }
  smoke.update(dt);
  snowSpray.update(dt, { x: wind.x, z: wind.z });
  // Traffic stops behind wrecks and the cars left standing in its way.
  traffic.setObstacles(fleetFootprints(obstacleOwners));
  {
    // The scene's origin moves now and then; the pieces in the road move with it.
    const here = world.toLocal(debrisAnchor.lat, debrisAnchor.lon);
    const shift = { x: here.east - debrisAt.x, z: -here.north - debrisAt.z };
    debrisAt.x = here.east;
    debrisAt.z = -here.north;
    debris.update(dt, sceneGround, shift, local.east, -local.north);
    shards.update(dt, sceneGround, shift);
    // The trains, live in Sweden, made up elsewhere; heard where they pass.
    trainFeed.update(Date.now(), moved.lat, moved.lon);
    trains.update(dt, Date.now(), local.east, -local.north, worldRenderer.railways, trainFeed.live ? trainFeed.trains : undefined,
      (la, lo) => { const l = world.toLocal(la, lo); return { x: l.east, z: -l.north }; }, shift, localTime(skyClock.now, moved.lat, moved.lon).hour, look.night,
      railStations(local.east, -local.north));
    trainSounds.update(trains.heard(camera.position.x, camera.position.y, camera.position.z), camera.position);
    for (const e of trains.takeSounds()) trainSounds.event(e.id, e.at);
    // Level crossings close for the trains, and ring.
    worldRenderer.crossings.update(dt, trains.views(), local.east, -local.north, sound);
    stationBoards.update(railStations(local.east, -local.north), worldRenderer.railways, local.east, -local.north);
  }

  // --- helicopters: the one being flown, the ones waiting and left ------------
  syncHelis(dt);
  heliFleet.place((la, lo) => world.toLocal(la, lo));
  const heli = player.helicopterPose;
  const flown = heliFleet.flown;
  if (heli.visible && flown !== undefined) {
    const helicopter = flown.model;
    // The airframe's origin is under the skids, and the player's altitude is
    // measured there too, so this is a straight placement.
    helicopter.root.visible = true;
    helicopter.root.position.set(local.east, moved.altitude - HELI.skidHeight, -local.north);
    orientHelicopter(helicopter.root, heli.yaw, heli.pitch, heli.roll);
    // Lit once the pilot is aboard.
    animateHelicopterLights(helicopter, heli.running && heliBoard === undefined, look.night, clock);
    flown.lat = moved.lat;
    flown.lon = moved.lon;
    flown.y = moved.altitude - HELI.skidHeight;
    flown.yaw = heli.yaw;
  }
  spinRotor(dt);
  speedSigns.update((la, lo) => world.toLocal(la, lo), local, (la, lo) => world.heightAt(la, lo));
  updateEnergy(dt, moved.speed);
  updateJob(dt);
  // The searchlight follows the view, so it points wherever the player looks.
  camera.getWorldDirection(viewDirection);
  searchlight.update(heli.visible ? flown?.model : undefined, viewDirection, look.night);

  // --- the player's body ---------------------------------------------------
  clock += dt;
  const avatar = player.avatarPose;
  playerPhase = advancePhase(playerPhase, avatar.speed, dt);
  // A jump: which leg leads it, from where the stride was as it left the
  // ground; and once down, the legs giving under the landing a moment.
  if (avatar.pose === 'jump' && lastAvatarPose !== 'jump') jumpLead = playerPhase % 1 < 0.5 ? 1 : -1;
  if (avatar.pose === 'ground' && lastAvatarPose === 'jump') landing = Math.min(1, 0.45 + Math.max(0, -lastAvatarVy) / 10);
  landing = Math.max(0, landing - dt / LAND_TIME);
  lastAvatarPose = avatar.pose;
  lastAvatarVy = avatar.vy;
  const bodyPose = avatar.pose === 'ground'
    ? landPose(gaitPose(avatar.speed, playerPhase, clock), landing * landing * (3 - 2 * landing))
    : avatar.pose === 'jump'
      ? jumpPose(avatar.vy, clock, avatar.speed, jumpLead)
      : airPose(avatar.pose, clock, avatar.dive);
  // The fists and what is held, on foot.
  const handsPose = poseHands(dt, bodyPose);
  // Thrown off: the body as it tumbles, lies and gets up, helmet on.
  if (downed !== undefined) downed.fallen.draw();
  // Getting in or out, or sitting at the wheel: posed in the vehicle.
  else if (posePilot(dt) || poseDriver(dt)) { /* placed */ }
  else if (riderOnBike !== undefined && motorbike !== undefined && avatar.visible) crowd.placeRider(playerSlot, motorbike.root.matrix, riderOnBike.pose);
  else crowd.place(playerSlot, {
    x: local.east,
    // In freefall the body pivots at the hips, so lift it by a hip height to
    // keep it centred where the camera is looking.
    y: avatar.pose === 'freefall' ? avatar.feet + 0.9 : avatar.feet,
    // Belly to the earth, tipping head-down as the dive comes on.
    z: -local.north,
    yaw: avatar.yaw,
    pose: handsPose,
    bodyPitch: avatar.pose === 'freefall' ? Math.PI / 2 + avatar.dive * DIVE_PITCH : 0,
    visible: avatar.visible && riderOnBike === undefined,
  });
  afterHands(dt, avatar.yaw, handsPose.twist - bodyPose.twist);

  // The canopy blooms open over about a second rather than appearing whole.
  const canopyWanted = player.state.mode === 'freefall' && avatar.pose === 'canopy';
  canopyOpen = canopyWanted ? Math.min(1, canopyOpen + dt * 1.2) : 0;
  parachute.visible = canopyOpen > 0 && avatar.visible;
  if (parachute.visible) {
    const bloom = 1 - Math.pow(1 - canopyOpen, 3);
    parachute.position.set(local.east, avatar.feet + 1.42, -local.north);
    parachute.rotation.set(Math.sin(clock * 1.4) * 0.04, avatar.yaw, Math.sin(clock * 0.9) * 0.05);
    parachute.scale.set(0.2 + 0.8 * bloom, 0.35 + 0.65 * bloom, 0.2 + 0.8 * bloom);
  }

  // --- time, sky and weather ---------------------------------------------
  const date = skyClock.now;
  world.dayOfYear = today();
  const sun = sunPosition(date, moved.lat, moved.lon);
  const moon = moonPosition(date, moved.lat, moved.lon);
  sunDir.fromArray(horizonToScene(sun));
  moonDir.fromArray(horizonToScene(moon));
  weather.update(moved.lat, moved.lon);
  const w = weatherSim.on ? weatherSim.weather : liveWeather();
  wind.update(dt, w);
  setFloraWind(wind);
  setWaterWind(wind);
  look.setSky({
    sun: sunDir,
    sunAltitude: sun.altitude,
    moon: moonDir,
    moonAltitude: moon.altitude,
    moonIllumination: moon.illumination,
    moonDiameter: moon.diameter,
    sidereal: localSidereal(date, moved.lon),
    latitude: moved.lat,
    cloud: w.cloud,
    fog: w.fog,
    flash: weatherFx.flash,
  });
  const hour = solarHour(date, moved.lon);
  houseNight.night.value = look.night;
  houseNight.windowsLit.value = windowsLit(hour);
  floraNight.value = look.night;
  terrainWeather.snow.value = Math.min(1, w.snowDepth / 0.08);
  // A dusting on the roads while it snows, and a little along their edges after.
  terrainWeather.roadSnow.value = Math.min(1, w.snowfall / 1.5) * 0.75 + (w.snowDepth > 0.02 ? 0.12 : 0);
  // Snow settling on the trees and the roofs, over a few seconds. Falling
  // snow clings to the branches unless it is clearly mild, and some stays on
  // them while it is below freezing, less in a strong wind. The roofs keep
  // what lies on the ground until a proper thaw. Both fade out over a few
  // degrees rather than at a line, so snow set by hand shows whatever the
  // thermometer said when the simulation began.
  const shaken = 1 - 0.6 * Math.min(1, Math.max(0, (w.wind - 8) / 8));
  const thawing = (from: number, to: number) => 1 - Math.min(1, Math.max(0, (w.temperature - from) / (to - from)));
  const treeSnow = Math.max(0.7 * Math.min(1, w.snowfall / 0.8) * thawing(1, 3), (w.snowDepth > 0.05 ? 0.4 : 0) * thawing(-1, 1)) * shaken;
  const roofSnow = Math.max(Math.min(1, w.snowDepth / 0.08), Math.min(1, w.snowfall / 1)) * thawing(3, 6);
  const settle = 1 - Math.exp(-dt / 6);
  terrainWeather.treeSnow.value += (treeSnow - terrainWeather.treeSnow.value) * settle;
  terrainWeather.roofSnow.value += (roofSnow - terrainWeather.roofSnow.value) * settle;
  stepSnow(dt, w, moved, local);
  // Everyone dressed for it, whenever it changes enough to change what they wear.
  const dressKey = `${Math.round(w.temperature)}:${Math.round(w.wind / 2)}:${w.rain > 0.3}:${w.snowfall > 0.05}`;
  if (dressKey !== dressedFor) {
    dressedFor = dressKey;
    crowd.setWeather(w);
  }
  terrainWeather.wet.value = Math.min(1, w.rain / 2);
  weatherFx.update(dt, camera, w, ground ?? moved.altitude, 1 - look.night);
  const sunUp = Math.min(1, Math.max(0, sun.altitude / 0.18));
  terrainWeather.cloudShadow.value = Math.min(1, w.cloud * (1 - w.cloud * 0.7) * 2.2) * sunUp * (1 - w.fog * 0.8);
  terrainWeather.cloudDrift.value.set(weatherFx.drift.x, weatherFx.drift.z);

  // Fewer people out at night, fewer still in the rain.
  const about = (0.2 + 0.8 * (1 - look.night)) * (1 - Math.min(0.7, w.rain / 6 + w.snowfall / 3));
  villagers.setPopulation(config.villagers * about);
  villagers.update(dt, local.east, -local.north);
  // Rush hour by the clocks at the place, not the sun: see core/traffic/profile.ts.
  const when = localTime(date, moved.lat, moved.lon);
  traffic.setDensity(trafficLevel(when.hour, when.weekday));
  traffic.playerOnFoot = moved.mode === 'walk' && !player.isDown;
  dressPolice(dt, moved.lat, moved.lon);
  staffStations(dt, local.east, -local.north);
  // The player at the wheel of a police car, blue lights on: the traffic gives way to them too.
  traffic.playerSiren = (moved.mode === 'drive') && fleet.driven?.police?.siren === true;
  transitFeed.update(Date.now(), when.hour, moved.lat, moved.lon, (la, lo) => world.toLocal(la, lo), traffic, trams);
  trams.update(dt, Date.now(), local.east, -local.north, moved.altitude, sceneGround, worldRenderer.tracks, traffic.around(local.east, -local.north, 600), look.night);
  traffic.setMovers([...trams.movers(), ...trains.movers(), ...worldRenderer.crossings.movers()]);
  traffic.update(dt, local.east, -local.north, moved.altitude);
  busSigns.update([...traffic.buses(), ...trams.signs()]);
  sirens.update(policeSirens(local.east, -local.north));
  rideBikes(dt);
  stepPolice(dt, local.east, -local.north);
  // The police helicopter over a city: watching the player while they are chased, or the police near them respond.
  policeHeli.update(dt, {
    toLocal: (la, lo) => world.toLocal(la, lo), toGeo: (x, z) => world.toGeo(x, -z), ground: (x, z) => sceneGround(x, z, 1e4),
  }, traffic.urban > HELI_CITY, { x: local.east, z: -local.north },
  wanted.stars > 0 || traffic.police.responding > 0 ? { x: local.east, z: -local.north } : undefined, look.night);
  // Stepped out in front of a car too late for its driver: run down.
  if (traffic.playerOnFoot) {
    const run = traffic.strikePerson(local.east, player.avatarPose.feet, -local.north);
    if (run !== undefined && player.knockDown()) {
      knockDownPlayer({ kind: 'struck', v: [run.hit.person.x, 0, run.hit.person.z], bumper: BUMPER[buildOfSpecies(run.species)] });
    }
  }
  // Drivers held up long enough sound their horns, fainter further off.
  const toots = traffic.takeToots();
  for (let i = 0; i < toots.length; i += 2) {
    horn.toot(Math.hypot(toots[i] - local.east, toots[i + 1] + local.north));
  }
  // G sounds the horn, at the wheel or on the bike; cars ahead move over for it.
  const honking = player.held('KeyG') && (player.state.mode === 'drive' || player.state.mode === 'moto');
  horn.set(honking, player.state.mode !== 'moto' ? 'car' : fleet.driven?.bike?.kind === 'scooter' ? 'scooter' : 'moto');
  hornHeard -= dt;
  if (honking && hornHeard <= 0) {
    hornHeard = 0.25;
    const heading = (player.state.heading * Math.PI) / 180;
    traffic.honk(local.east, -local.north, Math.sin(heading), -Math.cos(heading));
  }
  updateSound(dt, w, when.hour, moved, local);
  // Other players, where the room says they were a moment ago.
  remotePlayers.update(presence, {
    now, dt, night: look.night,
    toLocal: (lat, lon) => world.toLocal(lat, lon),
    ground: (x, z, near) => {
      const geo = world.toGeo(x, -z);
      const terrain = world.heightAt(geo.lat, geo.lon);
      if (terrain === undefined) return undefined;
      const base = worldRenderer.ground(x, z, terrain, near + STEP_HEIGHT);
      const dirt = jumps.heightAt(geo.lat, geo.lon);
      return dirt !== undefined && dirt > base ? dirt : base;
    },
    viewer: local,
    bodies: othersBodies,
  });
  crowd.commit();

  // The sky follows the biome under the player. Re-evaluated twice a second —
  // climate changes over hundreds of kilometres, so that is still far faster
  // than anything can visibly change.
  climateTimer -= dt;
  if (climateTimer <= 0) {
    climateTimer = 0.5;
    look.setClimate(biomeWeights(climateAt(moved.lat, moved.lon, ground ?? 0)));
  }
  focus.set(local.east, ground ?? moved.altitude, -local.north);
  look.update(focus, ground === undefined ? moved.altitude : moved.altitude - ground, dt);
  waterMaterial.color.copy(look.waterColor);
  // The world map covers the screen: no point drawing the world under it.
  topView();
  if (!worldMap.open) {
    snowField.render(scene);
    frameTimer.beginGpu();
    look.render();
    frameTimer.endGpu();
  }
  renderScale.frame(now, !worldMap.open && document.visibilityState === 'visible');

  const mapFrame = {
    player: { lat: moved.lat, lon: moved.lon, heading: moved.heading },
    date,
    layers: { night: false, radar: true, weather: false, labels: true },
    others: [...presence.players.values()],
    route: trip?.line ?? job?.line,
  };
  // On foot and under a canopy, where the feet are and the way the body faces:
  // the state's altitude is the eye's and its heading the camera's. In a car
  // or a helicopter the heading is already the machine's.
  const onFoot = moved.mode === 'walk' || moved.mode === 'freefall';
  sentState = {
    lat: moved.lat, lon: moved.lon,
    altitude: onFoot ? avatar.feet : moved.altitude,
    heading: onFoot ? ((-avatar.yaw * 180) / Math.PI % 360 + 360) % 360 : moved.heading,
    mode: moved.mode,
    fall: downed?.fall,
  };
  // Where the feet would land, for the word sent as the tab is hidden.
  groundUnderFeet = ground;
  presence.update(sentState, now);
  // The time as the device shows it, in its own time zone. The light follows
  // the sun where the player is; the clock follows the player's own.
  minimap.update(mapFrame, w, localClock(date), look.night > 0.5);
  worldMap.update(mapFrame, `${localDate(date)} ${localClock(date)}`);

  if (now - urlSynced >= URL_INTERVAL) {
    urlSynced = now;
    syncUrl(moved, ground);
  }

  radio.update(moved.mode === 'drive', moved.lat, moved.lon, sound.settings.muted ? 0 : sound.settings.master * sound.settings.radio);
  touch?.update(moved.mode);
  updateBoardHint(dt);
  touch?.setVisible(!worldMap.open);

  hud.tick();
  hudTimer -= dt;
  if (hudTimer <= 0) {
    hudTimer = 0.25;
    if (worldMap.open) refreshPeople();
    const deg = (r: number) => Math.round((r * 180) / Math.PI);
    const season = seasonState(climateAt(moved.lat, moved.lon, ground ?? 0), moved.lat, today());
    const hh = Math.floor(hour), mm = Math.floor((hour - hh) * 60);
    const speed = skyClock.timescale === 1 ? '' : `   ×${skyClock.timescale}`;
    hudWorld = [
      `${localDate(date)}  ${localClock(date)}   solar ${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}   ${season.name}${speed}`,
      `sun ${deg(sun.altitude)}°   moon ${deg(moon.altitude)}° ${phaseName(moon.phase)} ${Math.round(moon.illumination * 100)}%`,
      `${w.description}   ${w.temperature.toFixed(0)}°C   cloud ${Math.round(w.cloud * 100)}%   wind ${w.wind.toFixed(0)} m/s gusts ${w.gusts.toFixed(0)} from ${compass(w.windFrom)}`
        + (weatherSim.on ? '   (simulated)' : weather.live ? '' : weatherPreset === null ? '   (weather loading)' : '   (preset)'),
    ];
    const r = renderer.info.render;
    const size = renderer.getDrawingBufferSize(drawingBuffer);
    const dpr = window.devicePixelRatio;
    const screenW = Math.round(window.innerWidth * dpr), screenH = Math.round(window.innerHeight * dpr);
    const profile = GRAPHICS_PRESETS.find((p) => isChosen(graphics, p.patch))?.label ?? 'custom';
    hudPerf = [
      frameTimer.text(),
      `scene ${r.calls} draws   ${(r.triangles / 1e6).toFixed(2)}M tris`,
      `render ${size.x}×${size.y}   ${Math.round((size.x / screenW) * 100)}% of screen ${screenW}×${screenH}`,
      `density screen ${+dpr.toFixed(2)}×   drawn ${+renderer.getPixelRatio().toFixed(2)}×   cap ${graphics.maxPixelRatio}×   ${profile}`,
      `card ${gpuName ?? 'not named by this browser'}`,
    ];
    const beds = worldSound.last;
    const pct = (x: number) => `${Math.round(x * 100)}%`;
    hud.setSoundText([
      sound.context === undefined ? 'waiting for the first key press or tap' : `audio ${sound.context.state}   ${sound.context.sampleRate} Hz   bank ${sound.bankReady ? 'ready' : 'making…'}`,
      `engines ${engines.count}   main thread ${soundMs.toFixed(2)} ms a frame`,
      ...(beds === undefined ? [] : [
        `wind ${pct(beds.wind)}   rain ${pct(Math.max(beds.rainLight, beds.rainHeavy))}   roof ${pct(beds.roof)}`,
        `town ${pct(beds.town)}   chatter ${pct(beds.chatter)}   sea ${pct(beds.waves)}   crickets ${pct(beds.crickets)}`,
        `birds ${beds.birdsPerMinute.toFixed(1)}/min   gulls ${beds.gullsPerMinute.toFixed(1)}/min`,
      ]),
    ]);
    if (config.buildingTags) {
      const t = buildingTags.stats;
      hudWorld.push(`osm buildings ${t.items}   requests ${t.requests}   cached ${t.cacheHits}   failed ${t.failures}`);
    }
  }
  hud.update(moved, world.stats, ground, player.verticalSpeed, hudWorld, hudPerf);
  if (config.elevation.overlay !== undefined || config.vector.overlay !== undefined) {
    hud.setCredits(credits(
      config.elevation.overlay !== undefined && world.terrainAt(moved.lat, moved.lon)?.fromOverlay === true,
      config.vector.overlay !== undefined && world.hasLmBuildings(tileAt(moved.lat, moved.lon, config.vector.overlay.zoom)),
    ));
  }

  frameTimer.endFrame();
  requestAnimationFrame(frame);
}

// For the headless tools in tools/: the live objects, in development only.
/**
 * For checking crashes by hand (dev builds): hit the vehicle being driven at
 * `kmh` on `zone`, `across` metres off its middle, as a wall would (or a
 * post `width` metres across), and see what it does. `__wsg.crash(60)` is a
 * 60 km/h wall head-on; `__wsg.crash(50, 'front', 0, 0.3)` a lamp post.
 */
function testCrash(kmh: number, zone: Zone = 'front', across = 0, width = Infinity): void {
  const v = fleet.driven;
  if (v === undefined) return;
  const size = VEHICLE_SIZE[v.species], build = buildOf(v);
  const h = build === 'truck' ? 0.85 : build === 'moto' ? 0.75 : 0.5;
  const ends = { front: [across, h, -size.halfLength], rear: [across, h, size.halfLength], left: [-size.halfWidth, h, across], right: [size.halfWidth, h, across] } as const;
  const pushes = { front: [0, 0, 1], rear: [0, 0, -1], left: [1, 0, 0], right: [-1, 0, 0] } as const;
  const heading = (player.state.heading * Math.PI) / 180, dv = kmh / 3.6;
  crash(v, { dv, scrape: 0, zone, point: [...ends[zone]], push: [...pushes[zone]], width }, { x: Math.sin(heading) * dv, z: -Math.cos(heading) * dv });
}

if (import.meta.env.DEV) Object.assign(window, { __wsg: { player, world, worldRenderer, fleet, heliFleet, traffic, trams, trains, trainFeed, trainSounds, stationBoards, sound, gamepad, villagers, debris, shards, crash: testCrash, get downed() { return downed; }, bodyGround, bodyCollide, police: { wanted, offend: (o: Offence) => offend(wanted, o, true), stepPolice, heli: policeHeli, stations, bikeRiders, sirens, officers, get chasers() { return fleet.vehicles.filter(v => v.chaser !== undefined); }, get seen() { return sawThem; }, get lyingLow() { return wasLyingLow; }, get lastSeen() { return lastSeen; }, get release() { return release; } }, startTrip, renderer, camera, scene, get trip() { return trip; }, get job() { return job; }, get offer() { return offer; } } });

requestAnimationFrame(frame);
